import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Workbook } from "@fortune-sheet/react";
import "@fortune-sheet/react/dist/index.css";
import { format, addDays, subDays } from "date-fns";
import { tr } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";
import { ChevronLeft, ChevronRight, RefreshCw, GripVertical, X, Search, ChevronUp, ChevronDown, ChevronsUp, ChevronsDown, Ship, Filter, ReceiptText } from "lucide-react";
import { InvoiceDialog } from "../components/invoice/InvoiceDialog";
import RescheduleModal from "../components/RescheduleModal";
import { useAuth } from "../context/AuthContext";
import LiveSheetMobile from "./LiveSheetMobile";
import { getToken, isTokenExpiring, refreshAccessToken, authHeaders } from "../lib/authRefresh";
import * as offlineQueue from "../lib/offlineQueue";
import { exportSheetToExcel, exportTransferListToExcel, exportSheetRangeToExcel } from "../lib/exportExcel";

const API = window.location.origin;

// Görünüm seçimi: yalnızca TELEFON (küçük ekran, <768px) kart görünümü alır.
// Tablet/iPad ve üzeri -> masaüstüyle AYNI FortuneSheet (Sheets) grid'i (tüm sütunlar/detaylar, arama,
// sıralama butonları ve tekne-başlığına dokunarak atama dokunmatikte de çalışır).
const isPhoneScreen = () => {
  if (typeof window === "undefined") return false;
  return window.innerWidth < 768;
};
export default function LiveSheet() {
  const [isMobile, setIsMobile] = useState(isPhoneScreen);
  useEffect(() => {
    const onResize = () => setIsMobile(isPhoneScreen());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  return isMobile ? <LiveSheetMobile /> : <LiveSheetDesktop />;
}

// Bos/manuel satirlarda ayni sayfadan yazarak duzenlenebilen kimlik alanlari (col.key)
const MANUAL_FIELDS = new Set(["otel_adi", "acenta", "isim", "telefon", "kod", "opsiyon", "saat", "pax"]);
// Bókun/hesaplanan görünüm alanları: HER satırda düzenlenebilir (override olarak saklanır, senkron ezmez)
const OVERRIDE_FIELDS = new Set(["otel_adi", "acenta", "isim", "telefon", "kod", "opsiyon", "saat", "pax", "ekstra", "menu"]);
// MENÜ ozet sutunu kisa etiketleri (backend _MENU_LABELS ile ayni)
const MENU_LABELS = { yemeksiz: "YMKSZ", m1: "M1", m2: "M2", limit: "LMT", vip: "VIP", vip_alkol: "VIP-ALK", yarim: "ÇOCUK", inf: "İNF", snack: "SNACK", yemekli: "YML" };
// Yapisal oturum etiketleri (backend SEATING_TAGS ile ayni): [key,label,renk,sayilir,ekstra_odeme]
const SEATING_OPTS = [
  { key: "cam_kenari", label: "Cam Kenarı", color: "#B45309", counted: true },
  { key: "vip_cam_kenari", label: "VIP Cam Kenarı", color: "#9333EA", counted: true },
  { key: "vip_lounge", label: "VIP Lounge", color: "#7C3AED", counted: true },
  { key: "sahne_onu", label: "Sahne Önü", color: "#0369A1", counted: true },
  { key: "salon", label: "Salon", color: "#0f766e", counted: true },
  { key: "dogum_gunu", label: "Doğum Günü", color: "#DB2777", counted: false },
];


// Gecici (transient) edge/ag hatalari: Cloudflare 520-524, gateway 502/504, ag kopmasi (status 0).
// Bu hatalarda yazma isteği origin'e ulasmamis olabilir -> IDEMPOTENT edit cagrilarini otomatik yeniden dene.
const TRANSIENT_STATUS = new Set([0, 408, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524]);
const isTransientStatus = (s) => TRANSIENT_STATUS.has(s);
// fn: () => axios(...)  ({ validateStatus:()=>true } ile cagrilmali). Gecici hatada backoff ile 3 kez dener.
// 401 alinirsa access token BIR KEZ yenilenip istek tekrarlanir (fn taze authHeaders() okumali).
async function requestWithRetry(fn, { retries = 3, base = 400 } = {}) {
  let last = { status: 0, data: null };
  let refreshed = false;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fn();
      if (res.status === 401 && !refreshed) {
        refreshed = true;
        const nt = await refreshAccessToken();
        if (nt) { attempt -= 1; continue; }  // taze token ile ayni denemeyi tekrarla
        return res;  // yenileme basarisiz -> 401 don
      }
      if (!isTransientStatus(res.status)) { if (attempt > 0) toast.dismiss("edit-retry"); return res; }
      last = res;
    } catch (e) {
      last = { status: 0, data: null };  // ag/CORS hatasi -> gecici say
    }
    if (attempt < retries) {
      if (attempt === 1) toast.loading("Bağlantı dalgalandı, tekrar deniyorum…", { id: "edit-retry" });
      await new Promise((r) => setTimeout(r, base * (2 ** attempt) + Math.random() * 150));
    }
  }
  toast.dismiss("edit-retry");
  return last;  // last.status hala gecici -> cagiran taraf "baglanti sorunu" olarak isler
}

// FAZ F5: serbest renklendirme paleti
const COLORS = [
  { c: "#FEF08A", t: "Sarı" }, { c: "#BBF7D0", t: "Yeşil" }, { c: "#FBCFE8", t: "Pembe" },
  { c: "#BFDBFE", t: "Mavi" }, { c: "#FED7AA", t: "Turuncu" }, { c: "#E9D5FF", t: "Mor" },
];

function LiveSheetDesktop() {
  const { canManage, isGarson, isSuperAdmin } = useAuth();
  const isSuperAdminRef = useRef(isSuperAdmin); isSuperAdminRef.current = isSuperAdmin;
  const [searchParams, setSearchParams] = useSearchParams();
  const _initDate = searchParams.get("date");
  const [date, setDate] = useState(
    (_initDate && /^\d{4}-\d{2}-\d{2}$/.test(_initDate) && !isNaN(new Date(_initDate).getTime()))
      ? _initDate : format(new Date(), "yyyy-MM-dd")
  );
  const [dayLocked, setDayLocked] = useState(false);
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") ? Number(searchParams.get("category")) : null);
  const [categories, setCategories] = useState([]);
  const [unmappedCount, setUnmappedCount] = useState(0);
  const [unmappedProducts, setUnmappedProducts] = useState([]);
  const [totalPax, setTotalPax] = useState(0);
  const [transferPax, setTransferPax] = useState(0);
  const [transferPaxDay, setTransferPaxDay] = useState(0);
  const [mapChoice, setMapChoice] = useState({}); // product_name -> secili category_id
  const [sheetData, setSheetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [boats, setBoats] = useState([]);
  const [statsTick, setStatsTick] = useState(0); // check-in/katılım değişince "gelen" sayacını canlı tazele
  const [times, setTimes] = useState([]);
  const [boatFilter, setBoatFilter] = useState(null); // null = TÜMÜ
  const [grouped, setGrouped] = useState(true); // Ayrimli gorunum (tekneler alt alta) — varsayilan ACIK
  const groupedRef = useRef(true);
  useEffect(() => { groupedRef.current = grouped; }, [grouped]);
  const groupHeadersRef = useRef([]); // [{r, boat_id, name, count, pax}]
  const rBoatRef = useRef({});        // r -> ait oldugu tekne id (baslik dahil)
  const focusBlankRef = useRef(null); // (boatId) -> teknenin ilk bos satirina odaklan (hayalet satir)
  const [timeFilter, setTimeFilter] = useState(null); // null = tüm saatler
  const [pendingOnly, setPendingOnly] = useState(false); // sadece check-in olmamislar
  const [pendingCount, setPendingCount] = useState(null);
  useEffect(() => { boatsRef.current = boats; }, [boats]); // eslint-disable-line react-hooks/exhaustive-deps
  const [renderKey, setRenderKey] = useState(0); // FortuneSheet remount (data prop sadece mount'ta okunur)
  const [zoom, setZoom] = useState(() => {
    const z = parseFloat(localStorage.getItem("ls_zoom"));
    return z && z >= 1 && z <= 2 ? z : 1;
  });
  const zoomRef = useRef(zoom);
  const changeZoom = useCallback((dir) => {
    const steps = [1, 1.1, 1.25, 1.5, 1.75, 2];
    setZoom((z) => {
      const idx = steps.findIndex((s) => Math.abs(s - z) < 0.001);
      let ni = dir === "reset" ? 0 : (idx < 0 ? 0 : idx) + (dir === "in" ? 1 : -1);
      ni = Math.max(0, Math.min(steps.length - 1, ni));
      const nz = steps[ni];
      zoomRef.current = nz;
      localStorage.setItem("ls_zoom", String(nz));
      setRenderKey((k) => k + 1); // zoom degisti -> remount (scroll restore korunur)
      return nz;
    });
  }, []);
  // Mobil yatay (kısa ekran) için kompakt araç çubuğu + daraltma düğmesi
  const [compact, setCompact] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-height: 600px)").matches);
  const [toolbarOpen, setToolbarOpen] = useState(false); // kompakt modda varsayılan KAPALI
  useEffect(() => {
    const mq = window.matchMedia("(max-height: 600px)");
    const onCh = () => setCompact(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onCh); else mq.addListener(onCh);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", onCh); else mq.removeListener(onCh); };
  }, []);
  const showTools = !compact || toolbarOpen;
  // Satir yeniden siralama — Google Sheets gibi tablo-ici surukle (satir numarasindan tut)
  const [selRows, setSelRows] = useState(() => new Set()); // secili satir r-indexleri
  const selRowsRef = useRef(selRows);
  useEffect(() => { selRowsRef.current = selRows; }, [selRows]);
  useEffect(() => { if (selRows.size === 0 && (searchMatchesRef.current || []).length === 0) setRowBars([]); }, [selRows]);
  const anchorRef = useRef(null);        // shift-secim capasi
  const [dropLineY, setDropLineY] = useState(null); // surukleme sirasinda birakma cizgisi (px, wrapper-rel)
  const dropIndexRef = useRef(null);     // birakilacak liste indeksi (visible sirada)
  const dragRef = useRef({ active: false, startY: 0, moving: [] });
  const dragBoatRef = useRef(null);      // suruklenen satirlarin ortak teknesi (ayni tekne ici siralama modu)
  const assignBoatRef = useRef(null);    // doWrite (gemi dropdown) icin guncel assignBoat
  const moveSelWithinBoatRef = useRef(null); // tekne-ici siralama butonlari icin
  const dragCursorYRef = useRef(0);      // sürükleme sirasindaki guncel imlec Y'si
  const autoScrollRafRef = useRef(null);  // ivmeli otomatik-kaydirma rAF
  const [rowBars, setRowBars] = useState([]); // secili satirlarin vurgusu [{top,height}]
  const [rowChecks, setRowChecks] = useState([]); // gorunur veri satirlari icin secim kutucuklari [{r,top,height}]
  const lastChecksRef = useRef("");
  const [invoiceBookingId, setInvoiceBookingId] = useState(null);
  // FATURA sutunundaki hucreye tiklaninca o rezervasyonun fatura penceresi acilir
  const onSheetMouseUp = () => {
    if (!canManage) return;
    setTimeout(() => {
      let sel; try { sel = wbRef.current && wbRef.current.getSelection(); } catch { return; }
      const r = sel?.[0]?.row?.[0], c = sel?.[0]?.column?.[0];
      if (typeof r !== "number" || typeof c !== "number" || r === 0) return;
      if (sel[0].row[1] !== r || sel[0].column[1] !== c) return;
      if (columnsRef.current[c]?.key !== "fatura") return;
      const bid = rowsByRRef.current[r]?.booking_id;
      if (bid) setInvoiceBookingId(bid);
    }, 60);
  };
  const openInvoiceForSel = () => { const r = [...selRowsRef.current][0]; const bid = rowsByRRef.current[r]?.booking_id; if (bid) setInvoiceBookingId(bid); };
  const [selectMode, setSelectMode] = useState(false); // "Toplu Seç" modu: kutucuklar sadece bu acikken gorunur
  const selectModeRef = useRef(false);
  const [headerBadges, setHeaderBadges] = useState([]); // tekne basligi cam/vip renkli rozetleri
  const [seatFilter, setSeatFilter] = useState(null);   // aktif oturum filtresi (bucket) -> eslesmeyenleri soluklastir
  const seatFilterRef = useRef(null);
  const [dimBars, setDimBars] = useState([]);           // filtre overlay barlari [{top,height,width,match}]
  const dimBarsRef = useRef([]);
  const lastHBRef = useRef("");
  const [stripBox, setStripBox] = useState(null); // row-header overlay konumu
  const [dragGhost, setDragGhost] = useState(null); // {x,y,count,label} surukleme hayaleti
  const [boatMenuOpen, setBoatMenuOpen] = useState(false); // "Tekneye Ata" menusu
  const [autoAssigning, setAutoAssigning] = useState(false); // "Otomatik Dagit" durumu
  const [showRedistribute, setShowRedistribute] = useState(false); // Yeniden/Hard Dagit butonlari gorunur mu (ayarlardan)
  useEffect(() => {
    axios.get(`${API}/api/sheet/redistribute-visible`, { headers }).then(r => setShowRedistribute(!!r.data?.show)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [manualModal, setManualModal] = useState(false); // (kaldirildi) eski popup — artik inline giris
  const [extraBlank, setExtraBlank] = useState(6); // tekne basi bos satir sayisi (sag tik ile artar)
  const [ctxMenu, setCtxMenu] = useState(null); // {x,y} sag tik menusu
  const boatDropRef = useRef(null);           // surukleme sirasinda uzerine gelinen tekne id
  const [boatDropId, setBoatDropId] = useState(null); // vurgulanan tekne sekmesi
  const [tagPopover, setTagPopover] = useState(null); // {x,y,tags,extras} etiket balonu
  const [tagEditor, setTagEditor] = useState(null); // {row_id, x, y, sel:{key:qty|true}, pax} etiket duzenleyici
  const [hoverBar, setHoverBar] = useState(null); // strip hover satiri {top,height,width,r} (grip gostergesi)
  const lastHoverRRef = useRef(null);
  const [boatZone, setBoatZone] = useState(null); // ayrimli gorunumde surukleme: hedef tekne bolgesi
  const boatZoneRef = useRef(null);
  const boatsRef = useRef([]);
  // Coklu kullanici saniyelik senkron (poll) guard'lari
  const writingRef = useRef(0);          // devam eden hucre yazmasi sayaci
  const isEditingCellRef = useRef(() => false); // kullanici o an bir hucre editorunde aktif yaziyor mu? (remount korumasi)
  const creatingRef = useRef({});        // r -> Promise: o satirda manuel-create devam ediyor (cift olusturmayi onler)
  const pollBusyRef = useRef(false);
  const lastPollRefetchRef = useRef(0);
  const lastStateIdsRef = useRef(null);  // onceki poll'un tum-gun row_id seti (yeni kayit algisi)
  const epochRef = useRef(null);         // global sheet epoch (urun tanimi override degisimi)
  const activeUsersRef = useRef([]);
  // Hizli arama (Ctrl+F / "/") — kapida check-in / musteri bulma
  const [searchOpen, setSearchOpen] = useState(false);
  const [rangeExportOpen, setRangeExportOpen] = useState(false);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [rangeBusy, setRangeBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInfo, setSearchInfo] = useState({ count: 0, idx: 0 });
  const searchMatchesRef = useRef([]);   // eslesen hucre listesi (sirali) -> [{r,c}]
  const searchCurRef = useRef(null);     // aktif eslesme hucresi {r,c}
  const lastHiliteRef = useRef(null);    // en son sariya boyanan hucre {r,c} (temizlemek icin)
  const allHiliteRef = useRef([]);       // TUM eslesen hucreler (hafif vurgu) -> [{r,c}]
  const refreshFilterViewRef = useRef(null);
  const [searchFilter, setSearchFilter] = useState(false); // "bulunanlara gore suz"
  const searchFilterRef = useRef(false);
  const filterDebRef = useRef(null);
  const searchInputRef = useRef(null);
  const heightsRef = useRef({});         // r -> yukseklik (cache)
  const rowCountRef = useRef(0);
  const geoRafRef = useRef(null);
  const sheetDataRef = useRef(null);     // sheetData senkron aynasi (optimistic reorder)
  const reorderQueueRef = useRef(Promise.resolve()); // arka plan PATCH sirasi

  const wbRef = useRef(null);              // FortuneSheet WorkbookInstance
  const rowsByRRef = useRef({});           // celldata satir indeksi -> {row_id, version, source, values}
  const rowIdToRRef = useRef({});          // row_id -> celldata satir indeksi (WS icin ters harita)
  const fieldColRef = useRef({});          // operation DB alani -> sutun tanimi (WS icin)
  const menuColRef = useRef({});           // menu anahtari -> sutun tanimi (WS icin)
  const colByKeyRef = useRef({});          // col.key -> sutun tanimi (manuel/override canli delta icin)
  const rowNewTimerRef = useRef(null);     // row_new otomatik tazeleme debounce timer'i
  const lastMutationTsRef = useRef(0);     // son basarili yerel yazma/olusturma ani (fetch yarisi korumasi)
  const columnsRef = useRef([]);           // sutun tanimlari (index sirali)
  const pendingRef = useRef({});           // debounce timer'lari: `${r}:${c}` -> timeout
  const suppressRef = useRef(false);       // programatik revert/remote sirasinda onOp'u yut
  const garsonColRef = useRef(null);       // garson sutunu index'i
  const layoutRef = useRef("aksam");       // aktif defter duzeni (sutun genisligi hafizasi icin)
  const wsRef = useRef(null);
  const reconnectRef = useRef({ attempts: 0, timer: null, closed: false });
  const [wsConnected, setWsConnected] = useState(false);
  const wsConnectedRef = useRef(false);
  useEffect(() => { wsConnectedRef.current = wsConnected; }, [wsConnected]);
  const [wsState, setWsState] = useState("connecting"); // connecting | live | reconnecting
  const [queuedCount, setQueuedCount] = useState(offlineQueue.queueSize()); // cevrimdisi yazma kuyrugu sayisi
  const flushQueueRef = useRef(null);
  const flushingRef = useRef(false);
  const [activeUsers, setActiveUsers] = useState([]);
  useEffect(() => { activeUsersRef.current = activeUsers; }, [activeUsers]);
  const [userFocus, setUserFocus] = useState({}); // user -> "Kolon (satır)" konumu
  const myEmailRef = useRef((() => { try { return JSON.parse(atob((localStorage.getItem("auth_token") || "..").split(".")[1] || "")).email; } catch { return null; } })());

  const token = localStorage.getItem("auth_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Anlik gorunum onbellegi (stale-while-revalidate): tarih/sekme gecislerinde BEKLETME YOK —
  // onbellekteki veri ANINDA gosterilir, arka planda tazelenir, degistiyse sessizce yenilenir.
  const dataCacheRef = useRef(new Map()); // viewKey -> son basarili API yaniti
  const dataSig = (d) => `${d.row_count}|${d.total_pax}|${d.transfer_pax}|${d.transfer_pax_day}|` +
    (d.rows || []).map((rm) => `${rm.r}:${rm.row_id}:${rm.version}:${rm.boat_id}:${rm.checked_in ? 1 : 0}`).join(",");

  // API yanitini ekrana uygula (state + ref'ler + FortuneSheet remount)
  const applyData = useCallback((data) => {
    setCategories(data.categories || []);
    setUnmappedCount(data.unmapped_count || 0);
    setUnmappedProducts(data.unmapped_products || []);
    setTotalPax(data.total_pax || 0);
    setTransferPax(data.transfer_pax || 0);
    setTransferPaxDay(data.transfer_pax_day || 0);
    if (activeCategory == null && data.active_category_id != null) setActiveCategory(data.active_category_id);
    setBoats(data.boats || []);
    setTimes(data.times || []);
    setPendingCount(data.pending_count != null ? data.pending_count : null);
    const byR = {};
    const byRowId = {};
    (data.rows || []).forEach((rm) => { byR[rm.r] = rm; byRowId[rm.row_id] = rm.r; });
    rowsByRRef.current = byR;
    rowIdToRRef.current = byRowId;
    columnsRef.current = data.columns || [];
    const fieldCol = {}; const menuCol = {}; const colByKey = {};
    (data.columns || []).forEach((c) => {
      colByKey[c.key] = c;
      if (c.source === "operation") fieldCol[c.field] = c;
      else if (c.source === "menu") menuCol[c.field] = c;
    });
    fieldColRef.current = fieldCol;
    menuColRef.current = menuCol;
    colByKeyRef.current = colByKey;
    const columnlen = {};
    (data.columns || []).forEach((c) => { columnlen[c.index] = c.width; });
    // Kisisel sutun genisligi hafizasi (localStorage) uygula
    layoutRef.current = data.defter || "aksam";
    try {
      const savedW = JSON.parse(localStorage.getItem(`ls_colw_${layoutRef.current === "sunset" ? "sunset2" : layoutRef.current}`) || "{}");
      Object.entries(savedW).forEach(([k, w]) => { if (typeof w === "number") columnlen[k] = w; });
    } catch { /* noop */ }
    // Role gore gizli sutunlar (orn. garson icin YILDIZ): sifir genislik
    const colhidden = {};
    (data.columns || []).forEach((c) => { if (c.hidden) { columnlen[c.index] = 1; colhidden[c.index] = 0; } });
    const rowCount = Math.max(data.row_count || ((data.total || 0) + 2), 30);
    const colCount = (data.columns || []).length || 26;
    // Ayrimli gorunum: tekne basliklari (r) ve her satirin ait oldugu tekne haritasi
    groupHeadersRef.current = data.group_headers || [];
    const hByR = {}; (data.group_headers || []).forEach((h) => { hByR[h.r] = h.boat_id; });
    const rToBoat = {}; let curBoat = null;
    for (let r = 1; r < rowCount; r++) {
      if (hByR[r] !== undefined) curBoat = hByR[r];
      else if (byR[r]) curBoat = byR[r].boat_id;
      rToBoat[r] = curBoat;
    }
    rBoatRef.current = rToBoat;
    setStatsTick((t) => t + 1); // tam yükleme sonrası "gelen" sayacını tazele
    // Ayrimli gorunum: tekne baslik satirlarini tam-genislik birlestir (banner)
    const merges = {};
    (data.group_headers || []).forEach((h) => { merges[`${h.r}_0`] = { r: h.r, c: 0, rs: 1, cs: colCount }; });
    // Ödeme Şekli sutunu -> acilir menu (Cari/Havale/Peşin/K.Kartı)
    const dataVerification = {};
    const sekliCol = (data.columns || []).find((c) => c.key === "sekli");
    if (sekliCol) {
      for (let rr = 1; rr < rowCount; rr++) {
        dataVerification[`${rr}_${sekliCol.index}`] = {
          type: "dropdown", type2: false,
          value1: "Cari,Havale,Peşin,K.Kartı", value2: "",
          checked: false, remote: false, prohibitInput: false,
          hintShow: false, hintText: "",
        };
      }
    }
    // Gemi Adı sutunu -> tekne secim menusu (tikla-sec ile tekne atama; sadece veri satirlarinda)
    const gemiCol = (data.columns || []).find((c) => c.key === "gemi");
    if (gemiCol && (data.boats || []).length) {
      // Tekne isimleri + (yetki varsa) "İPTAL ET" ve "🗑 SİL" secenekleri
      const boatNames = (data.boats || []).map((b) => b.name).join(",") + (canManage ? ",İPTAL ET,🗑 SİL" : "");
      (data.rows || []).forEach((rm) => {
        dataVerification[`${rm.r}_${gemiCol.index}`] = {
          type: "dropdown", type2: false,
          value1: boatNames, value2: "",
          checked: false, remote: false, prohibitInput: false,
          hintShow: false, hintText: "",
        };
      });
    }
    // Garson sutunu -> NATIVE acilir menu. Liste: yonetilen garsonlar (temiz, ustte) + mevcut
    // tum degerler (eski BUYUK HARF kayitlar dahil) -> "listede yok" uyarisi cikmaz. Yazim serbest.
    const garsonCol = (data.columns || []).find((c) => c.key === "garson");
    garsonColRef.current = garsonCol ? garsonCol.index : null;
    const _gSeen = new Set();
    const garsonOpts = [];
    for (const n of [...(data.managed_garson_names || []), ...(data.garson_names || [])]) {
      const nm = String(n || "").trim();
      if (!nm || _gSeen.has(nm)) continue;   // birebir tekrarları at (uyari cikmamasi icin verbatim tut)
      _gSeen.add(nm); garsonOpts.push(nm);
    }
    if (garsonCol && garsonOpts.length) {
      const gVal = garsonOpts.join(",");
      (data.rows || []).forEach((rm) => {
        dataVerification[`${rm.r}_${garsonCol.index}`] = {
          type: "dropdown", type2: false,
          value1: gVal, value2: "",
          checked: false, remote: false, prohibitInput: false,
          hintShow: false, hintText: "",
        };
      });
    }
    // Acenta sutunu -> acilir menu (VIATOR/GYG/KLOOK/HEADOUT/Lufertour/Lufercomtr; yazim serbest)
    const acentaCol = (data.columns || []).find((c) => c.key === "acenta");
    if (acentaCol) {
      (data.rows || []).forEach((rm) => {
        dataVerification[`${rm.r}_${acentaCol.index}`] = {
          type: "dropdown", type2: false,
          value1: "VIATOR,GYG,KLOOK,HEADOUT,Lufertour,Lufercomtr,DİĞER", value2: "",
          checked: false, remote: false, prohibitInput: false,
          hintShow: false, hintText: "",
        };
      });
    }
    const rowlen = { 0: 34 };
    // Salon (masa_no) -> tekneye gore KAT secim menusu; Masa No (alt_no) -> 1..90 secim menusu.
    const FLOOR_OPTS = { "MASAL": "1.KAT,2.KAT", "MEGA": "1.KAT,2.KAT", "LÜFER 6": "1.KAT,2.KAT,3.KAT" };
    const boatNameById = {}; (data.boats || []).forEach((b) => { boatNameById[b.id] = (b.name || "").trim(); });
    const salonCol = (data.columns || []).find((c) => c.key === "masa_no");
    if (salonCol) {
      (data.rows || []).forEach((rm) => {
        const bname = (boatNameById[rToBoat[rm.r]] || "").toUpperCase();
        const opts = FLOOR_OPTS[bname];
        if (opts) {
          dataVerification[`${rm.r}_${salonCol.index}`] = {
            type: "dropdown", type2: false, value1: opts, value2: "",
            checked: false, remote: false, prohibitInput: false, hintShow: false, hintText: "",
          };
        }
      });
    }
    const MASA_OPTS = Array.from({ length: 90 }, (_, i) => i + 1).join(",");
    const masaCol = (data.columns || []).find((c) => c.key === "alt_no");
    if (masaCol) {
      (data.rows || []).forEach((rm) => {
        dataVerification[`${rm.r}_${masaCol.index}`] = {
          type: "dropdown", type2: false, value1: MASA_OPTS, value2: "",
          checked: false, remote: false, prohibitInput: false, hintShow: false, hintText: "",
        };
      });
      // Ayni teknede AYNI Masa No tekrar ediyorsa o hucreleri YESIL boya (cakisma uyarisi)
      const byBoatVal = {};
      (data.rows || []).forEach((rm) => {
        const v = String((rm.values && rm.values.alt_no) ?? "").trim();
        if (!v) return;
        const kat = String((rm.values && rm.values.masa_no) ?? "").trim();  // Salon/KAT degeri
        const bid = rToBoat[rm.r];
        const vk = `${kat}||${v}`;  // ayni tekne + AYNI KAT + ayni Masa No -> gercek cakisma (farkli kat yesil OLMAZ)
        (byBoatVal[bid] = byBoatVal[bid] || {});
        (byBoatVal[bid][vk] = byBoatVal[bid][vk] || []).push(rm.r);
      });
      const dupRows = new Set();
      Object.values(byBoatVal).forEach((vm) => Object.values(vm).forEach((rs) => { if (rs.length > 1) rs.forEach((r) => dupRows.add(r)); }));
      if (dupRows.size) {
        const cd = Array.isArray(data.celldata) ? [...data.celldata] : [];
        const cdIdx = {}; cd.forEach((c, i) => { cdIdx[`${c.r}_${c.c}`] = i; });
        dupRows.forEach((r) => {
          const k = `${r}_${masaCol.index}`;
          if (cdIdx[k] != null) { const cell = cd[cdIdx[k]]; cd[cdIdx[k]] = { ...cell, v: { ...(cell.v || {}), bg: "#22C55E" } }; }
          else cd.push({ r, c: masaCol.index, v: { bg: "#22C55E" } });
        });
        data.celldata = cd;
      }
    }
    (data.rows || []).forEach((rm) => {
      // opsiyon hucresi: etiket satiri + opsiyon + Bókun basligi tam sigsin (cihazlar arasi kesilmeyi onler)
      rowlen[rm.r] = (rm.tags && rm.tags.length) ? 50 : 32;
    });
    // COKLU KULLANICI: remount'ta scroll konumunu KORU (aksi halde her uzak degisiklikte
    // sayfa basa kayiyor -> "sayfa kaymasi" sikayeti). Mevcut fortune-sheet scroll'unu yakala.
    let keepScroll = null;
    try {
      const sx = document.querySelector(".luckysheet-scrollbar-x");
      const sy = document.querySelector(".luckysheet-scrollbar-y");
      const left = sx ? sx.scrollLeft : 0;
      const top = sy ? sy.scrollTop : 0;
      if (left > 0 || top > 0) keepScroll = { left, top };
    } catch { /* noop */ }
    const sd = {
      name: `${(data.defter || "").toUpperCase()} ${date}`,
      celldata: data.celldata || [],
      row: rowCount,
      column: colCount,
      config: { columnlen, rowlen, merge: merges, colhidden },
      zoomRatio: zoomRef.current,
      dataVerification,
      // Yalnizca ust baslik satiri sabit; A/B/C/D kolonlari artik yatayda serbest kayar (dondurma kaldirildi)
      frozen: { type: "row", range: { row_focus: 0, column_focus: 0 } },
      ...(keepScroll ? { scrollLeft: keepScroll.left, scrollTop: keepScroll.top } : {}),
    };
    sheetDataRef.current = sd;   // senkron (optimistic reorder icin)
    // Arama filtresi aciksa: canli yenilemelerde de eslesmeyen satirlar gizli kalsin
    if (searchFilterRef.current && (searchMatchesRef.current || []).length) {
      const matchRs = new Set(searchMatchesRef.current.map((m) => m.r));
      const visibleBoats = new Set();
      matchRs.forEach((r) => { const b = rToBoat[r]; if (b != null) visibleBoats.add(b); });
      const rowhidden = {};
      for (let r = 1; r < rowCount; r++) {
        const isHeader = hByR[r] !== undefined;
        const headerVisible = isHeader && visibleBoats.has(hByR[r]);
        if (!matchRs.has(r) && !headerVisible) rowhidden[r] = 0;
      }
      sd.config.rowhidden = rowhidden;
    }
    setSheetData(sd);
    setRenderKey((k) => k + 1); // yeni veri geldi -> FortuneSheet'i remount et
  }, [date, activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastAppliedKeyRef = useRef(null); // en son ekrana uygulanan gorunum anahtari
  const loadRetryRef = useRef(0); // ardisik yukleme hatasi sayaci (otomatik yeniden deneme)

  const fetchSheet = useCallback(async () => {
    const viewKey = `${date}|${activeCategory}|${grouped ? "g" : boatFilter}|${timeFilter}|${pendingOnly ? "p" : ""}|${extraBlank}`;
    const cached = dataCacheRef.current.get(viewKey);
    const isSameView = lastAppliedKeyRef.current === viewKey;
    // Gorunum DEGISIYORSA onbellegi ANINDA goster (bekletme yok); ayni gorunumun
    // yenilenmesinde bayat veriyi ekrana geri getirme (yerel degisiklikler korunur).
    if (cached && !isSameView) applyData(cached);
    lastAppliedKeyRef.current = viewKey;
    setLoading(true);
    setError(null);
    try {
      let url = `${API}/api/sheet/fortune/${date}?`;
      if (activeCategory === "unmapped") url += `unmapped=1&`;
      else if (activeCategory != null) url += `category_id=${activeCategory}&`;
      if (grouped) url += `group=1&`;
      else if (boatFilter != null) url += `boat_id=${boatFilter}&`;
      if (timeFilter != null) url += `time=${encodeURIComponent(timeFilter)}&`;
      if (pendingOnly) url += `pending=1&`;
      if (grouped && extraBlank && extraBlank !== 6) url += `blank_rows=${extraBlank}&`;
      // Gecici origin/Cloudflare hatasi (timeout, bos/HTML yanit, 5xx) -> gorunumu SILME.
      // Hem bos-rows hem EXCEPTION durumunda kisa backoff ile 4 kez dene; son denemede firlat.
      let data = null;
      let lastErr = null;
      const hadRows = Object.keys(rowsByRRef.current).length > 0;
      const fetchStartedAt = Date.now();
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const resp = await axios.get(url, { headers, timeout: 25000 });
          const body = resp.data;
          // Gecerli JSON gorunum yaniti mi? (Cloudflare bazen HTML/bos string dondurur)
          if (!body || typeof body !== "object" || !Array.isArray(body.categories)) {
            throw new Error("invalid_sheet_response");
          }
          data = body;
          lastErr = null;
          const empty = !(data.rows && data.rows.length);
          if (!empty || !hadRows || attempt === 3) break;
        } catch (err) {
          lastErr = err;
          data = null;
          const st = err?.response?.status;
          if (st === 401 || st === 403 || st === 404 || attempt === 3) break; // kalici hata -> tekrar deneme yok
        }
        await new Promise((r) => setTimeout(r, 450 * (attempt + 1)));
      }
      if (!data) throw (lastErr || new Error("sheet_load_failed"));
      // YARIS KORUMASI: istek yoldayken bu istemci satir olusturdu/yazdi -> bu anlik goruntu o
      // degisikligi icermeyebilir (yeni satir "kaybolur", kullanici tekrar yazar -> cift kayit).
      // Bayat goruntuyu UYGULAMA; kisa sure sonra yeniden al.
      if (isSameView && lastMutationTsRef.current > fetchStartedAt) {
        setTimeout(() => { fetchSheetRef.current(); }, 400);
        return;
      }
      const changed = !cached || isSameView || dataSig(cached) !== dataSig(data);
      dataCacheRef.current.set(viewKey, data);
      if (dataCacheRef.current.size > 24) { // basit LRU siniri
        dataCacheRef.current.delete(dataCacheRef.current.keys().next().value);
      }
      if (changed) {
        // Kullanicinin BEKLEYEN (debounce) veya UCUSAN (in-flight) hucre yazimi varsa REMOUNT ETME:
        // applyData tum sheet'i yeniden kurar; henuz kaydedilmemis/hizli girilen degerleri EZER (kaydetmeme sorunu).
        // Bu durumda remount'u kisa sure ertele; yazim bitince guncel gorunum uygulanir.
        const hasPendingWrites = writingRef.current > 0 || Object.keys(pendingRef.current).length > 0 || isEditingCellRef.current();
        if (hasPendingWrites && isSameView && hadRows) {
          setTimeout(() => { fetchSheetRef.current(); }, 900);
        } else {
          if (isSameView && hadRows) captureScroll();
          applyData(data);
        }
      } // degismediyse remount YOK
      loadRetryRef.current = 0; // basarili -> sayaci sifirla
      toast.dismiss("reload"); toast.dismiss("stale");
    } catch (e) {
      console.error(e);
      if (cached) {
        // Kisa origin/Cloudflare blip'i — onbellekteki gorunum zaten ekranda, SESSIZ gec (kullaniciyi rahatsiz etme).
      } else {
        // Ilk yuklemede origin/Cloudflare gecici hatasi -> BOS birakma, otomatik yeniden dene.
        // Kalici hatalarda (401/403/404) tekrar deneme; dogrudan hata goster.
        const st = e?.response?.status;
        const transient = !(st >= 400 && st < 500);
        if (transient && loadRetryRef.current < 3) {
          loadRetryRef.current += 1;
          // Ilk deneme SESSIZ; sadece art arda birkac deneme de basarisizsa yumusak bir bilgi ver.
          if (loadRetryRef.current >= 2) toast.loading("Bağlantı yeniden kuruluyor…", { id: "reload" });
          setTimeout(() => { fetchSheetRef.current(); }, 2500);
        } else {
          setError(e?.response?.data?.detail || "Defter yüklenemedi — bağlantıyı kontrol edip tekrar deneyin");
          setSheetData(null);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [date, activeCategory, boatFilter, timeFilter, grouped, pendingOnly, extraBlank, applyData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchSheet(); }, [fetchSheet]);
  // IBM Plex Sans yuklenince canvas'i bir kez yeniden ciz (font gec gelirse)
  useEffect(() => {
    if (document.fonts && document.fonts.load) {
      document.fonts.load('14px "IBM Plex Sans"').then(() => setRenderKey((k) => k + 1)).catch(() => {});
    }
  }, []);

  // fetchSheet'i ref'te tut (WS efektinin kategori degisiminde yeniden baglanmasini onlemek icin)
  const fetchSheetRef = useRef(fetchSheet);
  useEffect(() => { fetchSheetRef.current = fetchSheet; }, [fetchSheet]);

  // Reconnect sonrasi AKILLI resync: once hafif durum ucu (/fortune-state, ~2KB) kontrol edilir;
  // yalnizca sunucudan geride kalindiysa agir tam yenileme (~800KB) yapilir.
  // Boylece kisa kopma dongulerinde (churn) her reconnect'te agir payload spam'i olmaz.
  const resyncIfStale = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/sheet/fortune-state/${date}`, { headers, validateStatus: () => true });
      if (res.status !== 200) return;
      const serverRows = res.data.rows || [];
      let stale = false;
      for (const [id, ver] of serverRows) {
        const r = rowIdToRRef.current[id];
        if (r == null) continue;
        const rm = rowsByRRef.current[r];
        if (!rm) continue;
        // NOT: boat_id KARSILASTIRILMAZ. fortune gorunum icin boat_id'yi remap edebilir
        // (kategoride olmayan tekne -> "Atanmamis"), fortune-state ise HAM DB boat_id doner.
        // Bu fark her poll'da yanlis-pozitif stale -> sonsuz refetch -> titreme yaratirdi.
        // Tekne degisimi zaten version'i artirir, o yuzden version karsilastirmasi yeterli.
        if ((ver || 1) > (rm.version || 1)) { stale = true; break; }
      }
      const ids = serverRows.map((x) => x[0]);
      const prev = lastStateIdsRef.current;
      if (prev && (ids.length !== prev.size || ids.some((id) => !prev.has(id)))) stale = true;
      lastStateIdsRef.current = new Set(ids);
      if (stale && !dragRef.current.active && !isEditingCellRef.current()) {
        const jitter = 150 + Math.floor(Math.random() * 900);
        // Jitter sonrasi TEKRAR kontrol: bu arada kullanici yazmaya baslamis olabilir -> girisini silme.
        setTimeout(() => {
          if (!dragRef.current.active && !isEditingCellRef.current() &&
              writingRef.current === 0 && Object.keys(pendingRef.current).length === 0) {
            fetchSheetRef.current();
          }
        }, jitter);
      }
    } catch { /* durum ucu ulasilamazsa sessiz gec; siradaki poll yakalar */ }
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps
  const resyncRef = useRef(resyncIfStale);
  useEffect(() => { resyncRef.current = resyncIfStale; }, [resyncIfStale]);
  const dateRef = useRef(date);
  useEffect(() => { dateRef.current = date; }, [date]);

  // Secili gunun kilitli olup olmadigini getir (kilitli gune yeniden dagit/kural dokunmaz)
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/sheet/locked-dates`, { headers }).then((r) => {
      if (alive) setDayLocked((r.data?.locked_dates || []).includes(date));
    }).catch(() => {});
    return () => { alive = false; };
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDayLock = useCallback(async () => {
    const next = !dayLocked;
    if (next && !window.confirm(`${date} KİLİTLENSİN mi?\n\nKilitli günde "Yeniden Dağıt" ve kural değişiklikleri bu güne DOKUNMAZ.`)) return;
    try {
      const { data } = await axios.post(`${API}/api/sheet/lock-date`, { date, locked: next }, { headers });
      setDayLocked(!!data?.locked);
      toast.success(data?.locked ? "🔒 Gün kilitlendi" : "🔓 Kilit açıldı");
    } catch (e) { toast.error("İşlem başarısız: " + (e?.response?.data?.detail || "")); }
  }, [dayLocked, date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Otomatik Dagit: atanmamis rezervasyonlari kalkis/tur tipine gore dogru bolmeye tasi
  const runAutoAssign = useCallback(async () => {
    setAutoAssigning(true);
    try {
      const body = { date };
      if (activeCategory != null && activeCategory !== "unmapped") body.category_id = activeCategory;
      const { data } = await axios.post(`${API}/api/sheet/auto-assign`, body, { headers });
      toast.success(data.message || "Otomatik dağıtıldı");
      dataCacheRef.current.clear();
      await fetchSheetRef.current();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Otomatik dağıtım başarısız");
    }
    setAutoAssigning(false);
  }, [date, activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tüm tarihler: bugün ve sonraki TÜM tarihlerdeki ATANMAMIŞ satırları kurala göre dağıt
  // (redistribute YOK -> elle atanmış satırlara dokunulmaz, yalnız Atanmamış'takiler taşınır)
  const runAutoAssignAll = useCallback(async () => {
    if (!window.confirm("Bugün ve SONRAKİ TÜM tarihlerdeki ATANMAMIŞ rezervasyonlar kurallara göre otomatik dağıtılacak.\n(Elle atanmış satırlara dokunulmaz.)\nDevam edilsin mi?")) return;
    setAutoAssigning(true);
    const tid = toast.loading("Tarihler taranıyor...");
    try {
      const { data: dd } = await axios.get(`${API}/api/sheet/assign-dates`, { headers });
      const dates = dd.dates || [];
      if (!dates.length) { toast.info("İleri tarihli rezervasyon bulunamadı", { id: tid }); setAutoAssigning(false); return; }
      let total = 0, done = 0;
      for (const d of dates) {
        try {
          const { data } = await axios.post(`${API}/api/sheet/auto-assign`, { date: d }, { headers, timeout: 90000 });
          total += (data.moved || 0);
        } catch { /* bu günü atla, devam et */ }
        done++;
        toast.loading(`Dağıtılıyor... ${done}/${dates.length} gün · ${total} taşındı`, { id: tid });
      }
      toast.success(`Tamamlandı: ${dates.length} gün tarandı, ${total} rezervasyon dağıtıldı`, { id: tid });
      dataCacheRef.current.clear();
      await fetchSheetRef.current();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dağıtım başarısız", { id: tid });
    }
    setAutoAssigning(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mevcut atamaları kural değişikliğine göre yeniden diz (kilitli/iptal/Rezerve hariç)
  const runRedistribute = useCallback(async () => {
    if (!window.confirm("Mevcut atamalar yeni kurallara göre yeniden dizilecek.\n(Kilitli/reschedule, iptal ve Rezerve teknedeki satırlara dokunulmaz.)\nDevam edilsin mi?")) return;
    setAutoAssigning(true);
    try {
      const body = { date, redistribute: true };
      if (activeCategory != null && activeCategory !== "unmapped") body.category_id = activeCategory;
      const { data } = await axios.post(`${API}/api/sheet/auto-assign`, body, { headers });
      toast.success(data.message || "Yeniden dağıtıldı");
      dataCacheRef.current.clear();
      await fetchSheetRef.current();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yeniden dağıtım başarısız");
    }
    setAutoAssigning(false);
  }, [date, activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // HARD yeniden dağıt: bugün ve tüm ileri tarihli rezervasyonları kurala göre yeniden diz
  const runHardRedistribute = useCallback(async () => {
    if (!window.confirm("⚠️ HARD YENİDEN DAĞIT\n\nBugün ve SONRAKİ TÜM İLERİ TARİHLİ rezervasyonlar kurallara göre yeniden dağıtılacak.\n(İptal ve Rezerve teknedeki satırlara dokunulmaz.)\n\nBu işlem geri alınamaz. Devam edilsin mi?")) return;
    // Kilitli (elle atanmış) satırlar da sıfırlansın mı?
    const force = window.confirm("Elle atadığınız KİLİTLİ 🔒 satırlar da sıfırlanıp yeniden dağıtılsın mı?\n\nTamam = kilitliler DE sıfırlansın\nİptal = kilitliler KORUNSUN (önerilen)");
    setAutoAssigning(true);
    const tid = toast.loading("Tarihler taranıyor...");
    try {
      const { data: dd } = await axios.get(`${API}/api/sheet/assign-dates`, { headers });
      const dates = dd.dates || [];
      if (!dates.length) { toast.info("İleri tarihli rezervasyon bulunamadı", { id: tid }); setAutoAssigning(false); return; }
      let total = 0, done = 0;
      for (const d of dates) {
        try {
          const { data } = await axios.post(`${API}/api/sheet/auto-assign`, { date: d, redistribute: true, force }, { headers, timeout: 90000 });
          total += (data.moved || 0);
        } catch { /* bu günü atla, devam et */ }
        done++;
        toast.loading(`Yeniden dağıtılıyor... ${done}/${dates.length} gün · ${total} taşındı`, { id: tid });
      }
      toast.success(`Tamamlandı: ${dates.length} gün, ${total} rezervasyon yeniden dağıtıldı`, { id: tid });
      dataCacheRef.current.clear();
      await fetchSheetRef.current();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hard yeniden dağıtım başarısız", { id: tid });
    }
    setAutoAssigning(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Manuel Rez Ekle (popup): girilen alanları manual/cell ile zincirleyerek tek rez oluşturur
  const createManualFull = useCallback(async (boatId, fields) => {
    const entries = Object.entries(fields).filter(([, v]) => String(v ?? "").trim() !== "");
    if (!entries.length) { toast.error("En az bir alan girin"); return false; }
    try {
      let rowId = null;
      for (const [field, value] of entries) {
        const body = rowId
          ? { row_id: rowId, booking_date: dateRef.current, field, value }
          : { booking_date: dateRef.current, boat_id: boatId, field, value };
        const res = await axios.post(`${API}/api/sheet/manual/cell`, body, { headers: authHeaders(), validateStatus: () => true });
        if (res.status !== 200) { toast.error(res.data?.detail || "Eklenemedi"); return false; }
        if (!rowId) rowId = res.data.row_id;
      }
      toast.success("Manuel rezervasyon eklendi");
      dataCacheRef.current.clear();
      await fetchSheetRef.current();
      return true;
    } catch { toast.error("Ağ hatası — eklenemedi"); return false; }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // (server bg oncelik sirasi: etiket > cell_colors > check-in yesil > salt-okunur gri > yok)
  const cellOrigBg = useCallback((r, c) => {
    const rm = rowsByRRef.current[r];
    const col = columnsRef.current[c];
    if (!col) return null;
    if (col.key === "etiket" && rm && rm.tags && rm.tags.length) return rm.tags[0].bg;
    const color = rm && rm.cell_colors && rm.cell_colors[col.key];
    if (color) return color;
    if (rm && rm.checked_in) return "#bbf7d0";
    if (!col.editable) return "#f3f4f6";
    return r % 2 === 0 ? "#eef2f6" : null; // zebra tonu (backend ile ayni parite)
  }, []);

  const revertCell = useCallback((r, c, oldVal) => {
    if (!wbRef.current) return;
    suppressRef.current = true;
    try {
      wbRef.current.setCellValue(r, c, oldVal == null ? "" : oldVal);
    } catch (e) { /* noop */ }
    setTimeout(() => { suppressRef.current = false; }, 60);
  }, []);

  // ---- Tek hucreyi backend'e yaz ----
  const doWrite = useCallback(async (r, c, rawVal) => {
    if (rawVal === undefined) return; // savunma: reset/no-op op'undan gelen undefined'i persist ETME
    const col = columnsRef.current[c];
    const rm = rowsByRRef.current[r];
    if (!col) return;
    const valStr = rawVal == null ? "" : String(rawVal).trim();
    // Yaz(ma) oncesi token dolmus/dolmak uzereyse YENILE. Ozellikle kopyala-yapistir
    // cok sayida yazmayi ayni anda tetikledigi icin "token expired" hatasini onler (single-flight).
    if (isTokenExpiring(getToken())) { await refreshAccessToken(); }

    // --- BOŞ satır: ayni sayfadan yazarak MANUEL rezervasyon olustur ---
    if (!rm || !rm.row_id) {
      if (groupedRef.current && valStr && MANUAL_FIELDS.has(col.key)) {
        // Ayni satirda es zamanli 2. create'i onle: create suruyorsa bekle, sonra
        // ayni hucreyi UPDATE olarak yeniden isle (cift manuel satir olusmasini engeller).
        if (creatingRef.current[r]) {
          try { await creatingRef.current[r]; } catch (e) { /* noop */ }
          return doWrite(r, c, rawVal);
        }
        let boatId = rBoatRef.current[r];
        if (boatId === "__cancelled__") boatId = null; // iptaller bolumune eklenmez
        writingRef.current += 1;
        let resolveCreate;
        creatingRef.current[r] = new Promise((res) => { resolveCreate = res; });
        try {
          const res = await axios.post(`${API}/api/sheet/manual/cell`,
            { booking_date: dateRef.current, boat_id: boatId, field: col.key, value: valStr },
            { headers: authHeaders(), validateStatus: () => true });
          if (res.status === 200 && res.data?.row_id != null) {
            // REFRESH YOK: yeni row_id'yi yerel modele isle -> satir "gercek" olur,
            // sonraki hucreler refreshsiz override/operation yolundan gider. Boylece
            // "yeni rez eklerken her hucreye yazinca sayfa yenileniyor" sorunu biter.
            const newId = res.data.row_id;
            rowsByRRef.current[r] = {
              r, row_id: newId, boat_id: boatId, version: res.data?.version || 1,
              source: "manual", values: { [col.key]: valStr }, menu_cells: {}, checked_in: false,
            };
            rowIdToRRef.current[newId] = r;
            lastMutationTsRef.current = Date.now();
            dataCacheRef.current.clear(); // arka plan tazeleme icin; simdi REFRESH ETME
            if (res.data?.created === false) toast.info("Satır zaten eklenmişti — aynı satıra devam ediliyor", { id: "manual-dup" });
            else toast.success("Manuel rezervasyon eklendi — yazmaya devam edebilirsiniz");
          } else {
            revertCell(r, c, "");
            reportTelemetryRef.current("save_failed", { field: col.key, label: col.label, value: valStr, row_date: dateRef.current, queued: false, status_code: res.status, detail: res.data?.detail, reason: "manual_create" });
            toast.error(res.data?.detail || "Eklenemedi");
          }
        } catch { revertCell(r, c, ""); reportTelemetryRef.current("save_failed", { field: col.key, label: col.label, value: valStr, row_date: dateRef.current, queued: false, reason: "manual_create_network" }); toast.error("Ağ hatası — eklenemedi"); }
        finally {
          writingRef.current -= 1;
          if (resolveCreate) resolveCreate();
          delete creatingRef.current[r];
        }
        return;
      }
      revertCell(r, c, "");
      if (!groupedRef.current) toast.error("Satır kimliği çözülemedi — işlem reddedildi");
      else if (valStr) toast.info("Manuel eklemek için: Otel / İsim / Pax / KOD / Opsiyon / Saat / Telefon / Acenta sütununa yazın");
      return;
    }
    // Gemi Adı hucresi: acilir menuden tekne secildi -> tekne atamasi (hucre yazmasi degil)
    if (col.key === "gemi") {
      const name = String(rawVal == null ? "" : rawVal).trim();
      revertCell(r, c, rm.values[col.key]); // gorunum atama/iptal sonrasi refetch ile guncellenir
      // "İPTAL ET" secildiyse: rezervasyonu iptal et (tekne atamasi degil)
      if (name === "İPTAL ET") {
        (async () => {
          const res = await axios.patch(`${API}/api/sheet/row/${rm.row_id}/cancel`,
            { cancelled: true }, { headers, validateStatus: () => true });
          if (res.status === 200) { toast.success("Rezervasyon iptal edildi"); dataCacheRef.current.clear(); await fetchSheetRef.current(); }
          else toast.error(res.data?.detail || "İptal edilemedi");
        })();
        return;
      }
      // "🗑 SİL" secildiyse: satiri KALICI sil (yalnizca manuel rez; Bókun icin backend reddeder)
      if (name === "🗑 SİL") {
        (async () => {
          if (!window.confirm("Bu satır KALICI olarak silinsin mi?\n\n(Yalnızca MANUEL rezervasyonlar silinebilir. Bókun rezervasyonları için 'İPTAL ET' kullanın.)")) return;
          const res = await axios.delete(`${API}/api/sheet/row/${rm.row_id}`, { headers, validateStatus: () => true });
          if (res.status === 200) {
            toast.success("Satır silindi");
            if (rowIdToRRef.current[rm.row_id] != null) delete rowIdToRRef.current[rm.row_id];
            delete rowsByRRef.current[r];
            dataCacheRef.current.clear();
            await fetchSheetRef.current();
          } else toast.error(res.data?.detail || "Silinemedi");
        })();
        return;
      }
      const bt = boatsRef.current.find((b) => (b.name || "").trim() === name);
      if (!bt) { if (name) toast.error("Tekne bulunamadı"); return; }
      if (bt.id === rm.boat_id) return; // zaten bu teknede
      if (assignBoatRef.current) assignBoatRef.current(bt.id, [rm.row_id]);
      return;
    }
    // Kimlik/görünüm alanları (Bókun + manuel): override yolu — HER satırda tıkla-yaz düzenlenebilir
    if (OVERRIDE_FIELDS.has(col.key)) {
      writingRef.current += 1;
      try {
        const res = await requestWithRetry(() => axios.post(`${API}/api/sheet/manual/cell`,
          { row_id: rm.row_id, booking_date: dateRef.current, field: col.key, value: valStr },
          { headers: authHeaders(), validateStatus: () => true }));
        if (res.status === 200) {
          lastMutationTsRef.current = Date.now();
          if (res.data?.refetch) { dataCacheRef.current.clear(); await fetchSheetRef.current(); }
          else {
            const shown = res.data?.value != null ? res.data.value : valStr;
            rm.values[col.key] = shown;
            if (res.data?.version != null) rm.version = res.data.version;
            if (shown !== valStr) revertCell(r, c, shown); // override sıfırlandı -> orijinal Bókun değerini hücrede göster
          }
        }
        else if (res.status === 404) {
          // Satir sunucuda yok -> yerel gorunum bayat: aciklayici mesaj + sessiz tam yenile
          reportTelemetryRef.current("save_failed", { field: col.key, label: col.label, value: valStr, row_id: rm.row_id, customer_name: rm.values?.isim, row_date: dateRef.current, queued: false, status_code: 404, detail: res.data?.detail });
          toast.error("Bu satır artık yok (silinmiş ya da başka biri değiştirmiş) — defter yenileniyor, lütfen tekrar yazın", { id: "row-gone", duration: 6000 });
          dataCacheRef.current.clear();
          setTimeout(() => { fetchSheetRef.current(); }, 150);
        }
        else if (isTransientStatus(res.status)) {
          // Gecici baglanti hatasi (Cloudflare 520 vb.) — degeri GERI ALMA + KUYRUGA AL, baglanti gelince gonderilir.
          rm.values[col.key] = valStr;
          const n = offlineQueue.enqueue({ method: "post", url: `${API}/api/sheet/manual/cell`,
            body: { row_id: rm.row_id, booking_date: dateRef.current, field: col.key, value: valStr }, label: `override:${col.key}` });
          setQueuedCount(n);
          toast.error(`Bağlantı yok — değişiklik kuyruğa alındı, gelince otomatik gönderilecek (${n} bekliyor)`, { id: "edit-queued", duration: 5000 });
        }
        else { revertCell(r, c, rm.values[col.key]); toast.error(res.data?.detail || "Kaydedilemedi"); }
      } catch { revertCell(r, c, rm.values[col.key]); toast.error("Ağ hatası — kaydedilemedi"); }
      finally { writingRef.current -= 1; }
      return;
    }
    // Yıldız: otomatik (yorum senkronu) doldurulur; yalnızca süper admin elle düzenleyebilir
    if (col.key === "yildiz" && !isSuperAdminRef.current) {
      revertCell(r, c, rm.values[col.key]);
      toast.error("Yıldız alanı yorumlardan otomatik dolar; yalnızca süper admin düzenleyebilir");
      return;
    }
    // Deger donusturme
    let payloadVal;
    if (col.type === "number") {
      if (rawVal === "" || rawVal == null) payloadVal = null;
      else {
        payloadVal = Number(rawVal);
        if (Number.isNaN(payloadVal)) {
          revertCell(r, c, rm.values[col.key]);
          toast.error(`"${col.label}" için sayısal değer girin`);
          return;
        }
      }
    } else {
      payloadVal = rawVal == null ? "" : String(rawVal);
      // Aciklama: otomatik ekstra-odeme uyarisi HUCREDE gosteriliyor ama NOTLARA yazilmaz.
      // Kaydetmeden once bilinen uyari onekini ayikla (elle not korunur, uyari otomatik hesaplanir).
      if (col.key === "aciklama" && rm.aciklama_warn) {
        let s = payloadVal;
        if (s.startsWith(rm.aciklama_warn)) {
          s = s.slice(rm.aciklama_warn.length).replace(/^\s*·\s*/, "");
        }
        payloadVal = s;
      }
    }

    writingRef.current += 1;
    try {
      let res;
      let expVer = rm.version;
      // Hizli/ard-arda ayni satira yazimda version geride kalabilir -> 409'da KULLANICININ degerini
      // guncel version ile YENIDEN gonder (kendi hizli edit'i kazanir). Boylece "kaydedilemedi/cakisma" olmaz.
      for (let t = 0; t < 3; t++) {
        if (col.source === "menu") {
          res = await requestWithRetry(() => axios.patch(`${API}/api/sheet/row/${rm.row_id}/menu-cell`,
            { key: col.field, value: payloadVal, expected_version: expVer },
            { headers: authHeaders(), validateStatus: () => true }));
        } else {
          res = await requestWithRetry(() => axios.patch(`${API}/api/sheet/row/${rm.row_id}/cell`,
            { field: col.field, value: payloadVal, expected_version: expVer },
            { headers: authHeaders(), validateStatus: () => true }));
        }
        if (res.status === 409 && res.data?.current_version && t < 2) {
          expVer = res.data.current_version;
          rm.version = res.data.current_version;
          continue;
        }
        break;
      }

      if (res.status === 200) {
        rm.version = res.data.version;
        rm.values[col.key] = payloadVal;
        lastMutationTsRef.current = Date.now();
        if (col.key === "katilim") {
          // Katılım <-> Geldi: değer girildi → yeşil (geldi), boşaldı → geri al (canlı)
          rm.checked_in = !!(payloadVal != null && payloadVal !== "" && Number(payloadVal) > 0);
          rm.cell_colors = { ...(rm.cell_colors || {}) };
          if (rm.checked_in) rm.cell_colors.__checkin = "1"; else delete rm.cell_colors.__checkin;
          suppressRef.current = true;
          columnsRef.current.forEach((c2) => { try { wbRef.current.setCellFormat(r, c2.index, "bg", cellOrigBg(r, c2.index)); } catch { /* noop */ } });
          setTimeout(() => { suppressRef.current = false; }, 60);
          setStatsTick((t) => t + 1);
        }
        if (col.source === "menu") {
          rm.menu_cells = res.data.menu_cells;
          // MENÜ ozet sutununu kovalardan yeniden hesapla (donmus __ov.menu backend'de temizlendi)
          try {
            const menuCol = (columnsRef.current || []).find((cc) => cc.key === "menu");
            if (menuCol) {
              const parts = [];
              (columnsRef.current || []).forEach((cc) => {
                if (cc.source !== "menu") return;
                const cv = rm.values[cc.key];
                const nv = cv == null || cv === "" ? 0 : Number(cv);
                if (!nv) return;
                const num = Number.isInteger(nv) ? nv : nv;
                parts.push(`${num} ${MENU_LABELS[cc.field] || (cc.field || "").toUpperCase()}`);
              });
              const menuText = parts.join(" + ");
              rm.values["menu"] = menuText;
              suppressRef.current = true;
              try { wbRef.current.setCellValue(r, menuCol.index, menuText); } catch (e) { /* noop */ }
              setTimeout(() => { suppressRef.current = false; }, 40);
            }
          } catch (e) { /* noop */ }
        }
      } else if (res.status === 409) {
        // Cakisma: sadece o hucreyi sunucudaki guncel degere cek + version guncelle (remount YOK)
        const cur = res.data?.current_value;
        rm.version = res.data?.current_version || rm.version;
        rm.values[col.key] = cur;
        revertCell(r, c, cur);
        toast.error(`Çakışma: "${col.label}" ${res.data?.updated_by || "başka biri"} tarafından değiştirilmiş. Güncel değer alındı.`);
      } else if (isTransientStatus(res.status)) {
        // Gecici baglanti hatasi — degeri KORU + KUYRUGA AL (replay'de guncel version ile gonderilir).
        rm.values[col.key] = payloadVal;
        const _url = col.source === "menu" ? `${API}/api/sheet/row/${rm.row_id}/menu-cell` : `${API}/api/sheet/row/${rm.row_id}/cell`;
        const _body = col.source === "menu"
          ? { key: col.field, value: payloadVal, expected_version: rm.version }
          : { field: col.field, value: payloadVal, expected_version: rm.version };
        const n = offlineQueue.enqueue({ method: "patch", url: _url, body: _body, label: `cell:${col.key}` });
        setPendingCount(n);
        reportTelemetryRef.current("save_failed", { field: col.field, label: col.label, value: payloadVal, row_id: rm.row_id, customer_name: rm.values?.isim, row_date: date, queued: true, status_code: res.status });
        toast.error(`Bağlantı yok — değişiklik kuyruğa alındı, gelince otomatik gönderilecek (${n} bekliyor)`, { id: "edit-queued", duration: 5000 });
      } else if (res.status === 404) {
        // Satir sunucuda yok (silinmis/birlestirilmis) -> yerel gorunum bayat: aciklayici mesaj + sessiz tam yenile
        reportTelemetryRef.current("save_failed", { field: col.field, label: col.label, value: payloadVal, row_id: rm.row_id, customer_name: rm.values?.isim, row_date: date, queued: false, status_code: 404, detail: res.data?.detail });
        toast.error("Bu satır artık yok (silinmiş ya da başka biri değiştirmiş) — defter yenileniyor, lütfen tekrar yazın", { id: "row-gone", duration: 6000 });
        dataCacheRef.current.clear();
        setTimeout(() => { fetchSheetRef.current(); }, 150);
      } else {
        revertCell(r, c, rm.values[col.key]);
        reportTelemetryRef.current("save_failed", { field: col.field, label: col.label, value: payloadVal, row_id: rm.row_id, customer_name: rm.values?.isim, row_date: date, queued: false, status_code: res.status, detail: res.data?.detail });
        toast.error(res.data?.detail || "Kaydedilemedi");
      }
    } catch (e) {
      revertCell(r, c, rm.values[col.key]);
      reportTelemetryRef.current("save_failed", { field: col.field, label: col.label, value: payloadVal, row_id: rm.row_id, customer_name: rm.values?.isim, row_date: date, queued: false, reason: "network" });
      toast.error("Ağ hatası — kaydedilemedi");
    } finally {
      writingRef.current -= 1;
    }
  }, [fetchSheet, revertCell]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- FAZ F5: Secili hucrelere serbest renk uygula ----
  const applyColor = useCallback(async (color) => {
    if (!wbRef.current) return;
    const sel = wbRef.current.getSelection();
    if (!sel || !sel.length) { toast.info("Önce hücre seçin"); return; }
    const byRow = {};
    sel.forEach((rg) => {
      const [r0, r1] = rg.row; const [c0, c1] = rg.column;
      for (let r = r0; r <= r1; r++) {
        if (r === 0) continue;
        const rm = rowsByRRef.current[r];
        if (!rm || !rm.row_id) continue;
        for (let c = c0; c <= c1; c++) {
          const col = columnsRef.current[c];
          if (!col) continue;
          if (!byRow[r]) byRow[r] = { rm, cells: [] };
          byRow[r].cells.push({ c, key: col.key, editable: col.editable });
        }
      }
    });
    const rkeys = Object.keys(byRow);
    if (!rkeys.length) { toast.info("Geçerli hücre seçilmedi"); return; }

    suppressRef.current = true;
    for (const rStr of rkeys) {
      const r = Number(rStr);
      const { rm, cells } = byRow[rStr];
      const colors = {};
      cells.forEach(({ c, key, editable }) => {
        colors[key] = color; // null = rengi kaldir
        const bg = color || (editable ? null : "#f3f4f6");
        try { wbRef.current.setCellFormat(r, c, "bg", bg); } catch { /* noop */ }
      });
      try {
        const res = await axios.patch(`${API}/api/sheet/row/${rm.row_id}/cell-color`,
          { colors, expected_version: rm.version }, { headers, validateStatus: () => true });
        if (res.status === 200) { rm.version = res.data.version; rm.cell_colors = res.data.cell_colors; }
        else if (res.status === 409) { rm.version = res.data?.current_version || rm.version; toast.error("Renk çakışması — tekrar deneyin"); break; }
        else { toast.error(res.data?.detail || "Renk kaydedilemedi"); }
      } catch (e) { toast.error("Ağ hatası (renk)"); }
    }
    setTimeout(() => { suppressRef.current = false; }, 80);
  }, [fetchSheet]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Garson hucresi: NATIVE dropdown kullanilir (applyData'da dataVerification). ----
  // Eski ozel overlay-autocomplete kaldirildi (guvenilir degildi).


  // ---- FortuneSheet op koprusu ----
  const handleOp = useCallback((ops) => {
    if (!Array.isArray(ops)) return;
    ops.forEach((op) => {
      if (!op || !Array.isArray(op.path)) return;
      // Sutun genisligi degisimi -> kisisel olarak localStorage'a kaydet (GS gibi hafiza)
      if (op.path[0] === "config" && op.path[1] === "columnlen") {
        try {
          const key = `ls_colw_${layoutRef.current === "sunset" ? "sunset2" : layoutRef.current}`;
          const cur = JSON.parse(localStorage.getItem(key) || "{}");
          if (op.path.length >= 3 && typeof op.value === "number") cur[op.path[2]] = op.value;
          else if (op.value && typeof op.value === "object") Object.assign(cur, op.value);
          localStorage.setItem(key, JSON.stringify(cur));
        } catch { /* noop */ }
        return;
      }
      if (suppressRef.current) return;
      if (op.path[0] !== "data") return;
      const r = op.path[1];
      const c = op.path[2];
      if (typeof r !== "number" || typeof c !== "number" || r === 0) return;

      let val;
      if (op.path.length === 3) {
        if (op.value === undefined) return;      // patch: hucre kaldirma/reset -> yok say (deger silinmesin)
        val = op.value == null ? "" : (op.value.v ?? op.value.m ?? "");
      } else if (op.path[3] === "v") {
        // FortuneSheet satir-ici editorde (cift tik) commit sonrasi degeri OLMAYAN (undefined)
        // bir "reset" op'u da yolluyor. Bunu YUTMAZSAK bekleyen dogru yazim iptal olup
        // doWrite(undefined) tetikleniyor ve yazilan deger kayboluyordu -> yut.
        if (op.value === undefined) return;
        val = op.value;
      } else {
        return; // m/ct gibi metadata op'larini yok say
      }

      const key = `${r}:${c}`;
      if (pendingRef.current[key]) clearTimeout(pendingRef.current[key]);
      pendingRef.current[key] = setTimeout(() => {
        delete pendingRef.current[key];
        doWrite(r, c, val);
      }, 350);
      // Silme/duzenleme FortuneSheet'in hucre arka planini (bg) da temizliyor.
      // Orijinal rengi (cell_colors/zebra/checkin) hemen geri koy ki renk kaybolmasin.
      setTimeout(() => {
        if (!wbRef.current) return;
        suppressRef.current = true;
        try { wbRef.current.setCellFormat(r, c, "bg", cellOrigBg(r, c)); } catch { /* noop */ }
        setTimeout(() => { suppressRef.current = false; }, 40);
      }, 0);
    });
  }, [doWrite, cellOrigBg]);

  // ---- FAZ F6: WebSocket'ten gelen uzak degisikligi FortuneSheet'e uygula ----
  // Uzak (peer) guncellemeleri rAF ile TOPLU uygulanir + scroll KORUNUR.
  // Neden: her setCellValue/setCellFormat cagrisi FortuneSheet'te tam re-render (setContext) tetikler;
  // es zamanli garson/masa duzenlemelerinde bu "re-render firtinasi" -> takilma; ayrica scroll-sync
  // efekti bayat scrollTop'u geri yazip "kayma" yaratir. rAF'ta tek partide uygulayip scroll'u geri yukluyoruz.
  const _rb = useRef({ vals: new Map(), flashOn: new Set(), raf: null });

  const _flushRemoteBatch = useCallback(() => {
    _rb.current.raf = null;
    // Kullanici SU AN bir hucrede yaziyorsa peer guncellemesini UYGULAMA (editoru kapatip yazilani bozar).
    // Birikmis degerleri koru; yazim bitince tekrar dene.
    if (isEditingCellRef.current()) {
      _rb.current.raf = setTimeout(() => { _rb.current.raf = null; _flushRemoteBatch(); }, 400);
      return;
    }
    const vals = _rb.current.vals; _rb.current.vals = new Map();
    const flashOn = _rb.current.flashOn; _rb.current.flashOn = new Set();
    if (!wbRef.current || (vals.size === 0 && flashOn.size === 0)) return;
    const w = wrapRef.current;
    const vsb = w ? w.querySelector(".luckysheet-scrollbar-y") : null;
    const hsb = w ? w.querySelector(".luckysheet-scrollbar-x") : null;
    const sTop = vsb ? vsb.scrollTop : 0, sLeft = hsb ? hsb.scrollLeft : 0;
    suppressRef.current = true;
    try {
      vals.forEach((v, key) => { const [r, c] = key.split(":").map(Number); wbRef.current.setCellValue(r, c, v == null ? "" : v); });
      flashOn.forEach((key) => { const [r, c] = key.split(":").map(Number); wbRef.current.setCellFormat(r, c, "bg", "#FEF08A"); });
    } catch { /* noop */ }
    // Render flush sonrasi scroll'u (varsa) eski konumuna geri yükle -> peer'da kayma yok
    requestAnimationFrame(() => {
      const w2 = wrapRef.current; if (!w2) return;
      const v2 = w2.querySelector(".luckysheet-scrollbar-y"), h2 = w2.querySelector(".luckysheet-scrollbar-x");
      if (v2 && Math.abs(v2.scrollTop - sTop) > 2) v2.scrollTop = sTop;
      if (h2 && Math.abs(h2.scrollLeft - sLeft) > 2) h2.scrollLeft = sLeft;
    });
    setTimeout(() => { suppressRef.current = false; }, 60);
    // Flash-off (sari vurguyu geri al) — 1200ms sonra tek partide, yine scroll korumalı
    if (flashOn.size) {
      const cells = [...flashOn];
      setTimeout(() => {
        if (!wbRef.current) return;
        const w3 = wrapRef.current;
        const v3 = w3 ? w3.querySelector(".luckysheet-scrollbar-y") : null;
        const h3 = w3 ? w3.querySelector(".luckysheet-scrollbar-x") : null;
        const t3 = v3 ? v3.scrollTop : 0, l3 = h3 ? h3.scrollLeft : 0;
        suppressRef.current = true;
        try { cells.forEach((key) => { const [r, c] = key.split(":").map(Number); wbRef.current.setCellFormat(r, c, "bg", cellOrigBg(r, c)); }); } catch { /* noop */ }
        requestAnimationFrame(() => {
          const w4 = wrapRef.current; if (!w4) return;
          const v4 = w4.querySelector(".luckysheet-scrollbar-y"), h4 = w4.querySelector(".luckysheet-scrollbar-x");
          if (v4 && Math.abs(v4.scrollTop - t3) > 2) v4.scrollTop = t3;
          if (h4 && Math.abs(h4.scrollLeft - l3) > 2) h4.scrollLeft = l3;
        });
        setTimeout(() => { suppressRef.current = false; }, 60);
      }, 1200);
    }
  }, [cellOrigBg]);

  const _queueRemoteCell = useCallback((r, c, v, flash) => {
    _rb.current.vals.set(`${r}:${c}`, v);
    if (flash) _rb.current.flashOn.add(`${r}:${c}`);
    if (!_rb.current.raf) _rb.current.raf = requestAnimationFrame(_flushRemoteBatch);
  }, [_flushRemoteBatch]);

  const applyRemote = useCallback((msg) => {
    const r = rowIdToRRef.current[msg.row_id];
    if (r == null) return;
    const rm = rowsByRRef.current[r];
    if (!rm || !wbRef.current) return;
    // Etiket degisimi (ekstra/aciklama/toplamlar etkilenir) -> kendi echo degilse sessizce tazele
    if (msg.tags_changed) {
      if (msg.updated_by !== myEmailRef.current && !dragRef.current.active && writingRef.current === 0 && !isEditingCellRef.current()) {
        fetchSheetRef.current();
      }
      return;
    }
    // Kendi echo'sunu / bayat guncellemeyi atla
    if (msg.version != null && (rm.version || 1) >= msg.version) return;

    let boatChanged = false;
    try {
      if (msg.fields) {
        // Tekne atamasi (boat_id) computed "gemi" kolonuna yansir (canli cross-user sync)
        if (msg.fields.boat_id !== undefined && msg.fields.boat_id !== rm.boat_id) {
          rm.boat_id = msg.fields.boat_id;
          boatChanged = true;
          const bname = (boatsRef.current.find((b) => b.id === msg.fields.boat_id) || {}).name || "";
          const gemiCol = columnsRef.current.find((c) => c.key === "gemi");
          if (gemiCol) {
            rm.values.gemi = bname;
            _queueRemoteCell(r, gemiCol.index, bname, true);
          }
        }
        Object.entries(msg.fields).forEach(([k, v]) => {
          const col = fieldColRef.current[k];
          if (!col) return;
          rm.values[col.key] = v;
          _queueRemoteCell(r, col.index, v, true);
        });
      }
      // Manuel/override kimlik alanlari -> col.key ile gelir; refetch YOK, aninda uygulanir.
      if (msg.col_fields) {
        Object.entries(msg.col_fields).forEach(([key, v]) => {
          const col = colByKeyRef.current[key];
          if (!col) return;
          rm.values[col.key] = v;
          _queueRemoteCell(r, col.index, v, true);
        });
      }
      if (msg.menu_cells) {
        Object.entries(msg.menu_cells).forEach(([k, v]) => {
          const col = menuColRef.current[k];
          if (!col) return;
          rm.values[col.key] = v;
          _queueRemoteCell(r, col.index, v, true);
        });
        // MENÜ ozet sutununu (computed) kovalardan yeniden hesapla
        try {
          const menuCol = (columnsRef.current || []).find((cc) => cc.key === "menu");
          if (menuCol) {
            const parts = [];
            (columnsRef.current || []).forEach((cc) => {
              if (cc.source !== "menu") return;
              const cv = rm.values[cc.key];
              const nv = cv == null || cv === "" ? 0 : Number(cv);
              if (!nv) return;
              parts.push(`${nv} ${MENU_LABELS[cc.field] || (cc.field || "").toUpperCase()}`);
            });
            const menuText = parts.join(" + ");
            rm.values["menu"] = menuText;
            _queueRemoteCell(r, menuCol.index, menuText, false);
          }
        } catch (e) { /* noop */ }
      }
      if (msg.cell_colors) {
        rm.cell_colors = msg.cell_colors;
        rm.checked_in = !!msg.cell_colors.__checkin; // check-in bayragi -> yesil satir
        suppressRef.current = true;
        columnsRef.current.forEach((col) => {
          try { wbRef.current.setCellFormat(r, col.index, "bg", cellOrigBg(r, col.index)); } catch { /* noop */ }
        });
        setTimeout(() => { suppressRef.current = false; }, 60);
      }
    } catch (e) { /* noop */ }
    rm.version = msg.version != null ? msg.version : rm.version;
    // Check-in / Katılım / tekne değişiminde canlı "gelen" sayacını tazele
    if (msg.cell_colors || boatChanged || (msg.fields && ("katilim" in msg.fields))) {
      setStatsTick((t) => t + 1);
    }
    // Ayrimli gorunumde baskasinin tekne atamasi -> gruplama/toplamlar icin tazele (suruklerken degil)
    if (boatChanged && groupedRef.current && msg.updated_by !== myEmailRef.current && !dragRef.current.active && !isEditingCellRef.current()) {
      toast.info("Tekne ataması güncellendi", { id: "boat-move" });
      fetchSheetRef.current();
    }
  }, [_queueRemoteCell, cellOrigBg]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Canlı "Gelen / Toplam kişi" özeti (tekne başına) ----
  const arrivedStats = useMemo(() => {
    const byBoat = {}; let arrivedTotal = 0;
    const rb = rBoatRef.current || {};
    Object.entries(rowsByRRef.current || {}).forEach(([r, rm]) => {
      if (!rm || !rm.checked_in) return;
      const pax = Number(rm.values?.pax) || 0;
      const kat = rm.values?.katilim;
      const arrived = (kat != null && kat !== "") ? (Number(kat) || 0) : pax;
      let bid = (rm.boat_id != null) ? rm.boat_id : rb[r];
      if (bid === "__cancelled__") return;
      byBoat[bid] = (byBoat[bid] || 0) + arrived;
      arrivedTotal += arrived;
    });
    return { byBoat, arrivedTotal };
  }, [statsTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Yapisal oturum etiketleri: EKSTRA hucresine tiklayinca kutucuk paneli ----
  const saveTags = useCallback(async (rowId, sel) => {
    const tags = {};
    Object.entries(sel).forEach(([k, v]) => {
      const opt = SEATING_OPTS.find((o) => o.key === k);
      if (!opt) return;
      if (opt.counted) { const q = parseInt(v, 10); if (q > 0) tags[k] = q; }
      else if (v) tags[k] = true;
    });
    try {
      const res = await axios.post(`${API}/api/sheet/row/${rowId}/tags`, { tags }, { headers, validateStatus: () => true });
      if (res.status === 200) { toast.success("Etiketler kaydedildi"); dataCacheRef.current.clear(); await fetchSheetRef.current(); }
      else toast.error(res.data?.detail || "Etiketler kaydedilemedi");
    } catch { toast.error("Ağ hatası — etiketler kaydedilemedi"); }
    setTagEditor(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canManage) return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    const onClick = (e) => {
      setTimeout(() => {
        try {
          const ekCol = (columnsRef.current || []).find((c) => c.key === "ekstra");
          if (!ekCol || !wbRef.current) return;
          const sel = wbRef.current.getSelection();
          if (!sel || !sel.length) return;
          const r = sel[0].row?.[0], c = sel[0].column?.[0];
          if (typeof r !== "number" || typeof c !== "number" || c !== ekCol.index || r === 0) return;
          const rm = rowsByRRef.current[r];
          if (!rm || !rm.row_id) return; // yalnizca gercek veri satiri
          const cur = {};
          Object.entries(rm.seating_tags || {}).forEach(([k, v]) => { cur[k] = v; });
          setTagEditor({ row_id: rm.row_id, x: e.clientX, y: e.clientY, sel: cur, pax: Number(rm.values?.pax) || 0 });
        } catch (err) { /* noop */ }
      }, 30);
    };
    wrap.addEventListener("click", onClick);
    return () => wrap.removeEventListener("click", onClick);
  }, [canManage]);


  // ---- FAZ F6: date-scoped WS room, exponential backoff, reconnect resync ----
  // ---- Cevrimdisi yazma kuyrugu: bekleyen editleri gonder (baglanti gelince / periyodik / online olayinda) ----
  useEffect(() => {
    const flush = async () => {
      if (flushingRef.current) return;
      const items = offlineQueue.all();
      if (items.length === 0) return;
      flushingRef.current = true;
      try {
        // Token suresi dolmusssa once yenile ki tum tekrarlar taze token kullansin
        if (isTokenExpiring(getToken())) { await refreshAccessToken(); }
        for (const it of items) {
          try {
            let res = await axios({
              method: it.method, url: it.url, data: it.body,
              headers: authHeaders(), validateStatus: () => true,
            });
            if (res.status === 401) {
              const nt = await refreshAccessToken();
              if (!nt) break;  // yenileme olmadi -> sonraki turda dene
              res = await axios({ method: it.method, url: it.url, data: it.body, headers: authHeaders(), validateStatus: () => true });
            }
            // Versiyonlu edit (menu-cell/cell) eskimisse guncel version ile son-yazan-kazanir olarak tekrar gonder
            if (res.status === 409 && it.body && it.body.expected_version != null && res.data?.current_version != null) {
              res = await axios({ method: it.method, url: it.url,
                data: { ...it.body, expected_version: res.data.current_version },
                headers: authHeaders(), validateStatus: () => true });
            }
            if (isTransientStatus(res.status)) {
              break;  // hala baglanti sorunu -> kuyrukta kalsin, sonra tekrar dene
            }
            offlineQueue.remove(it.id);  // 200 veya kurtarilamaz 4xx -> kuyruktan cikar
          } catch {
            break;  // ag hatasi -> kuyrukta kalsin
          }
        }
      } finally {
        flushingRef.current = false;
        setQueuedCount(offlineQueue.queueSize());
        // Gonderilen editleri sunucu degerleriyle esitle (aktif duzenleme/surukleme yoksa)
        if (writingRef.current === 0 && !dragRef.current.active) {
          dataCacheRef.current.clear();
          try { await fetchSheetRef.current(); } catch { /* noop */ }
        }
      }
    };
    flushQueueRef.current = flush;
    const iv = setInterval(flush, 15000);
    window.addEventListener("online", flush);
    flush();  // sayfa acilisinda varsa bekleyenleri dene
    return () => { clearInterval(iv); window.removeEventListener("online", flush); };
  }, []);

  useEffect(() => {
    if (!getToken()) return;
    const rc = reconnectRef.current;
    rc.closed = false;
    const connect = async () => {
      // Her (yeniden) baglanmada TAZE token kullan; suresi dolmus/dolmak uzereyse once yenile.
      // Boylece 24 saat sonra WS 403 ile olmez; sessizce yeni token ile baglanir.
      let tok = getToken();
      if (isTokenExpiring(tok)) {
        const nt = await refreshAccessToken();
        if (nt) tok = nt;
      }
      if (!tok || rc.closed) return;
      const wsUrl = `${API.replace(/^http/, "ws")}/api/ws/sheet?date=${date}&token=${encodeURIComponent(tok)}`;
      let ws;
      try { ws = new WebSocket(wsUrl); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => {
        setWsConnected(true);
        setWsState("live");
        // Onceki bir kopma varsa: kopma suresini telemetriye yaz (aginda -> gonderim guvenilir)
        if (rc.dropAt) {
          const dur = Date.now() - rc.dropAt;
          rc.dropAt = null;
          if (dur > 1500) reportTelemetryRef.current("connection_lost", { duration_ms: dur, row_date: date });
        }
        flushQueueRef.current && flushQueueRef.current();  // baglanti geldi -> bekleyen yazmalari gonder
        // Backoff sayaci ancak baglanti 10sn STABIL kalirsa sifirlanir; aninda sifirlansa
        // kisa kopma dongusunde (churn) her seferinde 1sn'de baglanip sunucuyu bogar.
        if (rc.stableTimer) clearTimeout(rc.stableTimer);
        rc.stableTimer = setTimeout(() => { rc.attempts = 0; }, 10000);
        // Keepalive ping: iOS/Safari idle WS'i ~30-60sn'de kapatir; 25sn'de bir ping ile canli tut.
        if (rc.pingTimer) clearInterval(rc.pingTimer);
        rc.pingTimer = setInterval(() => {
          try { if (ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" })); } catch { /* noop */ }
        }, 25000);
        // RESYNC yalnizca GERCEK AG KOPMASI (onclose -> reconnect) sonrasi yapilir.
        // Effect yeniden calismasi/remount'ta YAPILMAZ (aksi halde fetchSheet->remount->
        // effect->resync sonsuz refresh dongusune girer; "kaydirinca surekli refresh" buydu).
        if (rc.needResync) {
          rc.needResync = false;
          const ae = document.activeElement;
          const editing = ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" ||
            (() => { const b = wrapRef.current && wrapRef.current.querySelector(".luckysheet-input-box"); if (!b) return false; const z = parseInt(window.getComputedStyle(b).zIndex, 10); return !isNaN(z) && z >= 0; })());
          if (!editing && !dragRef.current.active) {
            resyncRef.current(); // once hafif durum kontrolu; sadece geride isek agir yenileme
          } else {
            rc.pendingResync = true; // duzenleme bitince yakala
          }
        }
      };
      ws.onmessage = (ev) => {
        let msg; try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === "cell_update" || msg.type === "row_update") {
          applyRemote(msg);
        } else if (msg.type === "presence") {
          // Ayni kullanicinin birden fazla oturumu ( or iPad reconnect) tek sayilsin:
          // aksi halde tek kullanici >=2 gorunup saniyelik polling+remount "surekli reload" dongusune girer.
          const raw = Array.isArray(msg.users) ? msg.users : [];
          setActiveUsers([...new Set(raw)]);
          if (msg.event === "leave" && msg.user) setUserFocus((f) => { const n = { ...f }; delete n[msg.user]; return n; });
        } else if (msg.type === "cell_focus") {
          if (msg.user && msg.user !== myEmailRef.current) {
            const col = columnsRef.current[msg.c];
            const label = col ? `${col.label} (${msg.r})` : `H${msg.r}`;
            setUserFocus((f) => ({ ...f, [msg.user]: label }));
          }
        } else if (msg.type === "cell_blur") {
          if (msg.user) setUserFocus((f) => { const n = { ...f }; delete n[msg.user]; return n; });
        } else if (msg.type === "row_new") {
          // Yeni kayit: kendi echo'muz degilse ve aktif duzenleme/surukleme yoksa sessizce tazele.
          const own = msg.updated_by && msg.updated_by === myEmailRef.current;
          if (own) return;
          if (rowNewTimerRef.current) clearTimeout(rowNewTimerRef.current);
          rowNewTimerRef.current = setTimeout(() => {
            if (dragRef.current.active) return;
            if (writingRef.current > 0 || Object.keys(pendingRef.current).length > 0) return;
            const ae = document.activeElement;
            if (ae && (ae.isContentEditable ||
              ((ae.tagName === "INPUT" || ae.tagName === "TEXTAREA") && wrapRef.current && wrapRef.current.contains(ae)))) return;
            fetchSheetRef.current();
          }, 500);
        } else if (msg.type === "reorder") {
          // Satir sirasi degisti -> guncel siralamayi al (kendi echo'muzu email ile ayikla; suruklerken erteleme)
          const own = msg.updated_by && msg.updated_by === myEmailRef.current;
          if (!dragRef.current.active && !own && !isEditingCellRef.current()) {
            toast.info("Satır sırası güncellendi", { id: "reorder" });
            fetchSheetRef.current();
          }
        }
      };
      ws.onclose = () => {
        setWsConnected(false);
        if (rc.stableTimer) { clearTimeout(rc.stableTimer); rc.stableTimer = null; }
        if (rc.pingTimer) { clearInterval(rc.pingTimer); rc.pingTimer = null; }
        if (rc.closed) return;
        setWsState("reconnecting");
        if (!rc.dropAt) rc.dropAt = Date.now(); // kopma anini isaretle (reconnect'te sure telemetriye yazilir)
        rc.needResync = true; // gercek kopma -> yeniden baglaninca bir kez tazele
        rc.attempts = Math.min(rc.attempts + 1, 6);
        // Exponential backoff + jitter: 2s, 4s, 8s ... maks 30s (+0-800ms rastgele)
        const delay = Math.min(2000 * 2 ** (rc.attempts - 1), 30000) + Math.floor(Math.random() * 800);
        rc.timer = setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();
    return () => {
      rc.closed = true;
      if (rc.timer) clearTimeout(rc.timer);
      if (rc.stableTimer) { clearTimeout(rc.stableTimer); rc.stableTimer = null; }
      if (rc.pingTimer) { clearInterval(rc.pingTimer); rc.pingTimer = null; }
      try { wsRef.current && wsRef.current.close(); } catch {}
      setWsConnected(false);
    };
  }, [date, token, applyRemote]);

  // COKLU KULLANICI SANIYELIK CANLI YENILEME: ayni tarih sayfasinda 1'den fazla kullanici varsa
  // her saniye hafif durum ucunu (/fortune-state) yokla; yerel gorunum sunucudan geride kaldiysa
  // (kacan WS deltasi, tekne degisikligi veya yeni kayit) sessizce tam yenile.
  useEffect(() => {
    lastStateIdsRef.current = null; // tarih degisti -> yeni kayit algisini sifirla
    let _tick = 0;
    const iv = setInterval(async () => {
      if (pollBusyRef.current || dragRef.current.active) return;
      if (writingRef.current > 0 || Object.keys(pendingRef.current).length > 0) return;
      // EPOCH kontrolu: urun tanimi (otel tipi vb.) degisince tum acik defterler tazelensin.
      // Tek kullanicida bile ~5sn'de bir hafif durum ucu yoklanir (sadece degisimde refetch).
      const multiUser = activeUsersRef.current.length >= 2;
      _tick++;
      if (!multiUser && (_tick % 5 !== 0)) return;
      // Hucre duzenleme aktifse dokunma (kesintisiz duzenleme korunur)
      const ae = document.activeElement;
      if (ae && (ae.isContentEditable ||
        ((ae.tagName === "INPUT" || ae.tagName === "TEXTAREA") && wrapRef.current && wrapRef.current.contains(ae)))) return;
      pollBusyRef.current = true;
      try {
        const res = await axios.get(`${API}/api/sheet/fortune-state/${date}`, { headers, validateStatus: () => true });
        if (res.status !== 200) return;
        // (0) Global epoch degisti mi? (urun tanimi override) -> sessiz tam yenile
        const ep = res.data.epoch;
        if (ep != null) {
          if (epochRef.current == null) { epochRef.current = ep; }
          else if (ep !== epochRef.current) {
            epochRef.current = ep;
            if (Date.now() - lastPollRefetchRef.current > 1000 && !dragRef.current.active) {
              lastPollRefetchRef.current = Date.now();
              const jitter = 120 + Math.floor(Math.random() * 800);
              setTimeout(() => { if (!dragRef.current.active) fetchSheetRef.current(); }, jitter);
            }
          }
        }
        if (!multiUser) return;   // tek kullanici -> version/id delta'lari WS'e birak (yalniz epoch kontrol edildi)
        if (Date.now() - lastPollRefetchRef.current < 1500) return;
        const serverRows = res.data.rows || [];
        // (1) Yerel gorunumdeki bir satir sunucudan geride mi? (version)
        // WS BAGLIYKEN version deltalari zaten cell_update ile ANINDA gelir (applyRemote rm.version'i gunceller).
        // Bu yuzden WS bagliyken version-only staleness'te AGIR refetch YAPMA -> her uzak duzenlemede
        // remount/titreme ("dur kalk donma") olmaz. Yalnizca WS KOPUKKEN version fallback devreye girer.
        let stale = false;
        if (!wsConnectedRef.current) {
          for (const [id, ver] of serverRows) {
            const r = rowIdToRRef.current[id];
            if (r == null) continue;
            const rm = rowsByRRef.current[r];
            if (!rm) continue;
            if ((ver || 1) > (rm.version || 1)) { stale = true; break; }
          }
        }
        // (2) Tum-gun kayit setinde yeni/silinen id var mi? (baska filtre/kategoriye de dusebilir)
        // NOT: BU istemcinin az once olusturdugu manuel satir (rowIdToRRef'te zaten kayitli)
        // "yeni id" sayilmaz -> aksi halde yeni rez eklerken kendi kaydimiz poll refresh'i
        // tetikleyip yazmayi keserdi.
        const ids = serverRows.map((x) => x[0]);
        const idSet = new Set(ids);
        const prev = lastStateIdsRef.current;
        if (prev) {
          let changed = false;
          for (const id of ids) { if (!prev.has(id) && rowIdToRRef.current[id] == null) { changed = true; break; } }
          if (!changed) { for (const id of prev) { if (!idSet.has(id)) { changed = true; break; } } }
          if (changed) stale = true;
        }
        lastStateIdsRef.current = idSet;
        if (stale && !dragRef.current.active && !isEditingCellRef.current()) {
          lastPollRefetchRef.current = Date.now();
          // ANTI-STAMPEDE: ayni tarihte N kullanici ayni anda agir (~800KB) fetchSheet tetiklerse
          // tek-worker backend event loop'u kilitlenip WS ping'leri gecikir ("baglanti kopuyor").
          // Kucuk rastgele gecikme ile es zamanli yenilemeleri zamana yayip yuku dagitiyoruz.
          const jitter = 120 + Math.floor(Math.random() * 1000);
          // Jitter sonrasi TEKRAR kontrol: kullanici bu arada yazmaya baslamis olabilir -> girisini silme.
          setTimeout(() => {
            if (!dragRef.current.active && !isEditingCellRef.current() &&
                writingRef.current === 0 && Object.keys(pendingRef.current).length === 0) {
              fetchSheetRef.current();
            }
          }, jitter);
        }
      } catch { /* noop */ } finally { pollBusyRef.current = false; }
    }, 1000);
    return () => clearInterval(iv);
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hucre-imleci presence: kendi secimini periyodik olarak yayinla
  const lastFocusRef = useRef("");
  useEffect(() => {
    const iv = setInterval(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== 1 || !wbRef.current) return;
      // Reconnect sirasinda duzenleme yuzunden ertelenen resync'i, artik bosta ise uygula
      const rc = reconnectRef.current;
      if (rc && rc.pendingResync && !dragRef.current.active) {
        const ae = document.activeElement;
        const editing = ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" ||
          (() => { const b = wrapRef.current && wrapRef.current.querySelector(".luckysheet-input-box"); if (!b) return false; const z = parseInt(window.getComputedStyle(b).zIndex, 10); return !isNaN(z) && z >= 0; })());
        if (!editing) { rc.pendingResync = false; resyncRef.current(); return; }
      }
      let sel; try { sel = wbRef.current.getSelection(); } catch { return; }
      if (!sel || !sel.length) return;
      const r = sel[0].row?.[0], c = sel[0].column?.[0];
      if (typeof r !== "number" || typeof c !== "number" || r === 0) return;
      const key = `${r}:${c}`;
      if (key === lastFocusRef.current) return;
      lastFocusRef.current = key;
      try { ws.send(JSON.stringify({ type: "cell_focus", r, c })); } catch { /* noop */ }
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  const changeDate = (newDate) => {
    // Native <input type=date> bazı tarayıcılarda tarih tamamlanmadan/temizlenirken boş veya
    // geçersiz değer yayınlar -> date='' -> new Date('') (Invalid) -> format() çöker (beyaz ekran/hata).
    // Yalnız geçerli YYYY-AA-GG değerlerini kabul et.
    if (!newDate || !/^\d{4}-\d{2}-\d{2}$/.test(newDate) || isNaN(new Date(newDate).getTime())) return;
    if (isGarson) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const d = new Date(newDate); d.setHours(0, 0, 0, 0);
      const diff = Math.round((d - today) / 86400000);
      if (Math.abs(diff) > 2) { toast.error("Garson yalnızca bugün ±2 günü görüntüleyebilir"); return; }
    }
    setDate(newDate); setSearchParams({ date: newDate, category: activeCategory ?? "" });
  };
  const changeCategory = (id) => { setActiveCategory(id); setBoatFilter(null); setTimeFilter(null); setSearchParams({ date, category: id }); };

  // Eslenmemis urunu bir kategoriye bagla
  const mapProduct = async (productName) => {
    const cid = mapChoice[productName];
    if (!cid) { toast.info("Önce kategori seçin"); return; }
    try {
      const res = await axios.post(`${API}/api/category-mappings`,
        { product_name: productName, category_id: Number(cid) }, { headers, validateStatus: () => true });
      if (res.status < 300) { toast.success("Ürün kategoriye eşlendi"); fetchSheet(); }
      else toast.error(res.data?.detail || "Eşlenemedi");
    } catch (e) { toast.error("Ağ hatası"); }
  };

  // ---- Google Sheets tarzi tablo-ici satir siralama ----
  // Sheet geometrisi: DOM'dan olcum (row-header stripi + satir Y konumlari)
  const wrapRef = useRef(null);

  // Kullanici SU AN bir hucre editorunde aktif yaziyor mu? (remount/refetch bunu ezmesin -> "veri siliniyor" fix)
  const isEditingCell = useCallback(() => {
    const ae = document.activeElement;
    const wrap = wrapRef.current;
    if (ae && (ae.isContentEditable ||
      ((ae.tagName === "INPUT" || ae.tagName === "TEXTAREA") && wrap && wrap.contains(ae)))) return true;
    const b = wrap && wrap.querySelector(".luckysheet-input-box");
    if (b) { const z = parseInt(window.getComputedStyle(b).zIndex, 10); if (!isNaN(z) && z >= 0) return true; }
    return false;
  }, []);
  useEffect(() => { isEditingCellRef.current = isEditingCell; }, [isEditingCell]);

  // Telemetri: baglanti kopmasi / kaydedilemeyen giris olaylarini Kullanici Gecmisi'ne yaz (best-effort)
  const reportTelemetry = useCallback((event, extra = {}) => {
    try {
      axios.post(`${API}/api/telemetry/client-event`, { event, ...extra },
        { headers: authHeaders(), validateStatus: () => true }).catch(() => {});
    } catch { /* noop */ }
  }, []);
  const reportTelemetryRef = useRef(reportTelemetry);
  useEffect(() => { reportTelemetryRef.current = reportTelemetry; }, [reportTelemetry]);


  // ---- Scroll koruma (remount'ta "basa sarma"/refresh hissini onler) ----
  const scrollSaveRef = useRef(null);
  const getScrollbars = useCallback(() => {
    const w = wrapRef.current;
    return {
      vsb: w ? w.querySelector(".luckysheet-scrollbar-y") : null,
      hsb: w ? w.querySelector(".luckysheet-scrollbar-x") : null,
    };
  }, []);
  const captureScroll = useCallback(() => {
    const { vsb, hsb } = getScrollbars();
    scrollSaveRef.current = { top: vsb ? vsb.scrollTop : 0, left: hsb ? hsb.scrollLeft : 0 };
  }, [getScrollbars]);
  const getGeo = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap || !wbRef.current) return null;
    const area = wrap.querySelector(".fortune-cell-area");
    const header = wrap.querySelector(".fortune-row-header");
    const vsb = wrap.querySelector(".luckysheet-scrollbar-y");
    if (!area || !header) return null;
    const wrapRect = wrap.getBoundingClientRect();
    const areaRect = area.getBoundingClientRect();
    const headerRect = header.getBoundingClientRect();
    const scrollTop = vsb ? vsb.scrollTop : 0;
    const heights = heightsRef.current || {};
    const h0 = heights[0] || 34;
    // Gercek satir adimi = model yuksekligi + 1px izgara cizgisi. Gercek tarayici
    // zoom'unda (Ctrl +/-) getBoundingClientRect ve satir yukseklikleri ayni CSS-piksel
    // uzayinda kaldigi icin ek olceklemeye gerek yoktur.
    const pitch = (i) => (heights[i] || 19) + 1;
    return {
      wrapTop: wrapRect.top, wrapLeft: wrapRect.left,
      gridTop: areaRect.top, gridWidth: areaRect.width, gridLeft: areaRect.left,
      gridBottom: areaRect.bottom,
      headerLeft: headerRect.left, headerWidth: headerRect.width, headerHeight: headerRect.height, headerTop: headerRect.top,
      scrollTop, h0, heights, pitch,
    };
  }, []);

  // clientY -> satir r-indeksi (r=0 baslik/frozen, r>=1 veri satiri)
  const rowAtClientY = useCallback((clientY) => {
    const g = getGeo();
    if (!g) return null;
    const dataTop = g.gridTop + g.h0;
    if (clientY < dataTop) return 0;
    let y = dataTop - g.scrollTop;
    const n = rowCountRef.current;
    for (let r = 1; r < n; r++) {
      // Gercek satir adimi = (model yuksekligi + 1px izgara cizgisi) * olcek (zoom-guvenli)
      const h = g.pitch(r);
      if (clientY >= y && clientY < y + h) return r;
      y += h;
      if (y > g.gridBottom + 40) break;
    }
    return null;
  }, [getGeo]);

  // Bir satirin client-Y ustu/altini dondur (vurgu + drop cizgisi icin)
  // geo verilirse onu kullanir (frame basina tek getGeo -> reflow thrashing yok).
  const rowClientBounds = useCallback((r, geo) => {
    const g = geo || getGeo();
    if (!g || r < 1) return null;
    const dataTop = g.gridTop + g.h0;
    let y = dataTop - g.scrollTop;
    for (let i = 1; i < r; i++) y += g.pitch(i);
    return { top: y, height: g.pitch(r), g };
  }, [getGeo]);

  // Vurgu barlari + strip konumunu guncelle (rAF ile scroll'a duyarli)
  // Deger degismediyse setState YAPMA (sonsuz re-render'i onler).
  const lastStripRef = useRef("");
  const lastBarsRef = useRef("");
  const setStripBoxGuarded = useCallback((box) => {
    const s = box ? `${box.left}|${box.top}|${box.width}|${box.height}` : "null";
    if (s === lastStripRef.current) return;
    lastStripRef.current = s;
    setStripBox(box);
  }, []);
  // "+ Rez Ekle": DB'de bos satir OLUSTURMAZ — teknenin ilk bos (hayalet) satirina
  // kaydirir, o hucreyi secer ve kisaca vurgular; kullanici direkt yazip ekler.
  const addManualRowToBoat = useCallback((boatId) => {
    if (focusBlankRef.current) focusBlankRef.current(boatId);
    else toast.info("Tekne bölümünün altındaki boş satıra yazarak rezervasyon ekleyin");
  }, []);

  const setRowBarsGuarded = useCallback((bars) => {
    const s = JSON.stringify(bars);
    if (s === lastBarsRef.current) return;
    lastBarsRef.current = s;
    setRowBars(bars);
  }, []);
  const setRowChecksGuarded = useCallback((arr) => {
    const s = JSON.stringify(arr);
    if (s === lastChecksRef.current) return;
    lastChecksRef.current = s;
    setRowChecks(arr);
  }, []);
  const setHeaderBadgesGuarded = useCallback((arr) => {
    const s = JSON.stringify(arr);
    if (s === lastHBRef.current) return;
    lastHBRef.current = s;
    setHeaderBadges(arr);
  }, []);

  const syncOverlay = useCallback(() => {
    const g = getGeo();
    if (!g) { setRowBarsGuarded([]); setStripBoxGuarded(null); return; }
    // strip (row-header) konumu wrapper'a gore
    setStripBoxGuarded({
      left: g.headerLeft - g.wrapLeft,
      top: g.headerTop - g.wrapTop,
      width: g.headerWidth,
      height: g.headerHeight,
    });
    const sel = selRowsRef.current;
    const bars = [];
    const dataTop = g.gridTop + g.h0;
    const gwidth = Math.round(g.gridLeft - g.wrapLeft + g.gridWidth);
    const pushBar = (r, kind) => {
      const b = rowClientBounds(r, g);
      if (!b) return;
      if (b.top + b.height < dataTop || b.top > g.gridBottom) return;
      const clampedTop = Math.max(b.top, dataTop);
      const clampedBottom = Math.min(b.top + b.height, g.gridBottom);
      bars.push({ top: Math.round(clampedTop - g.wrapTop), height: Math.round(Math.max(clampedBottom - clampedTop, 0)), width: gwidth, kind });
    };
    // Arama vurgusu artik hucre-bazli (setCellFormat) yapiliyor; overlay bar YOK.
    // Secili satirlar (mavi)
    if (sel && sel.size > 0) sel.forEach((r) => pushBar(r, "sel"));
    setRowBarsGuarded(bars);
    // Gorunur veri satirlari icin secim kutucugu konumlari (yalnizca "Toplu Seç" modunda)
    const checks = [];
    if (selectModeRef.current) {
      const n = rowCountRef.current;
      for (let r = 1; r < n; r++) {
        const rm = rowsByRRef.current[r];
        if (!rm || !rm.row_id) continue;
        const b = rowClientBounds(r, g);
        if (!b) continue;
        if (b.top + b.height < dataTop || b.top > g.gridBottom) continue; // viewport disi
        const ct = Math.max(b.top, dataTop);
        const cb = Math.min(b.top + b.height, g.gridBottom);
        if (cb - ct < 10) continue;
        checks.push({ r, top: Math.round(ct - g.wrapTop), height: Math.round(cb - ct) });
      }
    }
    setRowChecksGuarded(checks);
    // Tekne basligi rozetleri + hizli "+ Rez Ekle" butonu (İPTALLER haric tum tekne basliklari)
    const badges = [];
    (groupHeadersRef.current || []).forEach((h) => {
      if (!h || h.boat_id === "__cancelled__") return;
      const b = rowClientBounds(h.r, g);
      if (!b) return;
      if (b.top + b.height < dataTop || b.top > g.gridBottom) return;
      badges.push({ r: h.r, top: Math.round(b.top - g.wrapTop), height: Math.round(b.height), cam: h.cam_count || 0, vipcam: h.vipcam_count || 0, vip: h.vip_count || 0, sahne: h.sahne_count || 0, salon: h.salon_count || 0, standart: h.standart_count || 0, boat_id: h.boat_id, name: h.name });
    });
    setHeaderBadgesGuarded(badges);
    // Oturum filtresi: aktifse eslesmeyen veri satirlarini soluklastir (dim overlay)
    const flt = seatFilterRef.current;
    const dbars = [];
    if (flt) {
      const n = rowCountRef.current;
      for (let r = 1; r < n; r++) {
        const rm = rowsByRRef.current[r];
        if (!rm || !rm.row_id) continue;
        const b = rowClientBounds(r, g);
        if (!b) continue;
        if (b.top + b.height < dataTop || b.top > g.gridBottom) continue;
        const ct = Math.max(b.top, dataTop);
        const cb = Math.min(b.top + b.height, g.gridBottom);
        if (cb - ct <= 0) continue;
        dbars.push({ top: Math.round(ct - g.wrapTop), height: Math.round(cb - ct), width: gwidth, match: rm.seat_bucket === flt });
      }
    }
    if (dbars.length || dimBarsRef.current.length) { dimBarsRef.current = dbars; setDimBars(dbars); }
  }, [getGeo, rowClientBounds, setStripBoxGuarded, setRowBarsGuarded, setRowChecksGuarded, setHeaderBadgesGuarded]);

  useEffect(() => { seatFilterRef.current = seatFilter; syncOverlay(); }, [seatFilter, syncOverlay]);

  // ---- Hizli arama (Ctrl+F / "/") — hucre bazli vurgu ----
  const scrollToRow = useCallback((r) => {
    const g = getGeo(); if (!g) return;
    let cum = 0; for (let i = 1; i < r; i++) cum += g.pitch(i);
    const vsb = wrapRef.current?.querySelector(".luckysheet-scrollbar-y");
    if (!vsb) return;
    const viewH = g.gridBottom - (g.gridTop + g.h0);
    vsb.scrollTop = Math.max(0, Math.round(cum - viewH / 2 + g.pitch(r) / 2));
  }, [getGeo]);

  // Arama: eslesen HUCRENIN kolonunu yatayda gorunur kil. Sağdaysa ortaya getirir,
  // soldaki kolonlar 0'a clamp olur (en sola çekilir) -> aranan hızlı bulunur.
  const scrollToColumn = useCallback((c) => {
    if (c == null) return;
    const g = getGeo(); if (!g) return;
    const hsb = wrapRef.current?.querySelector(".luckysheet-scrollbar-x");
    if (!hsb) return;
    const clen = (sheetDataRef.current?.config?.columnlen) || {};
    const colW = (i) => (typeof clen[i] === "number" ? clen[i] : 100);
    let cum = 0; for (let i = 0; i < c; i++) cum += colW(i);
    const viewW = g.gridWidth;
    const target = Math.round(cum - viewW / 2 + colW(c) / 2);
    const max = Math.max(0, hsb.scrollWidth - hsb.clientWidth);
    hsb.scrollLeft = Math.max(0, Math.min(max, target));
  }, [getGeo]);

  // ---- Hayalet satir: teknenin ilk bos satirina kaydir + hucreyi sec + kisa vurgu ----
  const focusBoatBlankRow = useCallback((boatId) => {
    const h = (groupHeadersRef.current || []).find((x) => x.boat_id === boatId);
    if (!h) { toast.info("Tekne bölümünün altındaki boş satıra yazarak rezervasyon ekleyin"); return; }
    const firstBlankR = h.r + (h.count || 0) + 1; // veri satirlarindan hemen sonraki ilk bos satir
    scrollToRow(firstBlankR);
    // NOT: canvas hucre flash'i (setCellFormat) bu fortune-sheet surumunde async
    // "sheet not found"/Immer cakismasi verebiliyor -> kullanmiyoruz. Kaydirma + toast yeterli.
    toast.info("En alttaki boş satıra çift tıklayıp bilgileri yazarak rezervasyonu ekleyin", { duration: 3500 });
  }, [scrollToRow]);
  useEffect(() => { focusBlankRef.current = focusBoatBlankRow; }, [focusBoatBlankRow]);

  const clearHighlight = useCallback(() => {
    if (!wbRef.current) { lastHiliteRef.current = null; allHiliteRef.current = []; return; }
    suppressRef.current = true;
    const cells = allHiliteRef.current.slice();
    if (lastHiliteRef.current) cells.push(lastHiliteRef.current);
    try { cells.forEach(({ r, c }) => wbRef.current.setCellFormat(r, c, "bg", cellOrigBg(r, c))); } catch { /* noop */ }
    lastHiliteRef.current = null; allHiliteRef.current = [];
    setTimeout(() => { suppressRef.current = false; }, 40);
  }, [cellOrigBg]);

  // Tum eslesen hucreleri HAFIF (amber-100) boya, GECERLI eslesmeyi PARLAK (amber-300) yap.
  const paintHighlights = useCallback((matches, cur) => {
    clearHighlight();
    if (!wbRef.current || !matches || !matches.length) return;
    suppressRef.current = true;
    const capped = matches.slice(0, 200); // cok fazla eslesmede jank olmasin
    try {
      capped.forEach(({ r, c }) => wbRef.current.setCellFormat(r, c, "bg", "#FEF9C3"));
      if (cur) wbRef.current.setCellFormat(cur.r, cur.c, "bg", "#FACC15"); // parlak sari (geceli)
    } catch { /* noop */ }
    allHiliteRef.current = capped;
    lastHiliteRef.current = cur ? { r: cur.r, c: cur.c } : null;
    setTimeout(() => { suppressRef.current = false; }, 40);
  }, [clearHighlight]);

  const highlightMatch = useCallback((m) => {
    paintHighlights(searchMatchesRef.current, m);
  }, [paintHighlights]);

  // "Bulunanlara gore suz": eslesmeyen satirlari (ve bos/toplam/eslesmesiz tekne basliklarini)
  // FortuneSheet config.rowhidden ile gizle. Filtre kapaliysa {} doner.
  const computeRowHidden = useCallback(() => {
    if (!searchFilterRef.current) return {};
    const matches = searchMatchesRef.current || [];
    if (!matches.length) return {};
    const cur = sheetDataRef.current; if (!cur) return {};
    const rowCount = cur.row || 0;
    const matchRs = new Set(matches.map((m) => m.r));
    const rToBoat = rBoatRef.current || {};
    const hByR = {}; (groupHeadersRef.current || []).forEach((h) => { hByR[h.r] = h.boat_id; });
    const visibleBoats = new Set();
    matchRs.forEach((r) => { const b = rToBoat[r]; if (b != null) visibleBoats.add(b); });
    const rowhidden = {};
    for (let r = 1; r < rowCount; r++) {
      const isHeader = hByR[r] !== undefined;
      const headerVisible = isHeader && visibleBoats.has(hByR[r]);
      if (!matchRs.has(r) && !headerVisible) rowhidden[r] = 0;
    }
    return rowhidden;
  }, []);

  const refreshFilterView = useCallback(() => {
    const cur = sheetDataRef.current; if (!cur) return;
    const rowhidden = computeRowHidden();
    // Ayni gizli-satir kumesi ise remount ETME (renderKey->runSearch->refresh sonsuz dongusunu kirar)
    const prev = (cur.config && cur.config.rowhidden) || {};
    const pk = Object.keys(prev).sort().join(","); const nk = Object.keys(rowhidden).sort().join(",");
    if (pk === nk) return;
    const sd = { ...cur, config: { ...cur.config, rowhidden } };
    sheetDataRef.current = sd; setSheetData(sd); setRenderKey((k) => k + 1);
  }, [computeRowHidden]);
  useEffect(() => { refreshFilterViewRef.current = refreshFilterView; }, [refreshFilterView]);
  useEffect(() => { searchFilterRef.current = searchFilter; }, [searchFilter]);

  const runSearch = useCallback((q) => {
    setSearchQuery(q);
    const query = (q || "").trim().toLowerCase();
    clearHighlight();
    if (!query) { searchMatchesRef.current = []; searchCurRef.current = null; setSearchInfo({ count: 0, idx: 0 }); return; }
    const cols = columnsRef.current || [];
    const matches = [];
    Object.values(rowsByRRef.current)
      .filter((rm) => rm && rm.r >= 1)
      .sort((a, b) => a.r - b.r)
      .forEach((rm) => {
        // satirda eslesen ILK hucreyi (kolonu) bul
        for (const col of cols) {
          const v = rm.values ? rm.values[col.key] : null;
          if (v != null && String(v).toLowerCase().includes(query)) {
            matches.push({ r: rm.r, c: col.index });
            break;
          }
        }
      });
    searchMatchesRef.current = matches;
    if (matches.length) {
      searchCurRef.current = matches[0];
      setSearchInfo({ count: matches.length, idx: 1 });
      scrollToRow(matches[0].r);
      scrollToColumn(matches[0].c);
      highlightMatch(matches[0]);
    } else { searchCurRef.current = null; setSearchInfo({ count: 0, idx: 0 }); }
    // Filtre aciksa: eslesme kumesi degisti -> gorunumu (debounced) tazele
    if (searchFilterRef.current) {
      if (filterDebRef.current) clearTimeout(filterDebRef.current);
      filterDebRef.current = setTimeout(() => refreshFilterViewRef.current && refreshFilterViewRef.current(), 300);
    }
  }, [scrollToRow, scrollToColumn, clearHighlight, highlightMatch]);

  const gotoMatch = useCallback((dir) => {
    const m = searchMatchesRef.current; if (!m.length) return;
    const cur = searchCurRef.current;
    let idx = cur ? m.findIndex((x) => x.r === cur.r && x.c === cur.c) : -1;
    if (idx < 0) idx = 0;
    idx = (idx + dir + m.length) % m.length;
    searchCurRef.current = m[idx];
    setSearchInfo({ count: m.length, idx: idx + 1 });
    scrollToRow(m[idx].r);
    scrollToColumn(m[idx].c);
    highlightMatch(m[idx]);
  }, [scrollToRow, scrollToColumn, highlightMatch]);

  // Check-in (geldi) toggle — aktif arama eslesmesi ya da secili satir icin
  const toggleCheckin = useCallback(async () => {
    let rowId = null; let curChecked = false;
    const curR = searchCurRef.current;
    if (curR != null && rowsByRRef.current[curR.r]) {
      rowId = rowsByRRef.current[curR.r].row_id; curChecked = !!rowsByRRef.current[curR.r].checked_in;
    } else {
      const r = Array.from(selRowsRef.current)[0];
      if (r != null && rowsByRRef.current[r]) { rowId = rowsByRRef.current[r].row_id; curChecked = !!rowsByRRef.current[r].checked_in; }
    }
    if (!rowId) { toast.info("Önce bir müşteri bulun veya satır seçin"); return; }
    try {
      const res = await axios.patch(`${API}/api/sheet/row/${rowId}/checkin`,
        { checked_in: !curChecked }, { headers, validateStatus: () => true });
      if (res.status === 200) { toast.success(!curChecked ? "Check-in: Geldi ✓" : "Check-in kaldırıldı"); fetchSheet(); }
      else toast.error("İşaretlenemedi");
    } catch (e) { toast.error("Ağ hatası"); }
  }, [fetchSheet]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seçili satır(lar)ı iptal et / iptali geri al
  const runCancel = useCallback(async (cancelled) => {
    const rs = Array.from(selRowsRef.current);
    const ids = rs.map((r) => rowsByRRef.current[r]?.row_id).filter(Boolean);
    if (!ids.length) { toast.info("Önce satır seçin (soldaki numaradan)"); return; }
    let reason = "";
    if (cancelled) {
      reason = window.prompt("İptal nedeni? (Müşteri talebi / No-show / Hata-Mükerrer / Diğer)\nBoş bırakabilirsiniz:", "Müşteri talebi") || "";
    }
    let ok = 0;
    for (const rid of ids) {
      try {
        const res = await axios.patch(`${API}/api/sheet/row/${rid}/cancel`,
          { cancelled, reason }, { headers, validateStatus: () => true });
        if (res.status === 200) ok++;
      } catch (e) { /* ignore */ }
    }
    if (ok) { toast.success(cancelled ? `${ok} rezervasyon iptal edildi` : `${ok} rezervasyon geri alındı`); fetchSheet(); }
    else toast.error("İşlem başarısız");
  }, [fetchSheet]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seçili satır(lar)ı KALICI sil (yalnızca manuel rez; Bókun atlanır)
  const runDelete = useCallback(async () => {
    const rs = Array.from(selRowsRef.current);
    const ids = rs.map((r) => rowsByRRef.current[r]?.row_id).filter(Boolean);
    if (!ids.length) { toast.info("Önce satır seçin (soldaki numaradan)"); return; }
    if (!window.confirm(`${ids.length} satır KALICI olarak silinsin mi?\n\n(Yalnızca MANUEL rezervasyonlar silinir. Bókun rezervasyonları atlanır — onlar için 'İptal' kullanın.)`)) return;
    let ok = 0, skip = 0;
    for (const rid of ids) {
      try {
        const res = await axios.delete(`${API}/api/sheet/row/${rid}`, { headers, validateStatus: () => true });
        if (res.status === 200) ok++; else skip++;
      } catch (e) { skip++; }
    }
    if (ok) toast.success(`${ok} satır silindi${skip ? ` · ${skip} atlandı (Bókun)` : ""}`);
    else toast.error(skip ? "Silinemedi — seçilenler Bókun rezervasyonu. Onlar için 'İptal' kullanın." : "İşlem başarısız");
    dataCacheRef.current.clear(); fetchSheet();
  }, [fetchSheet]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seçili satırı başka tarihe taşı (reschedule)
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [defters, setDefters] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/defters`, { headers }).then(({ data }) => {
      if (alive) setDefters(data.defters || []);
    }).catch(() => { /* yönetici değilse veya hata -> defter taşıma gizli kalır */ });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const runReschedule = useCallback(() => {
    const r = Array.from(selRowsRef.current)[0];
    const meta = r != null ? rowsByRRef.current[r] : null;
    if (!meta?.row_id) { toast.info("Önce tek bir satır seçin"); return; }
    setRescheduleTarget(meta);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const doReschedule = useCallback(async (payload) => {
    const meta = rescheduleTarget;
    const { newDate, categoryId, boatId } = payload || {};
    if (!meta?.row_id || !newDate) return;
    try {
      const body = { new_date: newDate };
      if (categoryId != null) body.category_id = categoryId;
      if (boatId != null) body.boat_id = boatId;
      const res = await axios.patch(`${API}/api/sheet/row/${meta.row_id}/reschedule`,
        body, { headers, validateStatus: () => true });
      if (res.status === 200) {
        setRescheduleTarget(null);
        if (res.data?.unchanged) { toast.info("Değişiklik yok"); return; }
        toast.success(categoryId != null ? "Rezervasyon taşındı (defter/tekne güncellendi)" : `Taşındı: ${res.data.from} → ${res.data.to}`);
        dataCacheRef.current.clear();
        fetchSheet();
      } else toast.error(res.data?.detail || "Taşınamadı");
    } catch (e) { toast.error("Ağ hatası — taşınamadı"); }
  }, [rescheduleTarget, fetchSheet]); // eslint-disable-line react-hooks/exhaustive-deps


  const closeSearch = useCallback(() => {
    setSearchOpen(false); setSearchQuery("");
    clearHighlight();
    searchMatchesRef.current = []; searchCurRef.current = null;
    setSearchInfo({ count: 0, idx: 0 }); setRowBars([]);
    // Filtre aciksa kapat + tum satirlari geri getir
    if (searchFilterRef.current) {
      searchFilterRef.current = false; setSearchFilter(false);
      if (filterDebRef.current) clearTimeout(filterDebRef.current);
      refreshFilterViewRef.current && refreshFilterViewRef.current();
    }
  }, [clearHighlight]);

  // "Süz" ac/kapa
  const toggleSearchFilter = useCallback(() => {
    setSearchFilter((on) => {
      const next = !on;
      searchFilterRef.current = next;
      if (filterDebRef.current) clearTimeout(filterDebRef.current);
      setTimeout(() => refreshFilterViewRef.current && refreshFilterViewRef.current(), 0);
      return next;
    });
  }, []);

  // Veri yenilenince (renderKey) mevcut aramayi tekrar calistir (r'ler degismis olabilir)
  useEffect(() => {
    if (searchQuery && searchQuery.trim()) {
      const t = setTimeout(() => runSearch(searchQuery), 400);
      return () => clearTimeout(t);
    }
  }, [renderKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Klavye: Ctrl/Cmd+F veya "/" -> KENDI aramamizi ac; Esc -> kapat.
  // ÖNEMLI: Ctrl+F'i CAPTURE fazinda yakalayip stopPropagation yapiyoruz ki FortuneSheet'in
  // kendi (ortada acilan buyuk) arama-degistir penceresi TETIKLENMESIN.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if ((e.ctrlKey || e.metaKey) && (e.key === "f" || e.key === "F")) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        setSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      } else if (e.key === "/" && !typing && !searchOpen) {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      } else if (e.key === "Escape" && searchOpen) {
        closeSearch();
      }
    };
    document.addEventListener("keydown", onKey, true); // capture: FortuneSheet'ten ONCE
    return () => document.removeEventListener("keydown", onKey, true);
  }, [searchOpen, closeSearch]);

  // Satir yuksekliklerini cache'le (render sonrasi)
  const cacheHeights = useCallback(() => {
    if (!wbRef.current || !sheetData) return;
    try {
      const n = sheetData.row || 0;
      rowCountRef.current = n;
      const idx = Array.from({ length: Math.min(n, 400) }, (_, i) => i);
      heightsRef.current = wbRef.current.getRowHeight(idx) || {};
    } catch { /* noop */ }
  }, [sheetData]);

  useEffect(() => {
    // yeni veri/remount sonrasi geometri hazir olsun
    const t = setTimeout(() => { cacheHeights(); syncOverlay(); }, 350);
    return () => clearTimeout(t);
  }, [renderKey, cacheHeights, syncOverlay]);

  // Remount sonrasi kaydirma konumunu geri yukle (scrollSaveRef doluysa) -> "refresh" hissi olmaz.
  // Icerik yuksekligi ve FortuneSheet init'i GEC hazir olabilir; bu yuzden ~1.2sn boyunca her
  // frame hedefe yeniden dayatiriz -> hem clamp (kisa icerik) hem kisa "0'a sarma" flash'i yenilir.
  useEffect(() => {
    if (!scrollSaveRef.current) return;
    const s = scrollSaveRef.current; scrollSaveRef.current = null;
    const startT = performance.now();
    let raf = 0;
    const step = () => {
      const { vsb, hsb } = getScrollbars();
      if (vsb) {
        const max = Math.max(0, vsb.scrollHeight - vsb.clientHeight);
        const want = Math.min(s.top, max);
        if (Math.abs(vsb.scrollTop - want) > 2) vsb.scrollTop = want;
        if (hsb && s.left && Math.abs(hsb.scrollLeft - s.left) > 2) hsb.scrollLeft = s.left;
      }
      if (performance.now() - startT < 1200) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [renderKey, getScrollbars]);

  // Grid uzerinde SAG TIK -> "Asagiya bos satir ekle" menusu (Excel gibi liste uzatma)
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const onCtx = (e) => {
      if (!canManage) return;
      e.preventDefault(); e.stopPropagation();
      setCtxMenu({
        x: Math.min(e.clientX, window.innerWidth - 220),
        y: Math.min(e.clientY, window.innerHeight - 80),
      });
    };
    wrap.addEventListener("contextmenu", onCtx, { capture: true });
    return () => wrap.removeEventListener("contextmenu", onCtx, { capture: true });
  }, [canManage]);

  // Secim/suruklerme AKTIFKEN rAF ile overlay'i taze tut. Bosta rAF CALISMAZ
  // (perpetual rAF, canvas scroll akiciligini bozuyordu — ozellikle yukari scroll).
  useEffect(() => {
    if (selRows.size === 0 && !dragRef.current.active) return;
    let raf;
    const loop = () => {
      if (selRowsRef.current.size > 0 || dragRef.current.active) {
        syncOverlay();
        raf = requestAnimationFrame(loop);
      } else {
        raf = null;
      }
    };
    raf = requestAnimationFrame(loop);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [selRows, syncOverlay]);

  useEffect(() => {
    const onResize = () => { cacheHeights(); syncOverlay(); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [cacheHeights, syncOverlay]);

  // "Toplu Seç" modu degisince: ref guncelle, overlay yenile, kapatilinca secimi temizle
  useEffect(() => {
    selectModeRef.current = selectMode;
    if (!selectMode) { setSelRows(new Set()); selRowsRef.current = new Set(); }
    syncOverlay();
  }, [selectMode, syncOverlay]);

  // Scroll'da overlay'i (secim kutucuklari + vurgu barlari) taze tut.
  // rAF ile throttle -> sadece scroll SIRASINDA calisir (bosta CPU yakmaz, jank yapmaz).
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; syncOverlay(); });
    };
    let vsb = null, hsb = null;
    const attach = () => {
      vsb = wrap.querySelector(".luckysheet-scrollbar-y");
      hsb = wrap.querySelector(".luckysheet-scrollbar-x");
      if (vsb) vsb.addEventListener("scroll", onScroll, { passive: true });
      if (hsb) hsb.addEventListener("scroll", onScroll, { passive: true });
      return !!vsb;
    };
    let tries = 0;
    const iv = setInterval(() => { if (attach() || ++tries > 20) clearInterval(iv); }, 150);
    return () => {
      clearInterval(iv);
      if (raf) cancelAnimationFrame(raf);
      if (vsb) vsb.removeEventListener("scroll", onScroll);
      if (hsb) hsb.removeEventListener("scroll", onScroll);
    };
  }, [renderKey, syncOverlay]);

  // WHEEL/TOUCHPAD KAYDIRMA: momentum (inertia) + klavye gezinme (PageUp/Down/Home/End).
  // FortuneSheet'in kendi wheel/key handler'i (a) her event'te setContext ile re-render yapar
  // (jank), (b) minik yatay deltaX'te verticalScrollLock ile dikey kaydirmayi 500ms bloklar
  // ("stuck at bottom"). Cozum: wheel'i CAPTURE fazinda yakala, FortuneSheet'e ulastirma
  // (preventDefault+stopPropagation) ve HEDEF (target) scrollTop'a rAF ile YUMUSAK ilerle
  // -> girdi bitince ease-out ile yavaslar (momentum hissi), steppy mouse wheel de akici olur.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    let raf = null;
    const target = { y: null, x: null };
    const getSb = () => ({
      vsb: wrap.querySelector(".luckysheet-scrollbar-y"),
      hsb: wrap.querySelector(".luckysheet-scrollbar-x"),
    });
    const EASE = 0.22; // 0..1 — yuksek = daha hizli yakalar, dusuk = daha uzun glide
    const animate = () => {
      const { vsb, hsb } = getSb();
      if (!vsb) { raf = null; return; }
      let cont = false;
      if (target.y != null) {
        const cur = vsb.scrollTop, diff = target.y - cur;
        if (Math.abs(diff) < 0.5) { vsb.scrollTop = target.y; target.y = null; }
        else { vsb.scrollTop = cur + diff * EASE; cont = true; }
      }
      if (hsb && target.x != null) {
        const cur = hsb.scrollLeft, diff = target.x - cur;
        if (Math.abs(diff) < 0.5) { hsb.scrollLeft = target.x; target.x = null; }
        else { hsb.scrollLeft = cur + diff * EASE; cont = true; }
      }
      raf = cont ? requestAnimationFrame(animate) : null;
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(animate); };
    const clampY = (vsb, v) => Math.max(0, Math.min(vsb.scrollHeight - vsb.clientHeight, v));
    const addY = (dy) => { const { vsb } = getSb(); if (!vsb) return; const base = (target.y != null ? target.y : vsb.scrollTop); target.y = clampY(vsb, base + dy); kick(); };
    const setY = (y) => { const { vsb } = getSb(); if (!vsb) return; target.y = clampY(vsb, y); kick(); };

    const onWheel = (e) => {
      if (e.ctrlKey) return; // Ctrl+wheel = zoom -> FortuneSheet'e birak
      const { vsb, hsb } = getSb();
      if (!vsb) return;
      let dy = e.deltaY, dx = e.deltaX;
      if (e.deltaMode === 1) { dy *= 16; dx *= 16; }            // satir modu
      else if (e.deltaMode === 2) { dy *= (vsb.clientHeight || 400); dx *= (hsb ? hsb.clientWidth : 400); } // sayfa modu
      if (Math.abs(dy) >= Math.abs(dx)) {
        addY(dy); // hedefe ekle -> rAF yumusak ilerler (momentum)
      } else if (hsb) {
        const maxX = hsb.scrollWidth - hsb.clientWidth;
        const base = (target.x != null ? target.x : hsb.scrollLeft);
        target.x = Math.max(0, Math.min(maxX, base + dx)); kick();
      }
      e.preventDefault();
      e.stopPropagation(); // FortuneSheet'in hatali/agir wheel handler'i tetiklenmesin
    };

    // Klavye ile hizli gezinme (hucre duzenlenmiyorsa): PageUp/Down + Home/End.
    // NOT: Ok yukari/asagi FortuneSheet'in KENDI dogal satir-secimi + follow-scroll'una birakildi
    // (programatik setSelection Immer'da read-only cakismasi veriyordu; native davranis dogru).
    const NAV_KEYS = ["PageDown", "PageUp", "Home", "End"];
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (!NAV_KEYS.includes(e.key)) return;
      const ae = document.activeElement;
      if (ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA")) return; // gercek form girisi (arama kutusu vb.)
      // FortuneSheet'in gizli hucre editoru (#luckysheet-rich-text-editor) DAIMA odaktadir;
      // yalnizca GERCEKTEN hucre duzenlenirken atla -> .luckysheet-input-box zIndex bostayken -1, duzenlerken 19.
      const box = wrap.querySelector(".luckysheet-input-box");
      if (box) { const z = parseInt(window.getComputedStyle(box).zIndex, 10); if (!isNaN(z) && z >= 0) return; }
      const { vsb } = getSb();
      if (!vsb) return;
      const page = Math.max(60, vsb.clientHeight * 0.9);
      if (e.key === "PageDown") addY(page);
      else if (e.key === "PageUp") addY(-page);
      else if (e.key === "Home") setY(0);
      else if (e.key === "End") setY(vsb.scrollHeight);
      e.preventDefault();
      e.stopPropagation();
    };

    // DOKUNMATIK (iPad/telefon) KAYDIRMA: FortuneSheet'in kendi touch-scroll'u iOS'ta
    // istikrarsiz (kaydirinca garip yerlere ziplama). Parmak surukleyisini dogrudan
    // scrollbar scrollTop/scrollLeft'e 1:1 baglayip FortuneSheet'in touch handler'ini baypas ediyoruz.
    const touch = { active: false, x0: 0, y0: 0, top0: 0, left0: 0, moved: false };
    const onTouchStart = (e) => {
      if (e.touches.length !== 1) { touch.active = false; return; } // 2 parmak = zoom/pinch -> dokunma
      const { vsb, hsb } = getSb();
      if (!vsb) { touch.active = false; return; }
      target.y = null; target.x = null; // devam eden momentum'u durdur
      const t = e.touches[0];
      touch.active = true; touch.moved = false;
      touch.x0 = t.clientX; touch.y0 = t.clientY;
      touch.top0 = vsb.scrollTop; touch.left0 = hsb ? hsb.scrollLeft : 0;
    };
    const onTouchMove = (e) => {
      if (!touch.active || e.touches.length !== 1) return;
      const { vsb, hsb } = getSb();
      if (!vsb) return;
      const t = e.touches[0];
      const dx = t.clientX - touch.x0;
      const dy = t.clientY - touch.y0;
      if (!touch.moved && Math.abs(dx) + Math.abs(dy) < 6) return; // kucuk oynama -> dokunus/tap
      touch.moved = true;
      // parmak asagi -> icerik yukari (dogal): scrollTop = top0 - dy
      const maxY = vsb.scrollHeight - vsb.clientHeight;
      vsb.scrollTop = Math.max(0, Math.min(maxY, touch.top0 - dy));
      if (hsb) { const maxX = hsb.scrollWidth - hsb.clientWidth; hsb.scrollLeft = Math.max(0, Math.min(maxX, touch.left0 - dx)); }
      e.preventDefault();
      e.stopPropagation(); // FortuneSheet touch-scroll + tarayici bounce devre disi
    };
    const onTouchEnd = () => { touch.active = false; };

    wrap.addEventListener("wheel", onWheel, { passive: false, capture: true });
    wrap.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
    wrap.addEventListener("touchmove", onTouchMove, { passive: false, capture: true });
    wrap.addEventListener("touchend", onTouchEnd, { passive: true, capture: true });
    wrap.addEventListener("touchcancel", onTouchEnd, { passive: true, capture: true });
    document.addEventListener("keydown", onKey, { capture: true });
    return () => {
      wrap.removeEventListener("wheel", onWheel, { capture: true });
      wrap.removeEventListener("touchstart", onTouchStart, { capture: true });
      wrap.removeEventListener("touchmove", onTouchMove, { capture: true });
      wrap.removeEventListener("touchend", onTouchEnd, { capture: true });
      wrap.removeEventListener("touchcancel", onTouchEnd, { capture: true });
      document.removeEventListener("keydown", onKey, { capture: true });
      if (raf) cancelAnimationFrame(raf);
    };
  }, [renderKey]);

  // Aktif gorunumdeki satirlarin r-sirali listesi (row_id ile)
  const orderedRows = useCallback(() => {
    return Object.values(rowsByRRef.current)
      .filter((rm) => rm && rm.row_id && rm.r >= 1)
      .sort((a, b) => a.r - b.r);
  }, []);

  // Reorder: ANINDA yerel uygula (agi bekleme) + arka planda sessizce kaydet.
  const sendReorder = useCallback((order, keepRowIds) => {
    const cur = sheetDataRef.current;
    if (!cur) return;
    // Sira gercekten degismediyse HICBIR SEY yapma (kazara/minik suruklemeleri yut).
    const curOrder = Object.values(rowsByRRef.current)
      .filter((rm) => rm && rm.row_id && rm.r >= 1)
      .sort((a, b) => a.r - b.r)
      .map((rm) => rm.row_id);
    if (curOrder.length === order.length && curOrder.every((id, i) => id === order[i])) return;
    // row_id -> yeni r: mevcut veri-satiri slotlari korunur (ayrimli gorunumde banner/bos/toplam
    // satirlarinin r'leri degismez; duz gorunumde slotlar zaten 1..N'dir)
    const slots = curOrder
      .map((rid) => rowIdToRRef.current[rid])
      .sort((a, b) => a - b);
    const newR = {};
    order.forEach((rid, i) => { newR[rid] = slots[i]; });
    // celldata'yi yeni r ile yeniden esle (baslik r=0 sabit)
    const oldToNew = {};
    Object.values(rowsByRRef.current).forEach((rm) => {
      if (rm && newR[rm.row_id] != null) oldToNew[rm.r] = newR[rm.row_id];
    });
    const newCell = (cur.celldata || []).map((cell) => {
      if (cell.r === 0) return cell;
      const nr = oldToNew[cell.r];
      return nr != null ? { ...cell, r: nr } : cell;
    });
    // rowsByRRef / rowIdToRRef yeniden kur
    const byR = {}; const byRowId = {};
    Object.values(rowsByRRef.current).forEach((rm) => {
      if (!rm) return;
      const nr = newR[rm.row_id];
      if (nr != null) { rm.r = nr; byR[nr] = rm; byRowId[rm.row_id] = nr; }
      else { byR[rm.r] = rm; byRowId[rm.row_id] = rm.r; }
    });
    rowsByRRef.current = byR;
    rowIdToRRef.current = byRowId;
    // sheetData'yi guncelle + remount (ANINDA gorunum)
    const newSd = { ...cur, celldata: newCell };
    sheetDataRef.current = newSd;
    captureScroll();
    setSheetData(newSd);
    setRenderKey((k) => k + 1);
    // secimi koru (nudge) / temizle (drag)
    if (keepRowIds && keepRowIds.length) {
      const sel = new Set();
      keepRowIds.forEach((rid) => { if (newR[rid] != null) sel.add(newR[rid]); });
      setSelRows(sel); selRowsRef.current = sel;
    } else {
      setSelRows(new Set()); selRowsRef.current = new Set();
    }
    // arka planda sessizce kaydet (sirali). Hata olursa toast + resync.
    reorderQueueRef.current = reorderQueueRef.current.then(async () => {
      const res = await axios.patch(`${API}/api/sheet/reorder`,
        { order, date }, { headers, validateStatus: () => true });
      if (res.status !== 200) {
        toast.error("Sıralama sunucuya kaydedilemedi — yenileniyor");
        fetchSheetRef.current();
      }
    }).catch(() => { fetchSheetRef.current(); });
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitReorder = useCallback(async (movingRs, dropR, dropAfter) => {
    const list = orderedRows();
    if (!list.length) return;
    const movingSet = new Set(movingRs);
    const moving = list.filter((rm) => movingSet.has(rm.r));
    const rest = list.filter((rm) => !movingSet.has(rm.r));
    if (!moving.length) return;
    // Hedef: dropR satirinin listedeki konumu; ustune/altina ekle
    let insertAt;
    if (dropR == null) {
      insertAt = rest.length; // en sona
    } else {
      const idxInRest = rest.findIndex((rm) => rm.r === dropR);
      if (idxInRest === -1) {
        // dropR tasinanlardan biri -> orijinal konuma gore hesapla
        const idxInList = list.findIndex((rm) => rm.r === dropR);
        const before = list.slice(0, idxInList).filter((rm) => !movingSet.has(rm.r)).length;
        insertAt = dropAfter ? before + 1 : before;
      } else {
        insertAt = dropAfter ? idxInRest + 1 : idxInRest;
      }
    }
    const newList = [...rest.slice(0, insertAt), ...moving, ...rest.slice(insertAt)];
    const order = newList.map((rm) => rm.row_id);
    // Ayni sira ise atla
    const same = order.length === list.length && order.every((id, i) => id === list[i].row_id);
    if (same) return;
    await sendReorder(order, null); // surukle-birak sonrasi secimi temizle
  }, [orderedRows, sendReorder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Alt+Yukari/Asagi: secili blogu bir kademe kaydir (secim korunur).
  // Ardisik tuslar ust uste binmesin diye zincirlenir (biri bitmeden digeri baslamaz).
  const nudgeChainRef = useRef(Promise.resolve());
  const doNudge = useCallback(async (dir) => {
    const list = orderedRows();
    if (!list.length) return;
    const sel = selRowsRef.current;
    const selIdx = [];
    list.forEach((rm, i) => { if (sel.has(rm.r)) selIdx.push(i); });
    if (!selIdx.length) return;
    const minI = Math.min(...selIdx);
    const maxI = Math.max(...selIdx);
    if (dir < 0 && minI === 0) return;
    if (dir > 0 && maxI === list.length - 1) return;
    const movingIds = selIdx.map((i) => list[i].row_id);
    const moving = selIdx.map((i) => list[i]);
    const rest = list.filter((_, i) => !selIdx.includes(i));
    let order;
    if (dir < 0) {
      const anchor = list[minI - 1]; // secim disinda ustteki satir
      const ai = rest.findIndex((rm) => rm.row_id === anchor.row_id);
      const nr = [...rest]; nr.splice(ai, 0, ...moving);
      order = nr.map((rm) => rm.row_id);
    } else {
      const anchor = list[maxI + 1]; // secim disinda alttaki satir
      const ai = rest.findIndex((rm) => rm.row_id === anchor.row_id);
      const nr = [...rest]; nr.splice(ai + 1, 0, ...moving);
      order = nr.map((rm) => rm.row_id);
    }
    await sendReorder(order, movingIds); // secili satirlari koru (tekrarli kaydirma)
  }, [orderedRows, sendReorder]);

  const nudgeRows = useCallback((dir) => {
    nudgeChainRef.current = nudgeChainRef.current.then(() => doNudge(dir)).catch(() => {});
    return nudgeChainRef.current;
  }, [doNudge]);

  // Tekne ICINDE sıralama (sürüklemesiz): seçili satır blogunu tekne sınırları içinde taşı.
  // mode: 'up' | 'down' | 'top' | 'bottom'. Ayrımlı gorunumde tekneler arası SIZMAZ.
  const moveSelWithinBoat = useCallback((mode) => {
    const list = orderedRows();
    if (!list.length) return;
    const sel = selRowsRef.current;
    const selRms = list.filter((rm) => sel.has(rm.r));
    if (!selRms.length) return;
    const boat = selRms[0].boat_id;
    const boatIds = list.filter((rm) => rm.boat_id === boat).map((rm) => rm.row_id);
    const selIdSet = new Set(selRms.filter((rm) => rm.boat_id === boat).map((rm) => rm.row_id));
    const moving = boatIds.filter((id) => selIdSet.has(id));
    if (!moving.length) return;
    const idxs = moving.map((id) => boatIds.indexOf(id));
    const minI = Math.min(...idxs);
    const maxI = Math.max(...idxs);
    const rest = boatIds.filter((id) => !selIdSet.has(id));
    let newBoatIds;
    if (mode === "top") {
      newBoatIds = [...moving, ...rest];
    } else if (mode === "bottom") {
      newBoatIds = [...rest, ...moving];
    } else if (mode === "up") {
      if (minI <= 0) return;
      const anchor = boatIds[minI - 1];
      const ai = rest.indexOf(anchor);
      newBoatIds = [...rest]; newBoatIds.splice(ai, 0, ...moving);
    } else { // down
      if (maxI >= boatIds.length - 1) return;
      const anchor = boatIds[maxI + 1];
      const ai = rest.indexOf(anchor);
      newBoatIds = [...rest]; newBoatIds.splice(ai + 1, 0, ...moving);
    }
    // Ayni sira ise atla
    if (newBoatIds.length === boatIds.length && newBoatIds.every((id, i) => id === boatIds[i])) return;
    let q = 0;
    const order = list.map((rm) => (rm.boat_id === boat ? newBoatIds[q++] : rm.row_id));
    sendReorder(order, moving); // secili satirlari koru (tekrarli tikla)
  }, [orderedRows, sendReorder]);
  useEffect(() => { moveSelWithinBoatRef.current = moveSelWithinBoat; }, [moveSelWithinBoat]);

  // Ayrimli gorunum: bir teknenin bolge sinirlarini (banner'dan sonraki tekneye kadar) hesapla
  const boatZoneFor = useCallback((boatId) => {
    const hs = groupHeadersRef.current || [];
    const idx = hs.findIndex((h) => h.boat_id === boatId);
    if (idx === -1) return null;
    const g = getGeo();
    if (!g) return null;
    const startR = hs[idx].r;
    const endR = Math.max((idx + 1 < hs.length ? hs[idx + 1].r : rowCountRef.current) - 1, startR);
    const b1 = rowClientBounds(startR);
    const b2 = rowClientBounds(endR);
    if (!b1 || !b2) return null;
    const dataTop = g.gridTop + g.h0;
    const top = Math.max(b1.top, dataTop);
    const bottom = Math.min(b2.top + b2.height, g.gridBottom);
    if (bottom - top < 8) return null;
    const width = Math.round(g.gridLeft - g.wrapLeft + g.gridWidth);
    return { top: Math.round(top - g.wrapTop), height: Math.round(bottom - top), width, name: hs[idx].name, boat_id: boatId };
  }, [getGeo, rowClientBounds]);

  // Birakma hedefini (drop cizgisi + dropIndexRef) guncelle. Otomatik-kaydirma sirasinda da cagrilir.
  const updateDropTarget = useCallback((clientY) => {
    const g = getGeo();
    if (!g) return;
    const tr = rowAtClientY(clientY);
    // Ayrimli gorunum: ayni tekne icinde SIRALAMA (mavi cizgi), farkli tekneye ATAMA (yesil bolge)
    if (groupedRef.current) {
      let targetBoat = tr != null && tr >= 1 ? rBoatRef.current[tr] : null;
      // İPTALLER bolumune atama yapilamaz -> gecersiz hedef
      if (targetBoat === "__cancelled__") { targetBoat = null; boatZoneRef.current = null; setBoatZone(null); setDropLineY(null); dropIndexRef.current = null; return; }
      const sameBoat = targetBoat != null && dragBoatRef.current != null && targetBoat === dragBoatRef.current;
      if (sameBoat) {
        boatZoneRef.current = null;
        setBoatZone(null);
        const isData = (rr) => rowsByRRef.current[rr]?.row_id != null;
        let dropR = null; let after = true;
        if (isData(tr)) {
          const b = rowClientBounds(tr);
          if (b) {
            dropR = tr; after = clientY > b.top + b.height / 2;
            dropIndexRef.current = { dropR, after, reorder: true };
            setDropLineY((after ? b.top + b.height : b.top) - g.wrapTop);
            return;
          }
        } else {
          // banner/bos/toplam satiri: bolum icindeki en yakin veri satirina hizala
          let up = tr;
          while (up >= 1 && rBoatRef.current[up] === targetBoat && !isData(up)) up -= 1;
          if (isData(up) && rBoatRef.current[up] === targetBoat) { dropR = up; after = true; }
          else {
            let down = tr + 1;
            const n = rowCountRef.current;
            while (down < n && rBoatRef.current[down] === targetBoat && !isData(down)) down += 1;
            if (isData(down) && rBoatRef.current[down] === targetBoat) { dropR = down; after = false; }
          }
          if (dropR != null) {
            const b = rowClientBounds(dropR);
            if (b) {
              dropIndexRef.current = { dropR, after, reorder: true };
              setDropLineY((after ? b.top + b.height : b.top) - g.wrapTop);
              return;
            }
          }
        }
        dropIndexRef.current = null;
        setDropLineY(null);
        return;
      }
      // Farkli tekne: HEDEF TEKNE BOLGESINI yesil vurgula
      const zone = targetBoat != null ? boatZoneFor(targetBoat) : null;
      const prev = boatZoneRef.current;
      boatZoneRef.current = zone;
      if (JSON.stringify(prev) !== JSON.stringify(zone)) setBoatZone(zone);
      setDropLineY(null);
      dropIndexRef.current = null;
      return;
    }
    if (tr == null || tr < 1) {
      // Imlec en alt satirin altinda -> son satirin ALTINA birak
      const rows = Object.values(rowsByRRef.current).filter((rm) => rm && rm.r >= 1);
      if (!rows.length) { setDropLineY(null); dropIndexRef.current = null; return; }
      if (clientY > g.gridTop + g.h0) {
        const lastR = Math.max(...rows.map((rm) => rm.r));
        const b = rowClientBounds(lastR);
        if (b) { dropIndexRef.current = { dropR: lastR, after: true }; setDropLineY((b.top + b.height) - g.wrapTop); }
      }
      return;
    }
    const b = rowClientBounds(tr);
    if (!b) { setDropLineY(null); return; }
    const after = clientY > b.top + b.height / 2;
    dropIndexRef.current = { dropR: tr, after };
    setDropLineY((after ? b.top + b.height : b.top) - g.wrapTop);
  }, [getGeo, rowAtClientY, rowClientBounds, boatZoneFor]);

  // Ivmeli otomatik-kaydirma: surukleme sirasinda imlec kenardaysa (fare dursa bile) akici kaydirir.
  const runAutoScroll = useCallback(() => {
    if (!dragRef.current.active) { autoScrollRafRef.current = null; return; }
    const g = getGeo();
    const vsb = wrapRef.current?.querySelector(".luckysheet-scrollbar-y");
    if (g && vsb) {
      const y = dragCursorYRef.current;
      const topEdge = g.gridTop + g.h0;   // frozen baslik alti
      const EDGE = 64;                     // kenar hassasiyet bolgesi (px)
      const MAX = 26;                      // max hiz (px/frame)
      let v = 0;
      if (y < topEdge + EDGE) {
        const t = Math.min(1, Math.max(0, (topEdge + EDGE - y) / EDGE));
        v = -MAX * t * t;                  // ivmeli (kenara yaklastikca hizlanir)
      } else if (y > g.gridBottom - EDGE) {
        const t = Math.min(1, Math.max(0, (y - (g.gridBottom - EDGE)) / EDGE));
        v = MAX * t * t;
      }
      if (v !== 0) {
        const before = vsb.scrollTop;
        vsb.scrollTop = Math.max(0, before + v);
        if (vsb.scrollTop !== before) updateDropTarget(y);
      }
    }
    autoScrollRafRef.current = requestAnimationFrame(runAutoScroll);
  }, [getGeo, updateDropTarget]);

  // Kullanici pes pese tasima yapabilir; durunca TEK sefer reconcile (bosluk/toplam/sirayi
  // sunucudan duzeltir, scroll korunur). Boylece her tasimada ikinci bir remount olmaz.
  const reconcileTimerRef = useRef(null);
  const scheduleReconcile = useCallback(() => {
    if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    reconcileTimerRef.current = setTimeout(() => {
      reconcileTimerRef.current = null;
      captureScroll();
      fetchSheetRef.current();
    }, 1300);
  }, [captureScroll]);

  // Optimistik YEREL tekne tasima (ayrimli/grouped gorunum) — ANINDA, agi beklemez.
  // Satiri hedef teknenin ilk BOS slotuna tasir, kaynak slotu bosaltir (bosluk reconcile'da
  // temizlenir). Uygun degilse false doner -> arayan ag yoluna (fetchSheet) duser.
  const optimisticMoveLocal = useCallback((ids, targetId) => {
    try {
      const cur = sheetDataRef.current;
      if (!cur || !groupedRef.current) return false;
      const cols = columnsRef.current || [];
      const gemiCol = cols.find((c) => c.key === "gemi");
      if (!gemiCol) return false;
      const targetBoat = (boatsRef.current || []).find((b) => b.id === targetId);
      const targetH = (groupHeadersRef.current || []).find((h) => h.boat_id === targetId);
      if (!targetBoat || !targetH) return false;
      const rms = ids.map((id) => rowsByRRef.current[rowIdToRRef.current[id]]).filter(Boolean);
      if (!rms.length || rms.length !== ids.length) return false;
      if (rms.every((rm) => rm.boat_id === targetId)) return true; // zaten hedefte
      const cells = cur.celldata.map((x) => ({ ...x }));
      // TOPLAM satirlari (asla ustune yazma)
      const totalRs = new Set();
      cells.forEach((c) => {
        if (c.c === 0 && typeof c.v === "string" && c.v.indexOf("TOPLAM ·") === 0) totalRs.add(c.r);
      });
      // hedefteki bos, kullanilabilir slotlar (banner'dan sonra, TOPLAM haric)
      const emptySlots = [];
      for (let r = targetH.r + 1; r < rowCountRef.current; r++) {
        if (rBoatRef.current[r] !== targetId) continue;   // hedef teknenin bolgesi disi
        if (rowsByRRef.current[r]) continue;              // dolu veri satiri
        if (totalRs.has(r)) continue;                     // TOPLAM satiri
        emptySlots.push(r);
        if (emptySlots.length >= rms.length) break;
      }
      if (emptySlots.length < rms.length) return false;   // yeterli bos slot yok -> ag yolu
      const cellMap = new Map();
      cells.forEach((c) => cellMap.set(`${c.r}_${c.c}`, c));
      const colCount = cur.column || 26;
      const dv = { ...(cur.dataVerification || {}) };
      const dvCols = cols.filter((c) => ["gemi", "garson", "acenta", "sekli"].includes(c.key)).map((c) => c.index);
      rms.forEach((rm, i) => {
        const rA = rm.r; const rNew = emptySlots[i];
        for (let c = 0; c < colCount; c++) {
          const src = cellMap.get(`${rA}_${c}`);
          const dst = cellMap.get(`${rNew}_${c}`);
          if (dst) { dst.v = ""; dst.m = ""; }
          if (src) { cellMap.set(`${rNew}_${c}`, { ...src, r: rNew }); src.v = ""; src.m = ""; }
        }
        // Gemi hucresi -> hedef tekne adi
        const gk = `${rNew}_${gemiCol.index}`;
        const g = cellMap.get(gk) || { r: rNew, c: gemiCol.index };
        g.v = targetBoat.name; g.m = targetBoat.name; cellMap.set(gk, g);
        // Dropdown dogrulamalari yeni slota tasi (gemi/garson/acenta/sekli)
        dvCols.forEach((idx) => {
          const from = `${rA}_${idx}`; const to = `${rNew}_${idx}`;
          if (dv[from]) { dv[to] = dv[from]; delete dv[from]; }
        });
        delete rowsByRRef.current[rA];
        rm.r = rNew; rm.boat_id = targetId;
        rowsByRRef.current[rNew] = rm;
        rowIdToRRef.current[rm.row_id] = rNew;
      });
      const newSd = { ...cur, celldata: Array.from(cellMap.values()), dataVerification: dv };
      sheetDataRef.current = newSd;
      captureScroll();
      setSheetData(newSd);
      setRenderKey((k) => k + 1);
      return true;
    } catch (e) {
      console.error("optimisticMoveLocal fail", e);
      return false;
    }
  }, [captureScroll]);

  // Secili (veya verilen) satirlari bir tekneye ata: ANINDA yerel tasi + arka planda kaydet.
  const assignBoat = useCallback(async (boatId, rowIdsArg) => {
    const ids = rowIdsArg && rowIdsArg.length
      ? rowIdsArg
      : Array.from(selRowsRef.current).map((r) => rowsByRRef.current[r]?.row_id).filter(Boolean);
    if (!ids.length) { toast.info("Önce satır seçin"); return; }
    const bt = boats.find((b) => b.id === boatId);
    const applied = optimisticMoveLocal(ids, boatId);
    setSelRows(new Set()); selRowsRef.current = new Set();
    setBoatMenuOpen(false);
    if (applied) toast.success(`${ids.length} rezervasyon → ${bt?.name || "tekne"}`);
    try {
      const res = await axios.post(`${API}/api/sheet/bulk-move`,
        { row_ids: ids, target_boat_id: boatId }, { headers, validateStatus: () => true });
      if (res.status === 200) {
        if (applied) {
          scheduleReconcile(); // bosluk/toplam/sirayi sessizce duzelt (scroll korunur)
        } else {
          toast.success(`${res.data?.moved ?? ids.length} rezervasyon → ${bt?.name || "tekne"}`);
          captureScroll(); fetchSheet();
        }
      } else {
        toast.error(res.data?.detail || "Tekneye atama başarısız");
        captureScroll(); fetchSheet();
      }
    } catch (e) {
      toast.error("Ağ hatası — atama başarısız");
      captureScroll(); fetchSheet();
    }
  }, [boats, fetchSheet, optimisticMoveLocal, scheduleReconcile, captureScroll]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { assignBoatRef.current = assignBoat; }, [assignBoat]);

  // Row-header stripinde mousedown: secim + surukle baslat.
  // forceToggle=true (kutucuk tiklamasi) -> Ctrl gibi ekle/cikar (coklu secim korunur).
  const onStripMouseDown = useCallback((e, forceToggle = false) => {
    // Remount sonrasi cache henuz olusmadiysa satir yuksekliklerini ANINDA cek
    // (yanlis satir secimini/tasimasini onler).
    if (!heightsRef.current || Object.keys(heightsRef.current).length === 0) cacheHeights();
    const r = rowAtClientY(e.clientY);
    if (r == null || r < 1) return;
    e.preventDefault();
    const rm = rowsByRRef.current[r];
    if (!rm || !rm.row_id) return;

    const ctrl = forceToggle || e.ctrlKey || e.metaKey;
    const shift = e.shiftKey;
    let nextSel = new Set(selRowsRef.current);

    if (shift && anchorRef.current != null) {
      const a = Math.min(anchorRef.current, r), b = Math.max(anchorRef.current, r);
      nextSel = new Set();
      for (let i = a; i <= b; i++) if (rowsByRRef.current[i]?.row_id) nextSel.add(i);
    } else if (ctrl) {
      if (nextSel.has(r)) nextSel.delete(r); else nextSel.add(r);
      anchorRef.current = r;
    } else {
      if (!nextSel.has(r)) { nextSel = new Set([r]); }
      anchorRef.current = r;
    }
    setSelRows(nextSel);
    selRowsRef.current = nextSel;

    // Surukleme adayi: secilen satir(lar)dan tut
    const movingRs = Array.from(nextSel).sort((a, b) => a - b);
    const movingBoats = new Set(movingRs.map((rr) => rowsByRRef.current[rr]?.boat_id));
    dragBoatRef.current = movingBoats.size === 1 ? Array.from(movingBoats)[0] : null;
    dragRef.current = { active: false, startY: e.clientY, moving: movingRs, armed: true };

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d.armed) return;
      // Kazara suruklemeyi onlemek icin daha buyuk esik (12px)
      if (!d.active && Math.abs(ev.clientY - d.startY) < 12) return;
      const firstStart = !d.active;
      d.active = true;
      dragCursorYRef.current = ev.clientY;
      if (lastHoverRRef.current != null) { lastHoverRRef.current = null; setHoverBar(null); }
      // birakma hedefi (ayrimli: tekne bolgesi vurgusu / normal: drop cizgisi)
      updateDropTarget(ev.clientY);
      // Tekne sekmesi uzerine mi? (surukleyerek atama)
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const boatEl = el && el.closest ? el.closest("[data-boat-assign]") : null;
      const bid = boatEl ? parseInt(boatEl.getAttribute("data-boat-assign"), 10) : null;
      if (bid !== boatDropRef.current) { boatDropRef.current = bid; setBoatDropId(bid); }
      // Surukleme hayaleti (imlec yaninda) + hedef tekne rozeti
      const firstRm = rowsByRRef.current[d.moving[0]];
      const lbl = firstRm ? `${firstRm.values?.saat ? firstRm.values.saat + " " : ""}${firstRm.values?.otel_adi || firstRm.values?.isim || "(satır)"}` : "";
      const tName = bid != null
        ? (boatsRef.current.find((b) => b.id === bid)?.name || null)
        : (groupedRef.current ? (boatZoneRef.current?.name || null) : null);
      setDragGhost({ x: ev.clientX, y: ev.clientY, count: d.moving.length, label: lbl, target: tName });
      // ivmeli otomatik-kaydirma dongusunu baslat (bir kez)
      if (firstStart && !autoScrollRafRef.current) {
        autoScrollRafRef.current = requestAnimationFrame(runAutoScroll);
      }
    };
    const onUp = (ev) => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      const d = dragRef.current;
      dragRef.current = { active: false, startY: 0, moving: [] };
      if (autoScrollRafRef.current) { cancelAnimationFrame(autoScrollRafRef.current); autoScrollRafRef.current = null; }
      setDropLineY(null);
      setDragGhost(null);
      const zone = boatZoneRef.current;
      boatZoneRef.current = null; setBoatZone(null);
      const boatTarget = boatDropRef.current;
      boatDropRef.current = null; setBoatDropId(null);
      if (d.active && boatTarget != null) {
        // Tekne sekmesine birakildi -> o tekneye ata
        const ids = d.moving.map((r) => rowsByRRef.current[r]?.row_id).filter(Boolean);
        assignBoat(boatTarget, ids);
      } else if (d.active && groupedRef.current) {
        if (dropIndexRef.current && dropIndexRef.current.reorder) {
          // Ayni tekne icinde SIRALAMA
          const { dropR, after } = dropIndexRef.current;
          dropIndexRef.current = null;
          commitReorder(d.moving, dropR, after);
        } else {
          // Farkli teknenin bolgesine ATAMA
          const rDrop = rowAtClientY(dragCursorYRef.current || ev.clientY);
          const targetBoat = zone != null ? zone.boat_id : (rDrop != null ? rBoatRef.current[rDrop] : null);
          const movingRms = d.moving.map((r) => rowsByRRef.current[r]).filter(Boolean);
          const ids = movingRms.map((rm) => rm.row_id).filter(Boolean);
          // Zaten ayni teknedeyse no-op (gereksiz yazma yok)
          const allSame = targetBoat != null && movingRms.length && movingRms.every((rm) => rm.boat_id === targetBoat);
          if (targetBoat != null && ids.length && !allSame) assignBoat(targetBoat, ids);
        }
        dragBoatRef.current = null;
      } else if (d.active && dropIndexRef.current) {
        const { dropR, after } = dropIndexRef.current;
        dropIndexRef.current = null;
        commitReorder(d.moving, dropR, after);
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [rowAtClientY, commitReorder, cacheHeights, updateDropTarget, runAutoScroll, assignBoat]);

  // Klavye: Alt+Yukari/Asagi ile secili satirlari bir kademe kaydir
  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
      if (selRowsRef.current.size === 0) return;
      e.preventDefault();
      if (groupedRef.current) {
        // Ayrimli gorunum: tekne ICINDE sirala (tekneler arasi sizmaz)
        moveSelWithinBoatRef.current && moveSelWithinBoatRef.current(e.key === "ArrowUp" ? "up" : "down");
      } else {
        nudgeRows(e.key === "ArrowUp" ? -1 : 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nudgeRows]);

  // Strip uzerinde gezinince: tutma sapi (grip) + satir vurgusu + etiket/addon balonu
  const onStripHover = useCallback((e) => {
    if (dragRef.current.active) { setTagPopover(null); setHoverBar(null); lastHoverRRef.current = null; return; }
    const r = rowAtClientY(e.clientY);
    const rm = r != null && r >= 1 ? rowsByRRef.current[r] : null;
    // Grip gostergesi sadece gercek veri satirlarinda
    if (rm && rm.row_id) {
      if (lastHoverRRef.current !== r) {
        lastHoverRRef.current = r;
        const b = rowClientBounds(r);
        if (b && b.g) {
          const g = b.g;
          const dataTop = g.gridTop + g.h0;
          const top = Math.max(b.top, dataTop);
          const bottom = Math.min(b.top + b.height, g.gridBottom);
          if (bottom > top) {
            setHoverBar({ top: Math.round(top - g.wrapTop), height: Math.round(bottom - top),
              width: Math.round(g.gridLeft - g.wrapLeft + g.gridWidth), r });
          } else setHoverBar(null);
        } else setHoverBar(null);
      }
    } else if (lastHoverRRef.current != null) {
      lastHoverRRef.current = null; setHoverBar(null);
    }
    if (rm && (((rm.tags || []).length) || ((rm.extras || []).length))) {
      setTagPopover({ x: e.clientX, y: e.clientY, tags: rm.tags || [], extras: rm.extras || [] });
    } else {
      setTagPopover(null);
    }
  }, [rowAtClientY, rowClientBounds]);

  const total = sheetData ? Object.keys(rowsByRRef.current).length : 0;

  return (
    <div className="flex flex-col h-full" data-testid="live-sheet-page" style={{ minWidth: 0 }}>
      <RescheduleModal
        open={!!rescheduleTarget}
        onClose={() => setRescheduleTarget(null)}
        onConfirm={doReschedule}
        currentDate={date}
        defters={defters}
        currentCategoryId={typeof activeCategory === "number" ? activeCategory : null}
        currentBoatId={rescheduleTarget ? (rescheduleTarget.boat_id ?? null) : null}
        title={rescheduleTarget ? (rescheduleTarget.values?.kod || rescheduleTarget.values?.otel || "") : ""}
      />
      {invoiceBookingId && (
        <InvoiceDialog bookingId={invoiceBookingId} open={!!invoiceBookingId}
          onOpenChange={(o) => { if (!o) setInvoiceBookingId(null); }} />
      )}
      {/* Ust bar */}
      <div className={`flex items-center border-b bg-white flex-nowrap overflow-x-auto md:flex-wrap md:overflow-visible [&>*]:shrink-0 ${compact ? "gap-x-2 gap-y-0.5 px-2 py-0.5" : "gap-x-3 gap-y-1 px-2 md:px-4 py-1"}`}>
        <div className="flex items-center gap-1">
          <button data-testid="ls-prev-day" onClick={() => changeDate(format(subDays(new Date(date), 1), "yyyy-MM-dd"))}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"><ChevronLeft size={18} /></button>
          <input data-testid="ls-date-input" type="date" value={date} onChange={(e) => changeDate(e.target.value)}
            min={isGarson ? format(subDays(new Date(), 2), "yyyy-MM-dd") : undefined}
            max={isGarson ? format(addDays(new Date(), 2), "yyyy-MM-dd") : undefined}
            className="px-2.5 py-1 border rounded-lg text-sm font-medium" />
          <button data-testid="ls-next-day" onClick={() => changeDate(format(addDays(new Date(date), 1), "yyyy-MM-dd"))}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"><ChevronRight size={18} /></button>
          <button data-testid="ls-today" onClick={() => changeDate(format(new Date(), "yyyy-MM-dd"))}
            className="px-2.5 py-1 text-xs font-medium rounded-lg border hover:bg-gray-100 ml-1">Bugün</button>
        </div>

        <div className="text-sm text-gray-500 hidden lg:block">
          {format(new Date(date), "d MMMM yyyy, EEEE", { locale: tr })}
        </div>

        {/* Kategori sekmeleri (Operasyon sayfasi gibi) */}
        <div className="flex items-center gap-1 md:ml-auto flex-wrap" data-testid="ls-category-tabs">
          {categories.map((cat) => {
            const active = activeCategory === cat.id;
            return (
              <button key={cat.id} data-testid={`ls-cat-${cat.slug}`} onClick={() => changeCategory(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${active ? "text-white shadow" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                style={active ? { backgroundColor: cat.color, borderColor: cat.color } : { borderColor: "#e5e7eb" }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: active ? "#fff" : cat.color }} />
                {cat.name} <span className="opacity-70">({cat.count})</span>
              </button>
            );
          })}
          {unmappedCount > 0 && (
            <button data-testid="ls-cat-unmapped" onClick={() => changeCategory("unmapped")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${activeCategory === "unmapped" ? "bg-amber-500 text-white border-amber-500 shadow" : "bg-white text-amber-600 border-amber-300 hover:bg-amber-50"}`}>
              ⚠ Eşlenmemiş <span className="opacity-70">({unmappedCount})</span>
            </button>
          )}
        </div>

        {/* Kompakt modda (mobil yatay) araç çubuğunu aç/kapat + mini sayaç */}
        {compact && (
          <div className="flex items-center gap-2 ml-auto">
            <span data-testid="ls-compact-total" className="text-[11px] font-semibold text-gray-600 whitespace-nowrap">{total} kayıt · {totalPax} kişi</span>
            <button data-testid="ls-toolbar-toggle" onClick={() => setToolbarOpen((v) => !v)}
              title={toolbarOpen ? "Araç çubuğunu gizle (daha fazla rez göster)" : "Araçları göster"}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border transition-colors ${toolbarOpen ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-600 border-blue-300 hover:bg-blue-50"}`}>
              <i className={`fa-solid fa-sliders ${toolbarOpen ? "" : ""}`} /> Araçlar
              <i className={`fa-solid ${toolbarOpen ? "fa-chevron-up" : "fa-chevron-down"} text-[9px]`} />
            </button>
          </div>
        )}

        {showTools && (<>
        {/* FAZ F5: renk paleti (yalnızca Manager/Admin) */}
        {canManage && (
        <div className="flex items-center gap-1 border-l pl-2" data-testid="ls-color-palette">
          {COLORS.map((col) => (
            <button key={col.c} data-testid={`ls-color-${col.t}`} title={col.t} onClick={() => applyColor(col.c)}
              className="w-5 h-5 rounded border border-gray-300 hover:scale-110 transition-transform"
              style={{ backgroundColor: col.c }} />
          ))}
          <button data-testid="ls-color-clear" title="Rengi kaldır" onClick={() => applyColor(null)}
            className="w-5 h-5 rounded border border-gray-300 hover:scale-110 transition-transform flex items-center justify-center text-gray-500 text-xs">✕</button>
        </div>
        )}

        <button data-testid="ls-search-open" onClick={() => { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 50); }} title="Ara (Ctrl+F veya /)"
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-semibold text-gray-600 hover:bg-gray-100">
          <Search size={14} /> Ara
        </button>
        <button data-testid="ls-refresh" onClick={fetchSheet} disabled={loading}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600">
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
        </button>
        <span data-testid="ls-total" className="text-xs text-gray-500">{total} kayıt · <span className="font-semibold text-gray-700">{totalPax}</span> kişi{arrivedStats.arrivedTotal > 0 && (<> · <span data-testid="ls-arrived-total" className="font-semibold text-emerald-600" title="Gelen (check-in yapılmış) toplam kişi / defterdeki toplam kişi">✓ {arrivedStats.arrivedTotal}/{totalPax} geldi</span></>)}{transferPax > 0 && (<> · <span data-testid="ls-transfer-pax" className="font-semibold text-blue-600" title="Bu defterde o günkü toplam transferli müşteri (kişi)">{transferPax} transferli</span></>)}{transferPaxDay > 0 && (<> · <span data-testid="ls-transfer-pax-day" className="font-semibold text-indigo-600" title="O günkü TÜM defterlerin toplam transferli müşteri paxı (tüm turlar bir arada)">🚌 {transferPaxDay} gün toplam</span></>)}</span>
        {grouped && boats.filter((bt) => bt.pax > 0).length > 0 && (
          <div className="flex items-center gap-1 flex-wrap" data-testid="ls-arrived-boats-summary">
            <span className="text-[11px] text-gray-400 font-medium">Tekne:</span>
            {boats.filter((bt) => bt.pax > 0).map((bt) => (
              <span key={`arr-${bt.id}`} data-testid={`ls-arrived-boat-${bt.id}`}
                className="px-2 py-0.5 rounded-md bg-white border text-[11px] font-semibold text-gray-600 whitespace-nowrap"
                title={`${bt.name}: gelen (check-in) / toplam kişi`}>
                {bt.name} <span className={(arrivedStats.byBoat[bt.id] || 0) >= bt.pax ? "text-emerald-600" : "text-emerald-500"}>✓{arrivedStats.byBoat[bt.id] || 0}</span><span className="text-gray-400">/{bt.pax}</span>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-0.5 bg-white border border-gray-300 rounded-md px-0.5" title="Yazı boyutunu büyüt/küçült (ekranı büyütmek için)">
          <button data-testid="ls-zoom-out" onClick={() => changeZoom("out")} disabled={zoom <= 1}
            className="px-1.5 py-1 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-30 text-sm font-bold">A−</button>
          <button data-testid="ls-zoom-reset" onClick={() => changeZoom("reset")}
            className="px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 hover:bg-gray-100 rounded min-w-[34px]">{Math.round(zoom * 100)}%</button>
          <button data-testid="ls-zoom-in" onClick={() => changeZoom("in")} disabled={zoom >= 2}
            className="px-1.5 py-1 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-30 text-base font-bold">A+</button>
        </div>
        <button data-testid="ls-selectmode-toggle" onClick={() => setSelectMode((v) => !v)}
          title="Toplu Seç: satırların soluna seçim kutucukları çıkar, işaretleyip toplu tekneye ata"
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${selectMode ? "bg-blue-600 text-white border-blue-600 shadow" : "bg-white text-blue-600 border-blue-300 hover:bg-blue-50"}`}>
          <i className={`fa-solid ${selectMode ? "fa-square-check" : "fa-list-check"}`} /> Toplu Seç {selectMode ? "AÇIK" : ""}
        </button>
        {selRows.size > 0 ? (
          <span data-testid="ls-reorder-selinfo" className="flex items-center gap-1.5 text-[11px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
            {selRows.size} satır seçili · sürükle veya Alt+↑/↓
            <button data-testid="ls-reorder-clearsel" onClick={() => setSelRows(new Set())} className="text-blue-400 hover:text-blue-700"><X size={12} /></button>
          </span>
        ) : null}
        {canManage && selRows.size === 1 && (
          <button data-testid="ls-invoice-btn" onClick={openInvoiceForSel} title="Seçili rezervasyona Paraşüt faturası kes"
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white transition-colors">
            <ReceiptText size={12} /> Fatura Kes
          </button>
        )}
        {selRows.size === 0 && (
          <span className="flex items-center gap-1 text-[11px] text-gray-400" title="Satır numarasından tutup sürükleyerek sırayı değiştirin. Toplu tekne atama için 'Toplu Seç' butonunu açın.">
            <GripVertical size={12} /> Numaradan sürükle-sırala · toplu için "Toplu Seç"
          </span>
        )}
        {activeUsers.length > 0 && (
          <div className="flex items-center -space-x-1.5" data-testid="ls-active-users" title={`Aktif: ${activeUsers.join(", ")}`}>
            {activeUsers.slice(0, 5).map((u) => {
              const initial = (u || "?").trim().charAt(0).toUpperCase();
              let h = 0; for (let i = 0; i < (u || "").length; i++) h = (h * 31 + u.charCodeAt(i)) % 360;
              return (
                <span key={u} className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ring-2 ring-white"
                  style={{ backgroundColor: `hsl(${h}, 55%, 45%)` }} title={`${u}${userFocus[u] ? " · " + userFocus[u] : ""}`}>{initial}</span>
              );
            })}
            {activeUsers.length > 5 && <span className="text-[10px] text-gray-500 pl-2">+{activeUsers.length - 5}</span>}
            {Object.entries(userFocus).filter(([u]) => u !== myEmailRef.current)[0] && (
              <span className="pl-2 text-[10px] text-gray-500 whitespace-nowrap" data-testid="ls-user-focus">
                ✎ {(() => { const e = Object.entries(userFocus).filter(([u]) => u !== myEmailRef.current)[0]; return `${(e[0] || "").split("@")[0]}: ${e[1]}`; })()}
              </span>
            )}
          </div>
        )}
        <span data-testid="ls-live-status" className={`flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full font-medium ${wsConnected ? "bg-emerald-100 text-emerald-700" : wsState === "reconnecting" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${wsConnected ? "bg-emerald-500 animate-pulse" : wsState === "reconnecting" ? "bg-amber-500 animate-pulse" : "bg-gray-400"}`} />
          {wsConnected ? (activeUsers.length > 1 ? "Canlı · 1sn senkron" : "Canlı") : wsState === "reconnecting" ? "Yeniden bağlanıyor…" : "Bağlantı yok"}
        </span>
        {queuedCount > 0 && (
          <span data-testid="ls-pending-writes" title="Bağlantı gelince otomatik gönderilecek bekleyen değişiklikler"
            className="flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-700">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
            {queuedCount} bekleyen kayıt
          </span>
        )}
        </>)}
      </div>

      {/* FAZ F8: Tekne + saat filtreleri */}
      {showTools && (
      <div className={`flex items-center gap-x-2 gap-y-1 border-b bg-gray-50 flex-nowrap overflow-x-auto md:flex-wrap md:overflow-visible text-xs [&>*]:shrink-0 ${compact ? "px-2 py-0.5" : "px-2 md:px-4 py-1"}`}>
        <div className="flex items-center gap-x-1 gap-y-1 flex-wrap" data-testid="ls-boat-filter">
          <button data-testid="ls-grouped-toggle" onClick={() => { setGrouped((v) => !v); setBoatFilter(null); }}
            title="Tekneleri alt alta ayrımlı göster / tek tekne filtresine geç"
            className={`px-2.5 py-1 rounded-md font-semibold border transition-colors mr-1 ${grouped ? "bg-indigo-600 text-white border-indigo-600 shadow" : "bg-white text-indigo-600 border-indigo-300 hover:bg-indigo-50"}`}>
            <i className="fa-solid fa-layer-group mr-1" /> Ayrımlı Görünüm {grouped ? "AÇIK" : "KAPALI"}
          </button>
          {canManage && (<>
          <button data-testid="ls-auto-assign" onClick={runAutoAssign} disabled={autoAssigning}
            title="Atanmamış rezervasyonları kalkış/tur tipine göre doğru bölmeye otomatik dağıt"
            className="px-2.5 py-1 rounded-md font-semibold border transition-colors mr-1 bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50 disabled:opacity-50">
            <i className={`fa-solid ${autoAssigning ? "fa-spinner fa-spin" : "fa-wand-magic-sparkles"} mr-1`} />
            {autoAssigning ? "Dağıtılıyor..." : "Otomatik Dağıt"}
          </button>
          <button data-testid="ls-auto-assign-all" onClick={runAutoAssignAll} disabled={autoAssigning}
            title="Bugün ve tüm ileri tarihlerdeki ATANMAMIŞ rezervasyonları kurallara göre dağıt (elle atananlara dokunmaz)"
            className="px-2.5 py-1 rounded-md font-semibold border transition-colors mr-1 bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50 disabled:opacity-50">
            <i className="fa-solid fa-calendar-check mr-1" /> Tüm Tarihler
          </button>
          {showRedistribute && (<>
          <button data-testid="ls-redistribute" onClick={runRedistribute} disabled={autoAssigning}
            title="Mevcut atamaları kurallara göre yeniden diz (kilitli/iptal/Rezerve hariç)"
            className="px-2.5 py-1 rounded-md font-semibold border transition-colors mr-1 bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50 disabled:opacity-40">
            <i className="fa-solid fa-arrows-rotate mr-1" /> Yeniden Dağıt
          </button>
          <button data-testid="ls-hard-redistribute" onClick={runHardRedistribute} disabled={autoAssigning}
            title="Bugün ve tüm ileri tarihli rezervasyonları kurallara göre yeniden dağıt (kilitli/iptal/Rezerve hariç)"
            className="px-2.5 py-1 rounded-md font-semibold border transition-colors mr-1 bg-white text-rose-700 border-rose-300 hover:bg-rose-50 disabled:opacity-40">
            <i className="fa-solid fa-bolt mr-1" /> Hard Yeniden Dağıt
          </button>
          </>)}
          <button data-testid="ls-lock-day" onClick={toggleDayLock}
            title={dayLocked ? "Bu günün kilidini aç (yeniden dağıt tekrar etkiler)" : "Bu günü kilitle (yeniden dağıt/kural bu güne dokunmaz)"}
            className={`px-2.5 py-1 rounded-md font-semibold border transition-colors mr-1 ${dayLocked ? "bg-amber-100 text-amber-800 border-amber-400" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
            <i className={`fa-solid ${dayLocked ? "fa-lock" : "fa-lock-open"} mr-1`} /> {dayLocked ? "Kilitli" : "Günü Kilitle"}
          </button>
          <button data-testid="ls-export-excel" onClick={async () => {
              try { toast.loading("Excel hazırlanıyor…", { id: "xls" }); await exportSheetToExcel({ date, activeCategory }); toast.success("Excel indirildi", { id: "xls" }); }
              catch (e) { toast.error("Excel dışa aktarılamadı", { id: "xls" }); }
            }}
            title="Bu tarih/kategori defterini Excel (.xlsx) olarak indir"
            className="px-2.5 py-1 rounded-md font-semibold border transition-colors mr-1 bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50">
            <i className="fa-solid fa-file-excel mr-1" /> Excel'e Aktar
          </button>
          <div className="relative inline-block mr-1">
            <button data-testid="ls-export-range-toggle" onClick={() => {
                if (!rangeStart) setRangeStart(date);
                if (!rangeEnd) setRangeEnd(date);
                setRangeExportOpen((o) => !o);
              }}
              title="Tarih aralığı seç → her tarih ayrı sekme olan tek Excel indir"
              className="px-2.5 py-1 rounded-md font-semibold border transition-colors bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50">
              <i className="fa-solid fa-calendar-week mr-1" /> Tarih Aralığı Excel
            </button>
            {rangeExportOpen && (
              <div data-testid="ls-range-popover" className="absolute z-50 mt-1 left-0 bg-white border border-gray-300 rounded-lg shadow-xl p-3 w-64 text-gray-700">
                <div className="text-xs font-semibold text-gray-500 mb-2">Her tarih ayrı sekme (tab) olarak indirilir</div>
                <label className="block text-xs text-gray-500 mb-1">Başlangıç</label>
                <input type="date" data-testid="ls-range-start" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)}
                  className="w-full mb-2 border border-gray-300 rounded px-2 py-1 text-sm" />
                <label className="block text-xs text-gray-500 mb-1">Bitiş</label>
                <input type="date" data-testid="ls-range-end" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)}
                  className="w-full mb-3 border border-gray-300 rounded px-2 py-1 text-sm" />
                <button data-testid="ls-range-export-btn" disabled={rangeBusy || !rangeStart || !rangeEnd}
                  onClick={async () => {
                    setRangeBusy(true);
                    try {
                      toast.loading("Excel hazırlanıyor… (0)", { id: "xls-r" });
                      await exportSheetRangeToExcel({ start: rangeStart, end: rangeEnd, activeCategory,
                        onProgress: (d, t) => toast.loading(`Excel hazırlanıyor… (${d}/${t} gün)`, { id: "xls-r" }) });
                      toast.success("Excel indirildi (her tarih ayrı sekme)", { id: "xls-r" });
                      setRangeExportOpen(false);
                    } catch (e) { toast.error(e?.message || "Aralık dışa aktarılamadı", { id: "xls-r" }); }
                    finally { setRangeBusy(false); }
                  }}
                  className="w-full px-3 py-1.5 rounded-md font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                  {rangeBusy ? "Hazırlanıyor…" : "Aralığı Dışa Aktar"}
                </button>
                <div className="text-[10px] text-gray-400 mt-2">En fazla 92 gün. Aktif kategori dahil edilir.</div>
              </div>
            )}
          </div>
          <button data-testid="ls-export-transfer" onClick={async () => {
              try { toast.loading("Transfer listesi hazırlanıyor…", { id: "xls-t" }); await exportTransferListToExcel({ date, activeCategory }); toast.success("Transfer listesi indirildi", { id: "xls-t" }); }
              catch (e) { toast.error("Transfer listesi dışa aktarılamadı", { id: "xls-t" }); }
            }}
            title="Sadece transferli rezleri (otel adında 'Kendi Gelir' olmayanlar) Excel olarak indir"
            className="px-2.5 py-1 rounded-md font-semibold border transition-colors mr-1 bg-white text-sky-700 border-sky-300 hover:bg-sky-50">
            <i className="fa-solid fa-van-shuttle mr-1" /> Transfer Listesi
          </button>
          <button data-testid="ls-reschedule" onClick={runReschedule}
            title="Seçili rezervasyonu başka bir güne taşı (reschedule) — Bókun sync tarafından geri alınmaz"
            className="px-2.5 py-1 rounded-md font-semibold border transition-colors mr-1 bg-white text-violet-700 border-violet-300 hover:bg-violet-50">
            <i className="fa-solid fa-calendar-day mr-1" /> Tarih Değiştir
          </button>
          <button data-testid="ls-cancel" onClick={() => runCancel(true)}
            title="Seçili rezervasyon(lar)ı iptal et — en alta düşer, pax sayımından çıkar"
            className="px-2.5 py-1 rounded-md font-semibold border transition-colors mr-1 bg-white text-rose-700 border-rose-300 hover:bg-rose-50">
            <i className="fa-solid fa-ban mr-1" /> İptal
          </button>
          <button data-testid="ls-uncancel" onClick={() => runCancel(false)}
            title="Seçili iptal edilmiş rezervasyon(lar)ı geri al"
            className="px-2.5 py-1 rounded-md font-semibold border transition-colors mr-1 bg-white text-gray-600 border-gray-300 hover:bg-gray-50">
            <i className="fa-solid fa-rotate-left mr-1" /> İptali Geri Al
          </button>
          <button data-testid="ls-delete" onClick={runDelete}
            title="Seçili MANUEL rezervasyon(lar)ı KALICI sil (Bókun rezervasyonları atlanır — onlar için İptal kullanın)"
            className="px-2.5 py-1 rounded-md font-semibold border transition-colors mr-1 bg-white text-red-700 border-red-400 hover:bg-red-50">
            <i className="fa-solid fa-trash mr-1" /> Sil
          </button>
          </>)}
          {!grouped && <span className="text-gray-400 font-medium mr-1">Tekne:</span>}
          {!grouped && (
            <button data-testid="ls-boat-all" onClick={() => setBoatFilter(null)}
              className={`px-2.5 py-1 rounded-md font-semibold ${boatFilter == null ? "bg-gray-800 text-white" : "bg-white border text-gray-600 hover:bg-gray-100"}`}>TÜMÜ</button>
          )}
          {!grouped && boats.map((bt) => {
            const active = boatFilter === bt.id;
            const cap = bt.capacity || 0;
            const over = cap > 0 && bt.pax > cap;
            const near = cap > 0 && !over && bt.pax >= cap * 0.9;
            const capCls = over ? "text-red-600 font-bold" : near ? "text-amber-600 font-semibold" : "opacity-70";
            const isDrop = boatDropId === bt.id;
            return (
              <button key={bt.id} data-testid={`ls-boat-${bt.id}`} data-boat-assign={bt.id}
                onClick={() => setBoatFilter(bt.id)}
                title={selRows.size > 0 ? `${selRows.size} seçili satırı ${bt.name} teknesine atamak için tıkla` : bt.name}
                onDoubleClick={() => selRows.size > 0 && assignBoat(bt.id)}
                className={`px-2.5 py-1 rounded-md font-semibold border transition-colors ${isDrop ? "bg-emerald-500 text-white border-emerald-600 ring-2 ring-emerald-300 scale-105" : active ? "bg-gray-800 text-white border-gray-800" : `bg-white text-gray-600 hover:bg-gray-100 ${over ? "border-red-400" : near ? "border-amber-400" : ""}`}`}>
                {bt.name} <span className={isDrop || active ? "opacity-80" : capCls}>· {bt.pax}{cap > 0 ? `/${cap}` : "p"} · {bt.count ?? 0} rez</span>{(arrivedStats.byBoat[bt.id] || 0) > 0 && (<span className={isDrop || active ? "opacity-80" : "text-emerald-600 font-semibold"} title="Bu tekneye gelen (check-in) kişi / toplam kişi"> · ✓{arrivedStats.byBoat[bt.id]}/{bt.pax}</span>)}{bt.transfer_pax > 0 && (<span className={isDrop || active ? "opacity-80" : "text-blue-600"} title="Bu teknedeki transferli müşteri (kişi)"> · {bt.transfer_pax} tr</span>)}
              </button>
            );
          })}
          {grouped && (
            <span className="text-gray-400 text-[11px] ml-1" data-testid="ls-grouped-hint">
              Satır seç → sol butonlarla (▲▼) sırala veya Alt+↑/↓ · Tekne başlığına tıkla: seçilenleri o tekneye taşı · sürükle de olur
            </span>
          )}
          {selRows.size > 0 && (
            <div className="relative border-l pl-2 ml-1" data-testid="ls-boat-assign-wrap">
              <button data-testid="ls-boat-assign-btn" onClick={() => setBoatMenuOpen((v) => !v)}
                className="px-2.5 py-1 rounded-md font-semibold bg-emerald-600 text-white hover:bg-emerald-700">
                {selRows.size} satırı Tekneye Ata ▾
              </button>
              {boatMenuOpen && (
                <div className="absolute z-50 mt-1 left-2 bg-white border rounded-lg shadow-xl py-1 min-w-[160px]" data-testid="ls-boat-assign-menu">
                  {boats.filter((b) => b.name !== "Atanmamis" && b.name !== "Atanmamış").map((bt) => (
                    <button key={bt.id} data-testid={`ls-boat-assign-${bt.id}`} onClick={() => assignBoat(bt.id)}
                      className="block w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50 text-gray-700">
                      {bt.name} <span className="text-gray-400">· {bt.pax}{bt.capacity ? `/${bt.capacity}` : "p"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {times.length > 1 && (
          <div className="flex items-center gap-1 border-l pl-3" data-testid="ls-time-filter">
            <span className="text-gray-400 font-medium mr-1">Saat:</span>
            <button data-testid="ls-time-all" onClick={() => setTimeFilter(null)}
              className={`px-2 py-1 rounded-md font-semibold ${timeFilter == null ? "bg-blue-600 text-white" : "bg-white border text-gray-600 hover:bg-gray-100"}`}>Tümü</button>
            {times.map((t) => (
              <button key={t || "none"} data-testid={`ls-time-${t || "none"}`} onClick={() => setTimeFilter(t)}
                className={`px-2 py-1 rounded-md font-semibold ${timeFilter === t ? "bg-blue-600 text-white" : "bg-white border text-gray-600 hover:bg-gray-100"}`}>
                {t || "Saatsiz"}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center border-l pl-3">
          <button data-testid="ls-pending-toggle" onClick={() => setPendingOnly((v) => !v)}
            title="Sadece henüz check-in olmamış (gelmemiş) müşterileri göster"
            className={`px-2.5 py-1 rounded-md font-semibold border transition-colors ${pendingOnly
              ? "bg-amber-500 text-white border-amber-500"
              : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"}`}>
            Gelmeyenler{pendingCount != null ? ` (${pendingCount})` : ""}
          </button>
        </div>
      </div>
      )}

      {/* Eşlenmemiş: ürünleri kategoriye bağlama paneli */}
      {activeCategory === "unmapped" && unmappedProducts.length > 0 && (
        <div className="px-4 py-2 border-b bg-amber-50/60 max-h-40 overflow-y-auto" data-testid="ls-unmapped-panel">
          <div className="text-xs font-semibold text-amber-700 mb-1.5">Eşlenmemiş ürünleri bir kategoriye bağlayın (tüm günlere uygulanır):</div>
          <div className="space-y-1">
            {unmappedProducts.map((p) => (
              <div key={p.product_name} className="flex items-center gap-2 text-xs" data-testid={`ls-unmapped-row`}>
                <span className="flex-1 truncate text-gray-700" title={p.product_name}>{p.product_name || "(isimsiz)"} <span className="text-gray-400">· {p.count}</span></span>
                <select data-testid="ls-map-select" value={mapChoice[p.product_name] || ""}
                  onChange={(e) => setMapChoice((m) => ({ ...m, [p.product_name]: e.target.value }))}
                  className="px-2 py-1 border rounded-md bg-white">
                  <option value="">Kategori seç…</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button data-testid="ls-map-btn" onClick={() => mapProduct(p.product_name)}
                  className="px-2.5 py-1 rounded-md bg-amber-500 text-white font-semibold hover:bg-amber-600">Eşle</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && sheetData && (
        <div data-testid="ls-error" className="px-4 py-2 bg-red-50 text-red-600 text-sm">{error}</div>
      )}

      {/* FortuneSheet */}
      <div ref={wrapRef} className="flex-1 relative" style={{ minWidth: 0, minHeight: 0 }} onMouseUp={onSheetMouseUp}>
        {loading && !sheetData && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm z-10">Yükleniyor…</div>
        )}
        {error && !sheetData && (
          <div data-testid="ls-error-full" className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10 px-6 text-center">
            <div className="text-red-600 text-sm max-w-md">{error}</div>
            <button
              data-testid="ls-error-retry"
              onClick={() => { loadRetryRef.current = 0; setError(null); fetchSheet(); }}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 active:scale-95 transition-transform"
            >
              Tekrar Dene
            </button>
            <div className="text-gray-400 text-xs max-w-xs">Sunucu geçici olarak yanıt vermiyor olabilir. Birkaç saniye sonra tekrar deneyin.</div>
          </div>
        )}
        {sheetData && (
          <Workbook
            ref={wbRef}
            key={renderKey}
            data={[sheetData]}
            showToolbar={false}
            showFormulaBar={true}
            allowEdit={true}
            cellContextMenu={["copy", "paste", "|", "clear"]}
            onOp={handleOp}
            row={sheetData.row}
            column={sheetData.column}
          />
        )}

        {seatFilter && (
          <button data-testid="ls-seatfilter-clear" onClick={() => setSeatFilter(null)}
            className="absolute flex items-center gap-2 rounded-full bg-fuchsia-700 hover:bg-fuchsia-800 text-white font-bold shadow-lg cursor-pointer"
            style={{ top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 30, fontSize: 12, padding: "5px 14px" }}
            title="Filtreyi kaldır">
            <i className="fa-solid fa-filter" /> Filtre: {({ cam: "Cam Kenarı", vip_cam: "VIP Cam Kenarı", vip: "VIP", sahne: "Sahne Önü", salon: "Salon", standart: "Standart" })[seatFilter] || seatFilter} <i className="fa-solid fa-xmark" />
          </button>
        )}

        {/* Oturum filtresi overlay: eslesmeyen satirlar soluk, eslesenler vurgulu */}
        {dimBars.map((b, i) => (
          <div key={`dim-${i}`} className="absolute pointer-events-none"
            style={{ top: b.top, left: 0, width: b.width, height: b.height, zIndex: 6,
              backgroundColor: b.match ? "transparent" : "rgba(255,255,255,0.74)",
              boxShadow: b.match ? "inset 3px 0 0 #9333EA, inset -3px 0 0 #9333EA" : "none" }} />
        ))}

        {/* Satir siralama: secim vurgu barlari (mavi) + strip uzerinde grip gostergesi */}
        {rowBars.map((b, i) => (
          <div key={i} data-testid="ls-selrow-bar" className="absolute pointer-events-none"
            style={{ top: b.top, left: 0, width: b.width, height: b.height, zIndex: 5,
              backgroundColor: "rgba(37,99,235,0.14)", boxShadow: "inset 2px 0 0 #2563eb" }} />
        ))}
        {stripBox && rowBars.map((b, i) => (
          <div key={`grip-${i}`} data-testid="ls-selrow-grip" className="absolute pointer-events-none flex items-center justify-center"
            style={{ top: b.top, left: stripBox.left, width: stripBox.width, height: b.height, zIndex: 7 }}>
            <GripVertical size={11} className="text-blue-700 opacity-80" strokeWidth={2.5} />
          </div>
        ))}

        {/* Tekne-ici sıralama butonları (sürüklemesiz): seçili satır(lar)ı yukarı/aşağı/en üst/en alt taşı */}
        {grouped && stripBox && rowBars.length > 0 && (() => {
          const top = Math.min(...rowBars.map((b) => b.top));
          const btn = "w-6 h-5 flex items-center justify-center rounded bg-white border border-blue-300 text-blue-700 shadow-sm hover:bg-blue-600 hover:text-white transition-colors";
          const mv = (m) => moveSelWithinBoatRef.current && moveSelWithinBoatRef.current(m);
          return (
            <div data-testid="ls-reorder-actions" className="absolute flex flex-col gap-0.5 pointer-events-auto"
              style={{ top: Math.max(top, 2), left: 1, zIndex: 12 }}>
              <button data-testid="ls-move-top" title="Teknede en üste al" onClick={() => mv("top")} className={btn}><ChevronsUp size={12} strokeWidth={2.5} /></button>
              <button data-testid="ls-move-up" title="Bir yukarı taşı (Alt+↑)" onClick={() => mv("up")} className={btn}><ChevronUp size={12} strokeWidth={2.5} /></button>
              <button data-testid="ls-move-down" title="Bir aşağı taşı (Alt+↓)" onClick={() => mv("down")} className={btn}><ChevronDown size={12} strokeWidth={2.5} /></button>
              <button data-testid="ls-move-bottom" title="Teknede en alta al" onClick={() => mv("bottom")} className={btn}><ChevronsDown size={12} strokeWidth={2.5} /></button>
              {canManage && rowBars.length === 1 && selRows.size === 1 && (
                <button data-testid="ls-row-invoice-btn" title="Fatura Kes" onClick={openInvoiceForSel} className={`${btn} !border-indigo-300 !text-indigo-700 hover:!bg-indigo-600 hover:!text-white`}><ReceiptText size={12} strokeWidth={2.5} /></button>
              )}
            </div>
          );
        })()}

        {/* Toplu tekne atama: her gorunur satirin solunda secim kutucugu (yalnizca Toplu Seç modu) */}
        {selectMode && stripBox && rowChecks.map((c) => {
          const checked = selRows.has(c.r);
          return (
            <div key={`chk-${c.r}`} data-testid={`ls-rowcheck-${c.r}`}
              onMouseDown={(e) => { e.stopPropagation(); onStripMouseDown(e, true); }}
              className="absolute flex items-center justify-center cursor-pointer"
              style={{ top: c.top, left: stripBox.left, width: 20, height: c.height, zIndex: 9 }}
              title="Toplu tekne atama için seç (Shift ile aralık)">
              <span className={`flex items-center justify-center rounded-[3px] border text-white ${checked ? "bg-blue-600 border-blue-600" : "bg-white/90 border-gray-400"}`}
                style={{ width: 14, height: 14, fontSize: 10, lineHeight: 1 }}>
                {checked ? "✓" : ""}
              </span>
            </div>
          );
        })}

        {/* Tekne başlığına tıkla-ata: satır seçiliyken tekne başlığının soluna tıklayınca seçilenler o tekneye taşınır */}
        {grouped && selRows.size > 0 && headerBadges.map((h) => (
          <div key={`hassign-${h.r}`} data-testid={`ls-header-assign-${h.boat_id}`}
            onClick={() => assignBoatRef.current && assignBoatRef.current(h.boat_id)}
            title={`Seçili ${selRows.size} rezervasyonu ${h.name} teknesine taşı`}
            className="absolute pointer-events-auto cursor-pointer flex items-center hover:bg-blue-500/10 transition-colors"
            style={{ top: h.top, height: h.height, left: 0, width: "52%", zIndex: 9 }}>
            <span className="ml-8 flex items-center gap-1 rounded-full bg-blue-600 text-white font-bold shadow" style={{ fontSize: 10, padding: "2px 8px" }}>
              <Ship size={11} strokeWidth={2.5} /> → {h.name}
            </span>
          </div>
        ))}

        {/* Tekne basligi: Cam Kenari / VIP renkli mini rozetleri (baslik satirinin saginda) */}
        {headerBadges.map((h) => (
          <div key={`hb-${h.r}`} data-testid={`ls-header-badge-${h.r}`} className="absolute pointer-events-none flex items-center gap-1.5"
            style={{ top: h.top, height: h.height, right: 28, zIndex: 8 }}>
            <button data-testid={`ls-add-rez-${h.boat_id}`} onClick={() => addManualRowToBoat(h.boat_id)}
              title="Boş satıra atla — yazarak hızlı rezervasyon ekle"
              className="pointer-events-auto flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow cursor-pointer"
              style={{ fontSize: 11, padding: "3px 10px" }}>
              <i className="fa-solid fa-plus" style={{ fontSize: 10 }} /> Rez Ekle
            </button>
            {h.cam > 0 && (
              <span onClick={() => setSeatFilter((f) => f === "cam" ? null : "cam")}
                className={`pointer-events-auto cursor-pointer flex items-center gap-1 rounded-full text-white font-bold shadow ${seatFilter === "cam" ? "ring-2 ring-offset-1 ring-amber-300" : ""}`}
                style={{ backgroundColor: "#B45309", fontSize: 11, padding: "3px 10px" }} title={`${h.cam} cam kenarı — tıkla: sadece bunları göster`}>
                <i className="fa-solid fa-window-maximize" style={{ fontSize: 10 }} /> Cam Kenarı {h.cam}
              </span>
            )}
            {h.vipcam > 0 && (
              <span onClick={() => setSeatFilter((f) => f === "vip_cam" ? null : "vip_cam")}
                className={`pointer-events-auto cursor-pointer flex items-center gap-1 rounded-full text-white font-bold shadow ${seatFilter === "vip_cam" ? "ring-2 ring-offset-1 ring-fuchsia-300" : ""}`}
                style={{ backgroundColor: "#9333EA", fontSize: 11, padding: "3px 10px" }} title={`${h.vipcam} VIP Cam Kenarı — tıkla: sadece bunları göster`}>
                <i className="fa-solid fa-star" style={{ fontSize: 10 }} /> VIP Cam {h.vipcam}
              </span>
            )}
            {h.vip > 0 && (
              <span onClick={() => setSeatFilter((f) => f === "vip" ? null : "vip")}
                className={`pointer-events-auto cursor-pointer flex items-center gap-1 rounded-full text-white font-bold shadow ${seatFilter === "vip" ? "ring-2 ring-offset-1 ring-violet-300" : ""}`}
                style={{ backgroundColor: "#7C3AED", fontSize: 11, padding: "3px 10px" }} title={`${h.vip} VIP — tıkla: sadece bunları göster`}>
                <i className="fa-solid fa-crown" style={{ fontSize: 10 }} /> VIP {h.vip}
              </span>
            )}
            {h.sahne > 0 && (
              <span className="flex items-center gap-1 rounded-full text-white font-bold shadow"
                style={{ backgroundColor: "#0369A1", fontSize: 11, padding: "3px 10px" }} title={`${h.sahne} sahne önü rezervasyonu`}>
                <i className="fa-solid fa-music" style={{ fontSize: 10 }} /> Sahne Önü {h.sahne}
              </span>
            )}
            {h.salon > 0 && (
              <span className="flex items-center gap-1 rounded-full text-white font-bold shadow"
                style={{ backgroundColor: "#0f766e", fontSize: 11, padding: "3px 10px" }} title={`${h.salon} salon rezervasyonu`}>
                <i className="fa-solid fa-couch" style={{ fontSize: 10 }} /> Salon {h.salon}
              </span>
            )}
            {h.standart > 0 && (
              <span className="flex items-center gap-1 rounded-full font-bold shadow"
                style={{ backgroundColor: "#e5e7eb", color: "#374151", fontSize: 11, padding: "3px 10px" }} title={`${h.standart} standart rezervasyon`}>
                <i className="fa-solid fa-chair" style={{ fontSize: 10 }} /> Standart {h.standart}
              </span>
            )}
          </div>
        ))}

        {/* Hover: satir vurgusu + tutma sapi (grip) — suruklenebilirligi gorunur kilar */}
        {hoverBar && !dragGhost && (
          <div data-testid="ls-row-hover" className="absolute pointer-events-none"
            style={{ top: hoverBar.top, left: 0, width: hoverBar.width, height: hoverBar.height, zIndex: 4,
              backgroundColor: "rgba(37,99,235,0.06)" }} />
        )}
        {hoverBar && stripBox && !dragGhost && (
          <div data-testid="ls-row-grip" className="absolute pointer-events-none flex items-center justify-center"
            style={{ top: hoverBar.top, left: stripBox.left, width: stripBox.width, height: hoverBar.height, zIndex: 7,
              backgroundColor: "rgba(37,99,235,0.12)", borderRight: "2px solid #2563eb" }}>
            <GripVertical size={12} className="text-blue-700" strokeWidth={2.5} />
          </div>
        )}

        {/* Ayrimli gorunumde surukleme: hedef tekne bolgesinin yesil dropzone vurgusu */}
        {boatZone && (
          <div data-testid="ls-boat-dropzone" className="absolute pointer-events-none"
            style={{ top: boatZone.top, left: 0, width: boatZone.width, height: boatZone.height, zIndex: 6,
              backgroundColor: "rgba(16,185,129,0.12)", border: "2px dashed #059669", borderRadius: 4 }}>
            <span className="absolute top-1.5 right-3 text-[11px] font-bold text-white bg-emerald-600 px-2.5 py-1 rounded-full shadow-lg">
              → {boatZone.name} teknesine bırak
            </span>
          </div>
        )}

        {/* Satir siralama: birakma cizgisi */}
        {dropLineY != null && (
          <div data-testid="ls-drop-line" className="absolute pointer-events-none"
            style={{ top: dropLineY - 1, left: 0, right: 0, height: 2, backgroundColor: "#2563eb", zIndex: 7, boxShadow: "0 0 0 1px rgba(37,99,235,0.4)" }} />
        )}

        {/* Satir siralama: row-header stripi uzerinde tut-surukle katmani */}
        {stripBox && sheetData && (
          <div data-testid="ls-reorder-strip"
            onMouseDown={onStripMouseDown}
            onMouseMove={onStripHover}
            onMouseLeave={() => { setTagPopover(null); setHoverBar(null); lastHoverRRef.current = null; }}
            title="Satır numarasından tutup sürükleyerek taşıyın veya tekne sekmesine bırakın (Ctrl/Shift ile çoklu seçim)"
            className="absolute"
            style={{ top: stripBox.top, left: stripBox.left, width: stripBox.width, height: stripBox.height, zIndex: 6, cursor: dragRef.current.active ? "grabbing" : "grab" }} />
        )}

        {!loading && sheetData && total === 0 && (
          <div data-testid="ls-empty" className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm z-10 pointer-events-none">
            Bu tarih ve defterde kayıt yok.
          </div>
        )}
      </div>

      {/* Surukleme hayaleti — imlec yaninda tasinan satir(lar) onizlemesi */}
      {dragGhost && (
        <div data-testid="ls-drag-ghost" className="fixed pointer-events-none z-[100] flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-blue-600 text-white shadow-xl text-xs font-semibold"
          style={{ left: dragGhost.x + 14, top: dragGhost.y + 10, maxWidth: 260 }}>
          <GripVertical size={13} className="opacity-80 flex-shrink-0" />
          <span className="truncate">{dragGhost.label || "Satır"}</span>
          {dragGhost.count > 1 && (
            <span className="flex-shrink-0 bg-white/25 rounded-full px-1.5 py-0.5 text-[10px]">+{dragGhost.count - 1}</span>
          )}
          {dragGhost.target && (
            <span data-testid="ls-ghost-target" className="flex-shrink-0 bg-emerald-500 rounded-full px-1.5 py-0.5 text-[10px] font-bold">→ {dragGhost.target}</span>
          )}
        </div>
      )}
      {/* Yapisal oturum etiketi duzenleyici (EKSTRA hucresine tiklayinca) */}
      {tagEditor && (
        <>
          <div className="fixed inset-0 z-[95]" onClick={() => setTagEditor(null)} data-testid="ls-tag-editor-overlay" />
          <div data-testid="ls-tag-editor" className="fixed z-[96] bg-white rounded-xl shadow-2xl border border-gray-200 p-3 w-72"
            style={{ left: Math.min(tagEditor.x, window.innerWidth - 300), top: Math.min(tagEditor.y, window.innerHeight - 340) }}>
            <div className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
              <i className="fa-solid fa-tags text-amber-600" /> Etiketler
              <span className="text-xs font-normal text-gray-400 ml-auto">{tagEditor.pax} kişi</span>
            </div>
            <div className="space-y-1.5 max-h-64 overflow-auto">
              {SEATING_OPTS.map((o) => {
                const on = tagEditor.sel[o.key] != null && tagEditor.sel[o.key] !== false && tagEditor.sel[o.key] !== 0;
                const qty = o.counted ? (parseInt(tagEditor.sel[o.key], 10) || tagEditor.pax || 1) : 0;
                return (
                  <div key={o.key} className="flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-gray-50">
                    <label className="flex items-center gap-2 flex-1 cursor-pointer">
                      <input type="checkbox" data-testid={`ls-tag-${o.key}`} checked={!!on}
                        onChange={(e) => setTagEditor((t) => {
                          const sel = { ...t.sel };
                          if (e.target.checked) sel[o.key] = o.counted ? (t.pax || 1) : true;
                          else delete sel[o.key];
                          return { ...t, sel };
                        })}
                        className="w-4 h-4 accent-amber-600" />
                      <span className="text-sm font-medium" style={{ color: o.color }}>{o.label}</span>
                    </label>
                    {o.counted && on && (
                      <input type="number" min="1" value={qty} data-testid={`ls-tagqty-${o.key}`}
                        onChange={(e) => setTagEditor((t) => ({ ...t, sel: { ...t.sel, [o.key]: e.target.value } }))}
                        className="w-14 text-sm border rounded px-1.5 py-0.5 text-right" title="Kişi sayısı" />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-[11px] text-gray-400 mt-1.5 leading-tight">Adet, kişi sayısından azsa farkı "ekstra ödeyecek" olarak Açıklama'da uyarılır.</div>
            <div className="flex gap-2 mt-2.5">
              <button data-testid="ls-tag-save" onClick={() => saveTags(tagEditor.row_id, tagEditor.sel)}
                className="flex-1 bg-amber-600 text-white text-sm font-semibold rounded-lg py-1.5 hover:bg-amber-700">Kaydet</button>
              <button data-testid="ls-tag-cancel" onClick={() => setTagEditor(null)}
                className="px-3 text-sm font-medium text-gray-500 rounded-lg py-1.5 hover:bg-gray-100 border">İptal</button>
            </div>
          </div>
        </>
      )}

      {/* Etiket/Addon balonu */}
      {tagPopover && (
        <div data-testid="ls-tag-popover" className="fixed pointer-events-none z-[90] bg-white rounded-lg shadow-2xl border border-gray-200 px-3 py-2 max-w-xs"
          style={{ left: Math.min(tagPopover.x + 16, window.innerWidth - 280), top: tagPopover.y + 8 }}>
          {tagPopover.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1">
              {tagPopover.tags.map((t, i) => (
                <span key={i} className="text-[10px] font-bold text-white px-1.5 py-0.5 rounded" style={{ backgroundColor: t.bg }}>{t.label}</span>
              ))}
            </div>
          )}
          {tagPopover.extras.length > 0 && (
            <div className="flex flex-col gap-0.5 pt-1 border-t border-gray-100">
              <span className="text-[9px] uppercase tracking-wide text-gray-400 font-semibold">Addon</span>
              {tagPopover.extras.map((e, i) => (
                <span key={i} className="text-[11px] text-gray-700 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 inline-block" />
                  {e.name}{(e.qty || 1) > 1 ? ` ×${e.qty}` : ""}
                </span>
              ))}
            </div>
          )}
          {tagPopover.tags.length === 0 && tagPopover.extras.length === 0 && (
            <span className="text-[11px] text-gray-400">Etiket yok</span>
          )}
        </div>
      )}
      {/* Hizli arama kutusu (Ctrl+F / "/") */}
      {searchOpen && (
        <div data-testid="ls-search-box" className="fixed z-[95] top-3 right-6 bg-white rounded-xl shadow-2xl border border-gray-200 flex items-center gap-1.5 pl-3 pr-1.5 py-1.5">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input
            ref={searchInputRef}
            data-testid="ls-search-input"
            autoFocus
            value={searchQuery}
            onChange={(e) => runSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); gotoMatch(e.shiftKey ? -1 : 1); }
              else if (e.key === "Escape") { e.preventDefault(); closeSearch(); }
            }}
            placeholder="Ara: isim, otel, kod, telefon…"
            className="w-64 text-sm outline-none placeholder-gray-400"
          />
          <span data-testid="ls-search-count" className={`text-xs font-semibold tabular-nums px-1.5 ${searchQuery && searchInfo.count === 0 ? "text-red-500" : "text-gray-500"}`}>
            {searchQuery ? (searchInfo.count ? `${searchInfo.idx}/${searchInfo.count}` : "0") : ""}
          </span>
          <button data-testid="ls-search-prev" onClick={() => gotoMatch(-1)} disabled={!searchInfo.count}
            className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30" title="Önceki (Shift+Enter)"><ChevronLeft size={16} /></button>
          <button data-testid="ls-search-next" onClick={() => gotoMatch(1)} disabled={!searchInfo.count}
            className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30" title="Sonraki (Enter)"><ChevronRight size={16} /></button>
          <button data-testid="ls-search-filter" onClick={toggleSearchFilter} disabled={!searchInfo.count && !searchFilter}
            className={`px-2 py-1 rounded-md text-xs font-bold flex items-center gap-1 flex-shrink-0 disabled:opacity-30 transition-colors ${searchFilter ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            title="Bulunanlara göre süz (sadece eşleşen satırları göster)">
            <Filter size={13} /> Süz
          </button>
          <button data-testid="ls-search-checkin" onClick={toggleCheckin} disabled={!searchInfo.count}
            className="px-2 py-1 rounded-md bg-green-600 text-white text-xs font-bold hover:bg-green-700 disabled:opacity-30 flex-shrink-0" title="Bulunan müşteriyi 'Geldi' işaretle">
            Geldi ✓
          </button>
          <button data-testid="ls-search-close" onClick={closeSearch}
            className="p-1 rounded hover:bg-gray-100 text-gray-400" title="Kapat (Esc)"><X size={16} /></button>
        </div>
      )}

      {ctxMenu && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setCtxMenu(null)}
            onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }} />
          <div data-testid="ls-ctx-menu" className="fixed z-[9999] bg-white rounded-lg shadow-2xl border border-slate-200 py-1 text-sm"
            style={{ top: ctxMenu.y, left: ctxMenu.x, minWidth: 210 }}>
            <button data-testid="ls-ctx-add-rows"
              onClick={() => { captureScroll(); setExtraBlank((n) => n + 6); setCtxMenu(null); toast.success("Aşağıya 6 boş satır eklendi — yazarak rezervasyon işleyin"); }}
              className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-slate-700 font-medium flex items-center gap-2">
              <i className="fa-solid fa-plus text-emerald-600" /> Aşağıya boş satır ekle
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// --- Masaüstü "Manuel Rez Ekle" popup formu ---
const _MANUAL_MODAL_FIELDS = [
  { key: "isim", label: "İsim", ph: "Müşteri adı" },
  { key: "saat", label: "Saat", ph: "20:30" },
  { key: "pax", label: "Pax", ph: "2", type: "number" },
  { key: "telefon", label: "Telefon", ph: "+90…" },
  { key: "otel_adi", label: "Otel", ph: "Otel adı" },
  { key: "acenta", label: "Acenta", ph: "Acenta" },
  { key: "kod", label: "KOD", ph: "Rezervasyon kodu" },
  { key: "opsiyon", label: "Opsiyon / Ürün", ph: "Ürün / opsiyon" },
];

function ManualRezModal({ boats, onClose, onSubmit }) {
  const selectable = (boats || []).filter((b) => (b.name || "") !== "Atanmamis");
  const [boatId, setBoatId] = useState(selectable[0]?.id || (boats || [])[0]?.id || "");
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => { setSaving(true); await onSubmit(Number(boatId), f); setSaving(false); };
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="ls-manual-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-800"><i className="fa-solid fa-plus text-emerald-600 mr-2" />Manuel Rezervasyon Ekle</h3>
          <button data-testid="ls-manual-modal-close" onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <label className="text-xs font-semibold text-slate-500 flex flex-col gap-1 mb-3">
          Tekne
          <select data-testid="ls-manual-modal-boat" value={boatId} onChange={(e) => setBoatId(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 outline-none focus:border-emerald-500">
            {(boats || []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          {_MANUAL_MODAL_FIELDS.map((mf) => (
            <label key={mf.key} className={`text-xs font-semibold text-slate-500 flex flex-col gap-1 ${mf.key === "opsiyon" ? "col-span-2" : ""}`}>
              {mf.label}
              <input data-testid={`ls-manual-modal-${mf.key}`} type={mf.type || "text"} placeholder={mf.ph}
                value={f[mf.key] || ""} onChange={(e) => set(mf.key, e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 outline-none focus:border-emerald-500" />
            </label>
          ))}
        </div>
        <div className="flex gap-2 mt-5">
          <button data-testid="ls-manual-modal-save" disabled={saving} onClick={submit}
            className="flex-1 bg-emerald-600 text-white text-sm font-bold py-2.5 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
          <button data-testid="ls-manual-modal-cancel" onClick={onClose}
            className="px-5 bg-white border border-slate-300 text-slate-600 text-sm font-semibold py-2.5 rounded-lg hover:bg-slate-100">İptal</button>
        </div>
      </div>
    </div>
  );
}
